/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { Suspense, lazy, useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, useLocation, useParams, Navigate } from 'react-router-dom';
import { useModuleData } from './ui/useModuleData';
import { useAuth } from './ui/useAuth';
import { useWorkflow } from './ui/useWorkflow';
import { useHeartbeat } from './core/useHeartbeat';
import { getRepository } from './config/repository';
import { IModuleRepository } from './domain/repository';
import { UserRole, User, SystemLog, PERMISSIONS, Permission, MODULE_IDS, ModuleId, ApiKey, ApiScope, API_SCOPES, ApiSettings, WebhookRegistration, Organization, PlanFeature, OrgPlan, APP_IDS, APP_REGISTRY, PLAN_LIMITS, Brand, Branch, Department, NotificationType, PLATFORM_ROLES, isPlatformRole, TENANT_ROLES, WhiteLabelSettings, UserStatus } from './domain/entities';
import { Rider, RequestType, RequestStatus, WorkflowRequest, RiderHistoryEntry } from './modules/rider-ops/types';
import StandalonePreview from './modules/menu-manage/components/StandalonePreview';
import { hasPermission as hasPermFn, canAccessModule, canAccessTab, filterByScope, hasTabVariant } from './core/security';
import { PLATFORM_MODULES, tabPermissionKey, allTabKeysForModule, getApiModules, buildApiScope, allApiScopes, allWebhookEvents } from './core/appRegistry';
import type { ApiEndpointDefinition } from './core/appRegistry';
import { canUsePlanFeature as _canUsePlanFeature, PLAN_LABELS, PLAN_BADGE_COLORS, isAtPlanLimit } from './core/planGating';
import { dispatchApiRequest, SimulatedRequest, SimulatedResponse } from './core/apiService';
import { GodModeInspectorPage } from './pages/GodModeInspectorPage';
import { MarketplacePage } from './pages/MarketplacePage';
import { PlatformSettingsPage } from './pages/PlatformSettingsPage';
import { LandingPage } from './pages/LandingPage';
import { Dashboard } from './pages/Dashboard';
import { LoginPage } from './pages/LoginPage';
import { SignupPage } from './pages/SignupPage';
import { AuthLayout } from './pages/AuthLayout';
import { ForceChangePasswordPage } from './pages/ForceChangePasswordPage';
import { ProfilePage } from './pages/ProfilePage';
import { LoadingScreen, LoadingSpinner } from './ui/LoadingScreen';
import {
  Users,
  UserPlus,
  Settings as SettingsIcon,
  Search,
  Download,
  Upload,
  Camera,
  Trash2,
  Edit2,
  X,
  Check,
  AlertCircle,
  FileText,
  Plus,
  ChevronRight,
  Copy,
  ClipboardCheck,
  LayoutDashboard,
  Clock,
  Briefcase,
  Eye,
  Phone,
  Smartphone,
  Package,
  DollarSign,
  Home,
  Bell,
  Inbox,
  ArrowRightLeft,
  ChevronDown,
  LogOut,
  History,
  ClipboardList,
  BarChart2,
  BarChart3,
  ArrowLeft,
  Send,
  Shield,
  ShieldCheck,
  Key,
  Globe,
  Zap,
  Code2,
  ToggleLeft,
  ToggleRight,
  Building2,
  CreditCard,
  Lock,
  CheckCircle2,
  XCircle,
  Info,
  AlertTriangle,
  Megaphone,
  Tag,
  Layers,
  BookOpen,
  LifeBuoy,
  ShoppingBag,
  LayoutGrid,
  Boxes,
  Banknote,
  Palette,
  RotateCcw,
  ExternalLink,
  Image as ImageIcon,
  HardDrive,
  MapPin,
} from 'lucide-react';
import { isRemoteMode } from './config/repository';
import { getAuthToken } from './data/ApiModuleRepository';
import { getActiveTasks } from './hooks/useBackgroundTask';
import { cn } from './ui/utils';
import { hasDataChanged } from './core/utils';
import { useToast } from './ui/Toast';
import { ConfirmDeleteDialog, type DeleteConfirmState } from './ui/ConfirmDeleteDialog';
import { CustomSelect } from './components/shared/CustomSelect';
import { useSearchParams } from 'react-router-dom';
import * as XLSX from 'xlsx-js-style';
import domtoimage from 'dom-to-image-more';
import { flattenBranches, getBrandSelectionState } from './core/brandBranches';

const RiderManagerModule = lazy(() => import('./modules/rider-ops/RiderManagerModule').then(m => ({ default: m.RiderManagerModule })));
const RiderDetailPopup = lazy(() => import('./modules/rider-ops/RiderManagerModule').then(m => ({ default: m.RiderDetailPopup })));
const AggregatorModule = lazy(() => import('./modules/aggregator/AggregatorModule').then(m => ({ default: m.AggregatorModule })));
const MenuManageModule = lazy(() => import('./modules/menu-manage/MenuManageModule').then(m => ({ default: m.MenuManageModule })));
const SupportDeskModule = lazy(() => import('./modules/support-desk/SupportDeskModule').then(m => ({ default: m.SupportDeskModule })));
const MarketMapModule = lazy(() => import('./modules/market-map/MarketMapModule').then(m => ({ default: m.MarketMapModule })));
const SaasAdminPage = lazy(() => import('./pages/SaasAdminPage').then(m => ({ default: m.SaasAdminPage })));
const LabPage = lazy(() => import('./pages/SaasAdminPage').then(m => ({ default: m.LabPage })));

function PlatformLoadingScreen() {
  return (
    <div className="fixed inset-0 z-[9999] bg-slate-950/95 backdrop-blur-sm flex items-center justify-center">
      <div className="w-full max-w-sm rounded-3xl border border-white/10 bg-white/[0.06] p-8 shadow-2xl">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-emerald-400/10 border border-emerald-300/20 flex items-center justify-center">
            <LoadingSpinner size="sm" />
          </div>
          <div>
            <p className="text-sm font-bold text-white tracking-tight">Loading workspace</p>
            <p className="text-xs text-slate-400 mt-1">Preparing module assets and secure context...</p>
          </div>
        </div>
        <div className="mt-6 h-1.5 rounded-full bg-white/10 overflow-hidden">
          <div className="h-full w-1/2 rounded-full bg-emerald-400 animate-pulse" />
        </div>
      </div>
    </div>
  );
}

function hasScopedAssignments(scope: Pick<User, 'assignedBrands' | 'assignedBranches'> | null | undefined): boolean {
  return ((scope?.assignedBrands?.length ?? 0) > 0) || ((scope?.assignedBranches?.length ?? 0) > 0);
}

function getAccessibleBranchCount(brands: Brand[], scope: Pick<User, 'assignedBrands' | 'assignedBranches'> | null | undefined): number {
  const allBranches = flattenBranches(brands);
  if (!hasScopedAssignments(scope)) return allBranches.length;

  const visibleBranchIds = new Set(scope?.assignedBranches ?? []);
  for (const brandId of scope?.assignedBrands ?? []) {
    for (const branch of allBranches) {
      if (branch.brandId === brandId) {
        visibleBranchIds.add(branch.id);
      }
    }
  }

  return visibleBranchIds.size;
}

function ScopeSelectionIndicator({ checked, indeterminate = false }: { checked: boolean; indeterminate?: boolean }) {
  return (
    <div
      className={cn(
        'w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-all',
        checked || indeterminate
          ? 'bg-indigo-600 border-indigo-600'
          : 'bg-white border-zinc-300',
      )}
    >
      {checked ? (
        <Check size={10} className="text-white" />
      ) : indeterminate ? (
        <div className="w-2 h-0.5 rounded-full bg-white" />
      ) : null}
    </div>
  );
}

function TopNav({ user, demoUsers, switchUser, signOut, notifications, markRead, markAllRead, clearNotifications, riders, setSelectedRider, currentOrg, isSuperAdmin, isImpersonating, brands, departments, refreshWorkflow }: any) {
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [pulseUnreadCount, setPulseUnreadCount] = useState(-1);
  const localUnreadCount = notifications.filter((n: any) => !n.isRead).length;
  const unreadCount = pulseUnreadCount >= 0 ? pulseUnreadCount : localUnreadCount;
  const navigate = useNavigate();
  const notifRef = React.useRef<HTMLDivElement>(null);
  const userMenuRef = React.useRef<HTMLDivElement>(null);

  // ── Pulse listener: update bell count + trigger workflow refresh on new notifications ──
  React.useEffect(() => {
    const handler = (e: any) => {
      const { unreadCount: uc } = e.detail || {};
      if (typeof uc === 'number') setPulseUnreadCount(uc);
      // Refresh the full notification list so dropdown shows new items
      if (refreshWorkflow) refreshWorkflow();
    };
    window.addEventListener('PULSE_NEW_NOTIFICATIONS', handler);
    return () => window.removeEventListener('PULSE_NEW_NOTIFICATIONS', handler);
  }, [refreshWorkflow]);

  // Sync pulse count when local notifications change (e.g., after markRead)
  React.useEffect(() => { setPulseUnreadCount(-1); }, [localUnreadCount]);

  // Outside click to close notifications
  React.useEffect(() => {
    if (!showNotifications) return;
    const handler = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) setShowNotifications(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showNotifications]);

  // Outside click to close user menu
  React.useEffect(() => {
    if (!showUserMenu) return;
    const handler = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) setShowUserMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showUserMenu]);

  const NOTIF_ICONS: Record<string, { icon: React.ElementType; bg: string; color: string }> = {
    NEW_REQUEST: { icon: Plus,          bg: 'bg-indigo-50',  color: 'text-indigo-500' },
    APPROVED:    { icon: CheckCircle2,  bg: 'bg-emerald-50', color: 'text-emerald-500' },
    REJECTED:    { icon: XCircle,       bg: 'bg-red-50',     color: 'text-red-500' },
    INFO:        { icon: Info,          bg: 'bg-sky-50',     color: 'text-sky-500' },
    WARNING:     { icon: AlertTriangle, bg: 'bg-amber-50',   color: 'text-amber-500' },
  };

  // Group demo users for display
  const _isPlatform = (r: string) => ['SUPER_ADMIN','PLATFORM_OWNER','PLATFORM_SUPPORT','PLATFORM_SALES'].includes(r);
  const platformUsers = demoUsers.filter((u: any) => _isPlatform(u.role));
  const orgGroups: { label: string; users: any[] }[] = [];
  const orgIds = [...new Set(demoUsers.filter((u: any) => !_isPlatform(u.role)).map((u: any) => u.orgId))];
  orgIds.forEach((orgId: any) => {
    const orgUsers = demoUsers.filter((u: any) => u.orgId === orgId && !_isPlatform(u.role));
    if (orgUsers.length > 0) {
      const label = (currentOrg && currentOrg.id === orgId) ? currentOrg.name : (orgId === 'org1' ? 'Tristar Logistics' : (orgUsers[0]?.name ? `${orgUsers[0].name}'s Org` : orgId));
      orgGroups.push({ label, users: orgUsers });
    }
  });

  return (
    <div className={cn("fixed left-56 right-0 h-16 bg-white border-b border-slate-200 px-8 flex items-center justify-between z-20", isImpersonating ? "top-10" : "top-0")}>
      <div className="flex items-center gap-4">
        <div className="px-3 py-1 bg-slate-100 rounded-full text-[10px] font-bold text-slate-500 uppercase tracking-widest">
          {user?.role === UserRole.SUPER_ADMIN || user?.role === UserRole.PLATFORM_OWNER ? 'Platform Owner' : user?.role === UserRole.PLATFORM_SUPPORT ? 'Platform Support' : user?.role === UserRole.PLATFORM_SALES ? 'Platform Sales' : user?.role === UserRole.ADMIN ? 'System Administrator' : user?.role === UserRole.DEPARTMENT_HEAD ? 'Dept. Head' : user?.role === UserRole.EMPLOYEE ? 'Employee' : 'Manager'}
        </div>
        {(user?.role === UserRole.MANAGER || user?.role === UserRole.EMPLOYEE) && (
          <>
          {user.assignedBrands && user.assignedBrands.length > 0 && (
            <div className="text-xs font-medium text-zinc-400 flex items-center gap-1">
              <Building2 size={12} className="text-zinc-300" />
              {user.assignedBrands.map((bid: string) => {
                const brand = (brands ?? []).find((b: any) => b.id === bid);
                return brand ? brand.name : bid;
              }).join(', ')}
            </div>
          )}
          {user.assignedDepartments && user.assignedDepartments.length > 0 && (
            <div className="text-xs font-medium text-zinc-400 flex items-center gap-1">
              <Tag size={12} className="text-zinc-300" />
              {user.assignedDepartments.map((did: string) => {
                const dept = (departments ?? []).find((d: any) => d.id === did);
                return dept ? dept.name : did;
              }).join(', ')}
            </div>
          )}
          </>
        )}
        {user?.role === UserRole.EMPLOYEE && (
          <div className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-slate-100 border border-slate-200 rounded-full text-[10px] font-bold text-slate-500 uppercase tracking-wider">
            Read-Only
          </div>
        )}
      </div>

      <div className="flex items-center gap-6">
        {/* Notifications */}
        <div className="relative" ref={notifRef}>
          {/* ── Bell trigger ── */}
          <button
            onClick={() => { const opening = !showNotifications; setShowNotifications(opening); if (opening && refreshWorkflow) refreshWorkflow(); }}
            className="flex items-center gap-2.5 group"
          >
            <div className="text-right">
              <span className="text-[13px] font-bold text-slate-900 block leading-tight">Notifications</span>
              {unreadCount > 0 && (
                <span className="text-[10px] font-bold text-teal-600 uppercase tracking-wide leading-tight">YOU HAVE {unreadCount} UNREAD</span>
              )}
            </div>
            <div className="relative w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center transition-colors group-hover:bg-emerald-50">
              <Bell size={18} className="text-teal-600 transition-colors group-hover:text-emerald-500" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-[10px] h-[10px] bg-red-500 rounded-full border-2 border-white shadow-sm animate-pulse" />
              )}
            </div>
          </button>

          {/* ── Dropdown popover ── */}
          {showNotifications && (
            <div className="absolute top-full right-0 mt-3 w-[380px] bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden z-50"
              style={{animation: 'fadeIn 0.2s ease-out'}}>
              {/* Header */}
              <div className="px-5 pt-5 pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="text-[15px] font-bold text-slate-900">Notifications</h4>
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mt-1">RECENT ACTIVITY</p>
                  </div>
                  <div className="flex items-center gap-5 pt-0.5">
                    {unreadCount > 0 && (
                      <button onClick={(e) => { e.stopPropagation(); markAllRead(); }} className="flex items-center gap-1.5 text-[12px] font-semibold text-teal-600 hover:text-teal-700 transition-colors">
                        <CheckCircle2 size={13} strokeWidth={2} /> Read
                      </button>
                    )}
                    <button onClick={(e) => { e.stopPropagation(); clearNotifications(); }} className="flex items-center gap-1.5 text-[12px] font-semibold text-slate-400 hover:text-red-500 transition-colors">
                      <Trash2 size={12} strokeWidth={2} /> Clear
                    </button>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="mx-5 border-t border-slate-100" />

              {/* Notification list */}
              <div className="max-h-[420px] overflow-y-auto py-2" style={{scrollbarWidth:'thin'}}>
                {notifications.length === 0 ? (
                  <div className="px-5 py-10 text-center">
                    <Bell size={28} className="text-slate-200 mx-auto mb-3" />
                    <p className="text-sm font-semibold text-slate-400">No notifications yet</p>
                    <p className="text-[11px] text-slate-300 mt-1">You're all caught up!</p>
                  </div>
                ) : (
                  notifications.map((n: any) => {
                    const iconCfg = NOTIF_ICONS[n.type] ?? NOTIF_ICONS.INFO;
                    const NIcon = iconCfg.icon;
                    const ts = new Date(n.createdAt);
                    const timeStr = ts.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                      + ', ' + ts.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });

                    return (
                      <button
                        key={n.id}
                        onClick={() => { markRead(n.id); navigate(n.targetPath); setShowNotifications(false); }}
                        className="w-full text-left flex items-start gap-0 py-3 transition-colors hover:bg-slate-50/70 group/row"
                      >
                        {/* Unread accent bar — thick left border */}
                        <div className={cn(
                          'w-[4px] min-h-full self-stretch shrink-0 rounded-r-sm transition-colors',
                          !n.isRead ? 'bg-teal-500' : 'bg-transparent',
                        )} />

                        {/* Icon circle */}
                        <div className="pl-4 pr-3 pt-0.5 shrink-0">
                          <div className={cn('w-10 h-10 rounded-full flex items-center justify-center', iconCfg.bg)}>
                            <NIcon size={18} className={iconCfg.color} strokeWidth={2} />
                          </div>
                        </div>

                        {/* Text content */}
                        <div className="flex-1 min-w-0 pr-5">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className={cn('text-[13px] text-slate-900 leading-snug', !n.isRead ? 'font-bold' : 'font-semibold')}>{n.title}</p>
                            <span className="text-[9px] text-slate-400 bg-slate-100 px-2 py-[2px] rounded font-medium shrink-0 whitespace-nowrap">{timeStr}</span>
                          </div>
                          <p className="text-[11.5px] text-slate-500 mt-1 leading-relaxed">{n.message}</p>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        {/* User Switcher (Demo Only) */}
        <div className="relative" ref={userMenuRef}>
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="flex items-center gap-3 pl-3 pr-2 py-1.5 bg-zinc-50 border border-zinc-200 rounded-xl hover:border-zinc-900 transition-all group"
          >
            <div className="w-7 h-7 bg-zinc-900 rounded-lg flex items-center justify-center text-white text-xs font-bold">
              {user?.name.charAt(0)}
            </div>
            <div className="text-left">
              <p className="text-xs font-bold text-zinc-900">{user?.name}</p>
              <p className="text-[10px] text-zinc-400 font-medium">{user?.role}</p>
            </div>
            <ChevronDown size={14} className="text-zinc-400 group-hover:text-zinc-900 transition-colors" />
          </button>

          {showUserMenu && (
            <div className="absolute top-full right-0 mt-2 w-64 bg-white border border-zinc-200 rounded-2xl shadow-xl overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
              {/* Platform section */}
              {platformUsers.length > 0 && (
                <div className="p-3 border-b border-zinc-100">
                  <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest px-2 mb-2">Platform</p>
                  {platformUsers.map((u: any) => (
                    <button
                      key={u.id}
                      onClick={() => { switchUser(u); setShowUserMenu(false); navigate('/dashboard'); }}
                      className={cn(
                        "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors",
                        user?.id === u.id ? "bg-zinc-100 text-zinc-900 font-bold" : "text-zinc-500 hover:bg-zinc-50"
                      )}
                    >
                      <div className="w-2 h-2 rounded-full bg-violet-500" />
                      {u.name}
                    </button>
                  ))}
                </div>
              )}
              {/* Org sections */}
              {orgGroups.map((group: any) => (
                <div key={group.label} className="p-3 border-b border-zinc-100 last:border-0">
                  <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest px-2 mb-2">{group.label}</p>
                  {group.users.map((u: any) => (
                    <button
                      key={u.id}
                      onClick={() => { switchUser(u); setShowUserMenu(false); navigate('/dashboard'); }}
                      className={cn(
                        "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors",
                        user?.id === u.id ? "bg-zinc-100 text-zinc-900 font-bold" : "text-zinc-500 hover:bg-zinc-50"
                      )}
                    >
                      <div className={cn("w-2 h-2 rounded-full", u.role === UserRole.ADMIN ? "bg-indigo-500" : u.role === UserRole.DEPARTMENT_HEAD ? "bg-purple-500" : u.role === UserRole.EMPLOYEE ? "bg-slate-400" : "bg-emerald-500")} />
                      {u.name}
                    </button>
                  ))}
                </div>
              ))}
              <div className="border-t border-zinc-100">
                <button
                  onClick={() => { setShowUserMenu(false); navigate('/profile'); }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm text-zinc-600 hover:bg-zinc-50 transition-colors">
                  <SettingsIcon size={16} />
                  <span>My Profile</span>
                </button>
                <button
                  onClick={async () => { if (getActiveTasks().some(t => t.status === 'pending') && !window.confirm('Background tasks are still running. Sign out anyway?')) return; await signOut(); }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-500 hover:bg-red-50 transition-colors">
                  <LogOut size={16} />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ProtectedRoute({ children, allowedRoles, permission, moduleId, tabId, user, currentOrg }: {
  children: React.ReactNode;
  allowedRoles?: UserRole[];
  permission?: Permission;
  moduleId?: ModuleId;
  tabId?: string;
  user: User | null;
  currentOrg?: Organization | null;
}) {
  if (!user) return <Navigate to="/" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) return <Navigate to="/403" replace />;
  // Pass org enabledApps so the org license gate works correctly for ADMIN/MANAGER
  const orgApps = currentOrg?.enabledApps ?? undefined;
  if (moduleId && !canAccessModule(user, moduleId, permission, orgApps)) return <Navigate to="/403" replace />;
  if (moduleId && tabId && !canAccessTab(user, moduleId, tabId)) return <Navigate to="/403" replace />;
  if (!moduleId && permission && !hasPermFn(user, permission)) return <Navigate to="/403" replace />;
  return <>{children}</>;
}

function PermissionGate({ permission, user, children }: {
  permission: Permission;
  user: User | null;
  children: React.ReactNode;
}) {
  if (!hasPermFn(user, permission)) return null;
  return <>{children}</>;
}

function PermissionDeniedPage({ user }: { user: User | null }) {
  const navigate = useNavigate();
  return (
    <div className="flex flex-col items-center justify-center py-24 gap-6 text-center">
      <div className="w-16 h-16 bg-zinc-900 rounded-2xl flex items-center justify-center text-white">
        <Shield size={28} />
      </div>
      <div>
        <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest mb-2">403 — Access Restricted</p>
        <h2 className="text-2xl font-bold tracking-tight text-zinc-900">Permission Required</h2>
        <p className="text-sm text-slate-500 mt-2 max-w-sm mx-auto">
          Your account does not have permission to access this page.
          Contact your administrator to request access.
        </p>
      </div>
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="px-4 py-2 border border-zinc-200 rounded-lg text-sm font-semibold hover:bg-zinc-50 transition-all"
        >
          Go Back
        </button>
        <button
          onClick={() => navigate('/dashboard')}
          className="px-4 py-2 bg-zinc-900 text-white rounded-lg text-sm font-semibold hover:bg-zinc-800 transition-all"
        >
          Return to Dashboard
        </button>
      </div>
      {user && (
        <p className="text-xs text-zinc-400">
          Current role: <span className="font-bold text-zinc-600">{user.role}</span>
        </p>
      )}
    </div>
  );
}

function PlanUpgradeBadge({ feature }: { feature: PlanFeature }) {
  return (
    <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-xl text-amber-700 text-xs font-bold">
      <Lock size={12} /> Upgrade required
    </div>
  );
}

function PlanGate({ feature, canUsePlanFeature, children, fallback }: {
  feature: PlanFeature;
  canUsePlanFeature: (f: PlanFeature) => boolean;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}) {
  if (canUsePlanFeature(feature)) return <>{children}</>;
  return fallback ? <>{fallback}</> : <PlanUpgradeBadge feature={feature} />;
}

function PlanLimitModal({ resource, currentPlan, onClose }: {
  resource: 'Branches' | 'Users' | 'Riders';
  currentPlan: OrgPlan | null;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const planLabel = currentPlan ? PLAN_LABELS[currentPlan] : 'current';
  const nextLabel = currentPlan === OrgPlan.FREE ? 'Pro' : 'Enterprise';
  return (
    <div className="fixed inset-0 bg-zinc-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-8 space-y-5 text-center">
        <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto">
          <Lock size={24} className="text-amber-600" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-zinc-900">Limit Reached</h3>
          <p className="text-sm text-zinc-500 mt-2">
            You&apos;ve reached the <strong>{resource}</strong> limit for the{' '}
            <strong>{planLabel}</strong> plan. Upgrade to <strong>{nextLabel}</strong> to add more.
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 border border-zinc-200 rounded-xl text-sm font-bold hover:bg-zinc-50"
          >
            Cancel
          </button>
          <button
            onClick={() => { onClose(); navigate('/saas-admin/subscriptions'); }}
            className="flex-1 px-4 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-bold hover:bg-indigo-700"
          >
            Upgrade Plan →
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Impersonation Banner
// ─────────────────────────────────────────────────────────────────────────────

const IMPERSONATION_KEY = 'dm_impersonation_target';

interface ImpersonationTarget {
  orgId: string;
  orgName: string;
  /** Unix timestamp when this session auto-expires (supportAccessExpiry from org). */
  expiresAt?: number;
}

function getImpersonationTarget(): ImpersonationTarget | null {
  try {
    const raw = sessionStorage.getItem(IMPERSONATION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function ImpersonationBanner({ target, onStop, onStopAndRevoke }: {
  target: ImpersonationTarget;
  onStop: () => void;
  onStopAndRevoke: () => void;
}) {
  const [remaining, setRemaining] = React.useState<string>('');

  React.useEffect(() => {
    if (!target.expiresAt) return;
    const update = () => {
      const diff = target.expiresAt! - Date.now();
      if (diff <= 0) { setRemaining('Expired'); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      setRemaining(`${h}h ${m}m remaining`);
    };
    update();
    const id = setInterval(update, 60000);
    return () => clearInterval(id);
  }, [target.expiresAt]);

  return (
    <div className="fixed top-0 left-0 right-0 z-[200] bg-rose-700 text-white px-6 py-2 flex items-center justify-between shadow-xl">
      <div className="flex items-center gap-3 text-sm font-bold">
        <ShieldCheck size={15} className="shrink-0" />
        <span className="uppercase tracking-widest text-[11px] font-black text-rose-200">Authorized Support Session</span>
        <span className="text-white">— Viewing <strong>{target.orgName}</strong></span>
        {remaining && (
          <span className="ml-2 px-2 py-0.5 bg-white/20 rounded text-[10px] font-bold">{remaining}</span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={onStop}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-white/20 hover:bg-white/30 text-white rounded-lg font-bold text-xs transition-colors"
        >
          <ArrowLeft size={12} /> Exit View
        </button>
        <button
          onClick={onStopAndRevoke}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-white text-rose-700 rounded-lg font-bold text-xs hover:bg-rose-50 transition-colors shadow"
        >
          <Lock size={12} /> Stop & Revoke Access
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ModuleGuard — blocks access when app is not in org's enabledApps
// ─────────────────────────────────────────────────────────────────────────────

function ModuleGuard({ appId, currentOrg, isSuperAdmin, isImpersonating, user, children }: {
  appId: string;
  currentOrg: Organization | null;
  isSuperAdmin: boolean;
  isImpersonating: boolean;
  user: User | null;
  children: React.ReactNode;
}) {
  const effectivelyRestricted = !isSuperAdmin || isImpersonating;

  // ── Layer 1: Organization License Gate (Dual-Key) ──
  // Treat undefined enabledApps as [] → strict zero-state lockdown.
  // Must check BEFORE user-level permissions.
  if (effectivelyRestricted) {
    const orgApps = currentOrg?.enabledApps ?? [];
    if (!orgApps.includes(appId)) {
      return (
        <div className="flex flex-col items-center justify-center py-24 gap-6">
          <div className="w-20 h-20 bg-zinc-100 rounded-3xl flex items-center justify-center">
            <Lock size={36} className="text-zinc-400" />
          </div>
          <div className="text-center max-w-sm">
            <h2 className="text-2xl font-black text-zinc-900 mb-2">Module Not Licensed</h2>
            <p className="text-zinc-500 text-sm">
              This module is not enabled for <strong>{currentOrg?.name ?? 'your organisation'}</strong>.
              Contact your platform administrator to activate it.
            </p>
          </div>
        </div>
      );
    }
  }

  // ── Layer 2: User-Level Permission Gate ──
  if (effectivelyRestricted && user && !canAccessModule(user, appId as ModuleId, undefined, currentOrg?.enabledApps ?? [])) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-6">
        <div className="w-20 h-20 bg-zinc-100 rounded-3xl flex items-center justify-center">
          <Lock size={36} className="text-zinc-400" />
        </div>
        <div className="text-center max-w-sm">
          <h2 className="text-2xl font-black text-zinc-900 mb-2">Access Restricted</h2>
          <p className="text-zinc-500 text-sm">
            Your account does not have permission to access this module.
            Contact your administrator to request access.
          </p>
        </div>
      </div>
    );
  }
  return <>{children}</>;
}

export default function App({ migrationApplied }: { migrationApplied?: boolean }) {
  const {
    user,
    demoUsers,
    loading: authLoading,
    switchUser,
    signOut,
    isSuperAdmin,
    isPlatformOwner,
    isPlatformSupport,
    isPlatformSales,
    isInternalAdmin,
    isAdmin,
    isStoreManager,
    isDepartmentHead,
    isManager,
    isEmployee,
    isReadOnly,
    currentOrg,
    orgPlan,
    canUsePlanFeature,
    subscriptionPlans,
    switchOrgContext,
    refresh,
    repository,
  } = useAuth();

  const { 
    riders: allRiders, 
    settings, 
    loading: ridersLoading, 
    nextAutoNumber, 
    addRider, 
    updateRider, 
    deleteRider, 
    updateSettings,
    checkFileNumberConflict,
    toggleRiderStatus
  } = useModuleData('rider-ops', user);

  const {
    requests,
    notifications,
    loading: workflowLoading,
    submitRequest,
    updateRequestStatus,
    markRead,
    markAllRead,
    clearNotifications,
    refresh: refreshWorkflow,
  } = useWorkflow(user);

  // ── Real-time Pulse: background poller for notifications + menu status ──
  useHeartbeat(!!user);

  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const [showDetailPopup, setShowDetailPopup] = useState<Rider | null>(null);

  // ── Header identity data: brands + departments for logged-in user ───────
  const [headerBrands, setHeaderBrands] = useState<Brand[]>([]);
  const [headerDepartments, setHeaderDepartments] = useState<Department[]>([]);
  React.useEffect(() => {
    if (!user) return;
    Promise.all([repository.getBrands(), repository.getDepartments()]).then(([b, d]) => {
      setHeaderBrands(b);
      setHeaderDepartments(d);
    });
  }, [user?.id, user?.orgId]);

  // ── Public CMS settings (fetched on public routes for Landing + Login pages) ──
  const [publicCms, setPublicCms] = useState<import('./domain/entities').PlatformSettings | null>(null);
  React.useEffect(() => {
    const pub = ['/', '/landing', '/login', '/signup', '/landing-preview'];
    if (!pub.includes(location.pathname)) return;
    const apiBase = (import.meta as any).env?.VITE_API_URL ?? 'http://localhost:8787';
    // Resolve a relative /api/... path to an absolute URL using the API base
    const abs = (url?: string) => url ? (url.startsWith('http') ? url : `${apiBase}${url}`) : undefined;
    fetch(`${apiBase}/api/platform-settings/public`)
      .then(r => r.ok ? r.json() : null)
      .then((d: any) => {
        if (d?.data) {
          const s = d.data;
          setPublicCms({
            ...s,
            logoUrl: abs(s.logoUrl),
            faviconUrl: abs(s.faviconUrl),
            heroImageUrl: abs(s.heroImageUrl),
            loginSidePanelImageUrl: abs(s.loginSidePanelImageUrl),
          } as import('./domain/entities').PlatformSettings);
        }
      })
      .catch(() => {});
  }, [location.pathname]);

  // ── White-Label Theme Injection Engine ────────────────────────────────────
  // Injects CSS custom properties from the org's whiteLabelSettings onto :root.
  // The impersonation bar always remains platform-indigo regardless.
  React.useEffect(() => {
    const root = document.documentElement;
    const wl = currentOrg?.whiteLabelSettings;
    if (wl?.primaryColor) {
      root.style.setProperty('--primary-color', wl.primaryColor);
    } else {
      root.style.setProperty('--primary-color', '#10b981');
    }
    // Favicon swap
    let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    if (wl?.faviconUrl) {
      const apiBase = (import.meta as any).env?.VITE_API_URL ?? 'http://localhost:8787';
      link.href = wl.faviconUrl.startsWith('http') ? wl.faviconUrl : `${apiBase}${wl.faviconUrl}`;
    } else {
      link.href = '/favicon.ico';
    }
  }, [currentOrg?.id, currentOrg?.whiteLabelSettings?.primaryColor, currentOrg?.whiteLabelSettings?.faviconUrl]);

  // ── Migration toast (fires once if data was patched) ─────────────────────
  React.useEffect(() => {
    if (migrationApplied) {
      toast.success(
        'System Migration Complete',
        'Campaign Manager updated to Aggregator Operations. All user permissions have been migrated.'
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Final Verification Toast ─────────────────────────────────────────────
  React.useEffect(() => {
    // Fire the requested confirmation toast on app load
    setTimeout(() => {
      toast.success('ClickIt Platform v1.2', 'Premium Dashboard Active.');
    }, 1000);
  }, []);

  // ── Warn before closing tab if background tasks are still running ────────
  React.useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (getActiveTasks().some(t => t.status === 'pending')) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  // ── Post-approval guidance: toast when org has newly approved modules ────
  React.useEffect(() => {
    if (!user || !isAdmin || isInternalAdmin) return;
    const approvedUnread = notifications.filter(
      n => n.type === NotificationType.APPROVED && !n.isRead
    );
    if (approvedUnread.length > 0 && location.pathname === '/') {
      const first = approvedUnread[0];
      toast.success(first.title, first.message);
      navigate('/system/users');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, notifications.length]);

  // ── Impersonation state ──────────────────────────────────────────────────
  const [impersonationTarget, setImpersonationTarget] = useState<ImpersonationTarget | null>(
    () => getImpersonationTarget()
  );
  const isImpersonating = !!impersonationTarget;

  const writeAuditLog = async (action: string, _extra?: Record<string, string>) => {
    try {
      const { apiFetch } = await import('./data/ApiModuleRepository');
      await apiFetch('/api/system/audit-log', {
        method: 'POST',
        body: JSON.stringify({ action }),
      });
    } catch { /* best-effort — don't break the calling flow */ }
  };

  const startImpersonation = async (org: Organization) => {
    // Guard: check consent is granted and not expired
    if (!org.supportAccessEnabled || (org.supportAccessExpiry && Date.now() > org.supportAccessExpiry)) {
      alert(`Access Denied: "${org.name}" has not granted support permission.`);
      return;
    }
    const target: ImpersonationTarget = {
      orgId: org.id,
      orgName: org.name,
      expiresAt: org.supportAccessExpiry,
    };
    sessionStorage.setItem(IMPERSONATION_KEY, JSON.stringify(target));
    setImpersonationTarget(target);
    writeAuditLog(`ADMIN_IMPERSONATION_START — ${user?.role ?? 'Platform Admin'} started authorized support session for "${org.name}" (${org.id})`);
    await switchOrgContext(org.id);
    navigate('/dashboard');
  };

  const stopImpersonation = async () => {
    if (impersonationTarget) {
      writeAuditLog(`ADMIN_IMPERSONATION_END — ${user?.role ?? 'Platform Admin'} ended support session for "${impersonationTarget.orgName}" (${impersonationTarget.orgId})`);
    }
    sessionStorage.removeItem(IMPERSONATION_KEY);
    setImpersonationTarget(null);
    await switchOrgContext(null);
    navigate('/saas-admin/organizations');
  };

  const stopAndRevoke = async () => {
    if (impersonationTarget) {
      writeAuditLog(`ADMIN_IMPERSONATION_END — Support session ended AND access revoked for "${impersonationTarget.orgName}" (${impersonationTarget.orgId})`);
      // Revoke consent immediately on the org
      const repo = getRepository();
      const org = await repo.getOrganizationById(impersonationTarget.orgId);
      if (org) await repo.saveOrganization({ ...org, supportAccessEnabled: false, supportAccessExpiry: undefined });
    }
    sessionStorage.removeItem(IMPERSONATION_KEY);
    setImpersonationTarget(null);
    await switchOrgContext(null);
    navigate('/saas-admin/organizations');
  };

  const handleDataExport = () => {
    if (impersonationTarget) {
      writeAuditLog(`ADMIN_DATA_EXPORT ⚠️ HIGH PRIORITY — ${user?.role ?? 'Platform Staff'} exported rider data for "${impersonationTarget.orgName}" (${impersonationTarget.orgId}) during support session`);
    }
  };

  // Auto-expire impersonation session when supportAccessExpiry passes
  React.useEffect(() => {
    if (!impersonationTarget?.expiresAt) return;
    const check = async () => {
      if (Date.now() > impersonationTarget.expiresAt!) {
        writeAuditLog(`ADMIN_IMPERSONATION_END — Session auto-expired for "${impersonationTarget.orgName}" (${impersonationTarget.orgId})`);
        sessionStorage.removeItem(IMPERSONATION_KEY);
        setImpersonationTarget(null);
        await switchOrgContext(null);
        navigate('/saas-admin/organizations');
      }
    };
    const id = setInterval(check, 30000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [impersonationTarget]);

  // maskData = true when platform staff views data WITHOUT an authorized session (not impersonating)
  const maskData = isInternalAdmin && !isImpersonating;

  const loading = ridersLoading || authLoading || workflowLoading;

  // ── Public routes bypass the app shell entirely ────────────────────────
  const isPublicPreview = location.pathname.startsWith('/public-preview');
  const isPublicRoute = ['/', '/landing', '/login', '/signup', '/landing-preview'].includes(location.pathname) || isPublicPreview;

  // Public-preview always renders standalone (even for logged-in admins)
  if (isPublicPreview) {
    return (
      <Routes>
        <Route path="/public-preview/:menuId" element={<StandalonePreview />} />
      </Routes>
    );
  }

  if (isPublicRoute && !user) {
    return (
      <Routes>
        <Route path="/" element={
          <LandingPage
            onGetStarted={(plan) => navigate(plan ? `/signup?plan=${plan}` : '/signup')}
            onSignIn={() => navigate('/login')}
            cms={publicCms ? {
              platformName: publicCms.platformName,
              logoUrl: publicCms.logoUrl,
              heroTitle: publicCms.heroTitle,
              heroDescription: publicCms.heroDescription,
              heroImageUrl: publicCms.heroImageUrl,
              newsItems: publicCms.newsItems,
            } : undefined}
          />
        } />
        {/* Legacy /landing path — redirect to / */}
        <Route path="/landing" element={<Navigate to="/" replace />} />
        <Route path="/login" element={
          <AuthLayout
            initialMode="login"
            demoUsers={demoUsers}
            switchUser={async (u: User) => { await switchUser(u); navigate('/dashboard'); }}
            onComplete={async (u: User) => { await switchUser(u); navigate('/dashboard'); }}
            cms={publicCms ? {
              platformName: publicCms.platformName,
              logoUrl: publicCms.logoUrl,
              loginWelcomeText: publicCms.loginWelcomeText,
              loginSidePanelImageUrl: publicCms.loginSidePanelImageUrl,
            } : undefined}
          />
        } />
        <Route path="/signup" element={
          <AuthLayout
            initialMode="signup"
            demoUsers={demoUsers}
            switchUser={async (u: User) => { await switchUser(u); navigate('/dashboard'); }}
            onComplete={async (u: User) => { await switchUser(u); navigate('/dashboard'); }}
            cms={publicCms ? {
              platformName: publicCms.platformName,
              logoUrl: publicCms.logoUrl,
              loginWelcomeText: publicCms.loginWelcomeText,
              loginSidePanelImageUrl: publicCms.loginSidePanelImageUrl,
            } : undefined}
          />
        } />
        <Route path="/landing-preview" element={
          <LandingPage
            onGetStarted={(plan) => navigate(plan ? `/signup?plan=${plan}` : '/signup')}
            onSignIn={() => navigate('/login')}
            cms={publicCms ? {
              platformName: publicCms.platformName,
              logoUrl: publicCms.logoUrl,
              heroTitle: publicCms.heroTitle,
              heroDescription: publicCms.heroDescription,
              heroImageUrl: publicCms.heroImageUrl,
              newsItems: publicCms.newsItems,
            } : undefined}
          />
        } />
        <Route path="/public-preview/:menuId" element={<StandalonePreview />} />
      </Routes>
    );
  }

  // Logged-in users visiting public pages → redirect to dashboard
  if (user && ['/', '/landing', '/login'].includes(location.pathname)) {
    return <Navigate to="/dashboard" replace />;
  }

  // Filter riders based on user role — filterByScope applies branch and brand restrictions
  const riders = (isInternalAdmin || isAdmin || isDepartmentHead)
    ? allRiders
    : filterByScope(user, allRiders);

  // Auth must resolve before anything — rider/workflow data can load in background
  if (authLoading) return <LoadingScreen message="Authenticating..." />;

  // Redirect to login when not authenticated
  if (!user) return <Navigate to="/login" replace />;

  // ── Force Password Change Gate ──
  if (user.mustChangePassword) {
    return (
      <ForceChangePasswordPage
        user={user}
        onPasswordChanged={async (newPwd: string) => {
          const apiBase = (import.meta as any).env?.VITE_API_URL ?? 'http://localhost:8787';
          const token = getAuthToken();
          const res = await fetch(`${apiBase}/api/auth/change-password`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
            body: JSON.stringify({ newPassword: newPwd, forced: true }),
          });
          if (!res.ok) {
            const err = await res.json().catch(() => ({ message: 'Password change failed' }));
            throw new Error((err as any).message || 'Password change failed');
          }
          // Show success feedback, then force a clean logout.
          // This destroys the JWT and hard-navigates to /login, breaking any
          // possible re-entry into the mustChangePassword guard loop.
          toast.success('Password updated', 'Please log in with your new password.');
          sessionStorage.setItem('post_pw_change', '1');
          await signOut();
        }}
      />
    );
  }

  // ── Access Restricted Gate ──
  if (user.accessRestricted && !isInternalAdmin) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-6 text-center px-6 bg-slate-50">
        <div className="w-20 h-20 bg-amber-100 rounded-3xl flex items-center justify-center">
          <AlertTriangle size={36} className="text-amber-600" />
        </div>
        <div>
          <h2 className="text-2xl font-black text-zinc-900 mb-2">Access Restricted</h2>
          <p className="text-zinc-500 text-sm max-w-sm mx-auto">
            Your role has been removed from this organisation's provisioned roles.
            Contact your administrator to reassign your access.
          </p>
        </div>
        <button
          onClick={async () => { if (getActiveTasks().some(t => t.status === 'pending') && !window.confirm('Background tasks are still running. Sign out anyway?')) return; await signOut(); }}
          className="px-6 py-2.5 bg-zinc-900 text-white rounded-xl text-sm font-bold hover:bg-zinc-800 transition-all"
        >
          Sign Out
        </button>
      </div>
    );
  }

  return (
    <div className={cn("min-h-screen bg-slate-50 text-zinc-900 font-[Inter,system-ui,sans-serif] flex", isImpersonating ? "pt-24" : "pt-16")}>
      {isImpersonating && impersonationTarget && (
        <ImpersonationBanner
          target={impersonationTarget}
          onStop={stopImpersonation}
          onStopAndRevoke={stopAndRevoke}
        />
      )}
      <TopNav
        user={user}
        demoUsers={demoUsers}
        switchUser={switchUser}
        signOut={signOut}
        notifications={notifications}
        markRead={markRead}
        markAllRead={markAllRead}
        clearNotifications={clearNotifications}
        riders={allRiders}
        setSelectedRider={setShowDetailPopup}
        currentOrg={currentOrg}
        isSuperAdmin={isInternalAdmin}
        isImpersonating={isImpersonating}
        brands={headerBrands}
        departments={headerDepartments}
        refreshWorkflow={refreshWorkflow}
      />
      {/* Sidebar — Dark for Platform Admin, Light for Org Admin / Lab */}
      <nav className={cn(
        "fixed left-0 h-full w-56 border-r p-5 flex flex-col gap-6 z-30",
        isInternalAdmin && !isImpersonating
          ? "bg-[#0f172a] border-[#1e293b]"  // Dark Navy — Control Tower
          : "bg-white border-slate-200",       // Clean Light — Workspace
        isImpersonating ? "top-10" : "top-0"
      )}>
        <div className="flex items-center gap-3 px-2">
          <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center shrink-0",
            isInternalAdmin && !isImpersonating ? "bg-indigo-600/20 text-indigo-400" : "bg-slate-100 text-slate-600"
          )}>
            {isInternalAdmin && !isImpersonating
              ? <ShieldCheck size={20} />
              : currentOrg?.whiteLabelSettings?.logoUrl
                ? <img src={currentOrg.whiteLabelSettings.logoUrl.startsWith('http') ? currentOrg.whiteLabelSettings.logoUrl : `${(import.meta as any).env?.VITE_API_URL ?? 'http://localhost:8787'}${currentOrg.whiteLabelSettings.logoUrl}`} alt="Logo" className="w-8 h-8 rounded-lg object-contain" />
                : <Building2 size={20} />
            }
          </div>
          <div className="min-w-0">
            <h1 className={cn("text-sm font-bold tracking-tight truncate leading-tight",
              isInternalAdmin && !isImpersonating ? "text-white" : "text-slate-900"
            )}>
              {isInternalAdmin && !isImpersonating ? 'ClickIt' : (currentOrg?.name ?? 'ClickIt')}
            </h1>
            <p className={cn("text-[9px] font-bold uppercase tracking-widest leading-tight",
              isInternalAdmin && !isImpersonating ? "text-indigo-400/70" : "text-slate-400"
            )}>
              {isInternalAdmin && !isImpersonating
                ? (isPlatformOwner ? 'PLATFORM OWNER' : isPlatformSupport ? 'PLATFORM SUPPORT' : 'PLATFORM SALES')
                : 'WORKSPACE'}
            </p>
          </div>
        </div>

        {/* God Mode — Return to Platform Admin button */}
        {isImpersonating && impersonationTarget && (
          <button
            onClick={stopImpersonation}
            className="flex items-center gap-2 mx-2 px-3 py-2.5 bg-rose-50 border border-rose-200 rounded-xl text-xs font-bold text-rose-700 hover:bg-rose-100 transition-colors"
          >
            <ArrowLeft size={14} />
            <span className="truncate">Return to Platform Admin</span>
          </button>
        )}

        <div className="flex flex-col gap-4 overflow-y-auto pr-1">
          {(() => {
            const menuSections: Array<{
              label: string;
              items: Array<{ label: string; icon: React.ElementType; to: string; permission?: Permission; moduleId?: ModuleId; tabId?: string; planFeature?: PlanFeature; superAdminOnly?: boolean; platformRoles?: UserRole[]; external?: string }>;
              superAdminOnly?: boolean;
              tenantOnly?: boolean;
            }> = [
              // ── PLATFORM ADMIN: Platform ──
              {
                label: 'Platform',
                superAdminOnly: true,
                items: [
                  { label: 'Organizations',    icon: Building2,     to: '/saas-admin/organizations', superAdminOnly: true },
                  { label: 'Module Requests',  icon: Inbox,         to: '/saas-admin/requests',      superAdminOnly: true },
                  { label: 'Platform Health',  icon: BarChart3,     to: '/saas-admin/health',        superAdminOnly: true, platformRoles: [UserRole.SUPER_ADMIN, UserRole.PLATFORM_OWNER] },
                  { label: 'Platform Logs',    icon: ClipboardList, to: '/system/logs',              superAdminOnly: true, platformRoles: [UserRole.SUPER_ADMIN, UserRole.PLATFORM_OWNER, UserRole.PLATFORM_SUPPORT] },
                  { label: 'Subscriptions',    icon: CreditCard,    to: '/saas-admin/subscriptions', superAdminOnly: true, platformRoles: [UserRole.SUPER_ADMIN, UserRole.PLATFORM_OWNER] },
                  { label: 'API Management',   icon: Key,           to: '/saas-admin/api',           superAdminOnly: true, platformRoles: [UserRole.SUPER_ADMIN, UserRole.PLATFORM_OWNER] },
                ],
              },
              // ── PLATFORM ADMIN: System ──
              {
                label: 'System',
                superAdminOnly: true,
                items: [
                  { label: 'Internal Accounts', icon: Users,       to: '/system/users', superAdminOnly: true, platformRoles: [UserRole.SUPER_ADMIN, UserRole.PLATFORM_OWNER] },
                  { label: 'Module Catalog',      icon: LayoutGrid,  to: '/saas-admin/lab', superAdminOnly: true, platformRoles: [UserRole.SUPER_ADMIN, UserRole.PLATFORM_OWNER, UserRole.PLATFORM_SALES] },
                  { label: 'Platform Settings',   icon: SettingsIcon, to: '/saas-admin/settings', superAdminOnly: true, platformRoles: [UserRole.SUPER_ADMIN, UserRole.PLATFORM_OWNER] },
                  { label: 'Landing Page',         icon: Globe,        to: '/__preview-landing', superAdminOnly: true, platformRoles: [UserRole.SUPER_ADMIN, UserRole.PLATFORM_OWNER], external: '/landing-preview' },
                ],
              },
              // ── TENANT: Apps (hidden from Platform Admin unless impersonating) ──
              {
                label: 'Apps',
                tenantOnly: true,
                items: [
                  { label: 'Riders',       icon: Package,  to: '/apps/rider-manager',  permission: PERMISSIONS.RIDER_VIEW_DB,   moduleId: MODULE_IDS.RIDER_MANAGER    },
                  { label: 'Aggregators',  icon: Globe,    to: '/apps/aggregator',     permission: PERMISSIONS.CAMPAIGN_VIEW,   moduleId: MODULE_IDS.CAMPAIGN_MANAGER },
                  { label: 'Menus',        icon: BookOpen, to: '/apps/menu-manage',    permission: PERMISSIONS.MENU_VIEW,       moduleId: MODULE_IDS.MENU_MANAGE      },
                  { label: 'Support',      icon: LifeBuoy, to: '/apps/support-desk',   permission: PERMISSIONS.SUPPORT_VIEW,    moduleId: MODULE_IDS.SUPPORT_DESK     },
                  { label: 'Market Map',   icon: MapPin,   to: '/apps/market-map',     permission: PERMISSIONS.MAP_VIEW,        moduleId: MODULE_IDS.MARKET_MAP       },
                ],
              },
              // ── TENANT: Business Hub (Master Data) ──
              {
                label: 'Business Hub',
                tenantOnly: true,
                items: [
                  { label: 'Brands',        icon: Tag,    to: '/system/brands',       permission: PERMISSIONS.SYS_MANAGE_BRANDS, moduleId: MODULE_IDS.SYSTEM, tabId: 'brands' },
                  { label: 'Departments',  icon: Layers, to: '/system/departments',  permission: PERMISSIONS.SYS_MANAGE_DEPARTMENTS, moduleId: MODULE_IDS.SYSTEM, tabId: 'departments' },
                ],
              },
              // ── TENANT: Organization ──
              {
                label: 'Organization',
                tenantOnly: true,
                items: [
                  { label: 'Explore Apps',       icon: ShoppingBag,    to: '/system/marketplace', permission: PERMISSIONS.SYS_MANAGE_USERS },
                  { label: 'User Accounts',      icon: Users,          to: '/system/users',     permission: PERMISSIONS.SYS_MANAGE_USERS,     moduleId: MODULE_IDS.SYSTEM, tabId: 'users' },
                  { label: 'API Settings',       icon: Key,            to: '/system/api',       permission: PERMISSIONS.SYS_MANAGE_API,       moduleId: MODULE_IDS.SYSTEM, tabId: 'api',       planFeature: 'apiAccess' },
                  { label: 'System Logs',        icon: ClipboardList,  to: '/system/logs',      permission: PERMISSIONS.SYS_VIEW_LOGS,        moduleId: MODULE_IDS.SYSTEM, tabId: 'logs',      planFeature: 'systemLogsAccess' },
                  { label: 'Whitelabel',         icon: Zap,            to: '/system/whitelabel', permission: PERMISSIONS.SYS_MANAGE_USERS, planFeature: 'whiteLabelAccess' },
                  { label: 'Maintenance',        icon: HardDrive,      to: '/system/maintenance', permission: PERMISSIONS.SYS_MANAGE_USERS, planFeature: 'maintenanceAccess' },
                ],
              },
            ];

            const unread = notifications.filter((n: any) => !n.isRead).length;
            const isDarkSidebar = isInternalAdmin && !isImpersonating;

            const visibleSections = menuSections
              .filter(section => {
                if (section.superAdminOnly && !isInternalAdmin) return false;
                // Tenant-only sections (Apps) are hidden from platform staff unless impersonating
                if (section.tenantOnly && isInternalAdmin && !isImpersonating) return false;
                return true;
              })
              .map(section => ({
                ...section,
                items: section.items.filter(item => {
                  // Platform-admin-only items — check granular platformRoles if specified
                  if (item.superAdminOnly) {
                    if (!isInternalAdmin) return false;
                    if (item.platformRoles && user) {
                      return item.platformRoles.includes(user.role);
                    }
                    return true; // no platformRoles restriction → visible to all platform staff
                  }

                  // Plan feature gate — hide if current org doesn't have the feature (platform staff bypasses)
                  if (item.planFeature && !isInternalAdmin && !canUsePlanFeature(item.planFeature)) return false;
                  // Org app gate — Dual-Key: treat undefined enabledApps as [] (strict lockdown)
                  // Platform staff bypass only when NOT impersonating (impersonation shows the real tenant view)
                  // System module is NEVER gated by enabledApps — it's core platform, not an installable app
                  if (item.moduleId && item.moduleId !== MODULE_IDS.SYSTEM) {
                    const gated = !isInternalAdmin || isImpersonating;
                    if (gated) {
                      const orgApps = currentOrg?.enabledApps ?? [];
                      if (!orgApps.includes(item.moduleId)) return false;
                    }
                  }
                  if (item.moduleId) {
                    // Pass org enabledApps so canAccessModule enforces org license + user permission together
                    const orgApps = (!isInternalAdmin || isImpersonating) ? (currentOrg?.enabledApps ?? []) : undefined;
                    if (!canAccessModule(user, item.moduleId, item.permission, orgApps)) return false;
                    if (item.tabId) return canAccessTab(user, item.moduleId, item.tabId);
                    return true;
                  }
                  return !item.permission || hasPermFn(user, item.permission);
                }),
              }))
              .filter(section => section.items.length > 0);

            return visibleSections.map(section => (
              <div key={section.label}>
                <p className={cn("text-[10px] font-bold uppercase tracking-widest px-3 mb-2", isDarkSidebar ? "text-slate-500" : "text-slate-400")}>{section.label}</p>
                <div className="flex flex-col gap-1">
                  {section.items.map(item => {
                    const isActive = !item.external && location.pathname.startsWith(item.to);
                    const cls = cn(
                      "flex items-center gap-2.5 px-3 py-2 rounded-xl transition-all duration-200",
                      isActive
                        ? isDarkSidebar ? "bg-slate-800 text-white font-semibold" : "bg-slate-100 text-slate-900 font-semibold"
                        : isDarkSidebar ? "text-slate-400 hover:bg-slate-800 hover:text-white" : "text-slate-500 hover:bg-slate-50 hover:text-slate-900"
                    );
                    if (item.external) {
                      return (
                        <a key={item.to} href={item.external} target="_blank" rel="noopener noreferrer" className={cls}>
                          <item.icon size={16} />
                          <span className="text-sm">{item.label}</span>
                          <ExternalLink size={11} className="ml-auto opacity-40" />
                        </a>
                      );
                    }
                    return (
                    <Link
                      key={item.to}
                      to={item.to}
                      className={cls}
                    >
                      <item.icon size={16} />
                      <span className="text-sm">{item.label}</span>
                      {item.to === '/notifications' && unread > 0 && (
                        <span className="ml-auto w-5 h-5 bg-red-500 text-white text-[10px] font-bold flex items-center justify-center rounded-full">
                          {unread}
                        </span>
                      )}
                    </Link>
                    );
                  })}
                </div>
              </div>
            ));
          })()}
        </div>

        <div className="mt-auto flex flex-col gap-1">
          {/* Company identity — tenant users only */}
          {!isInternalAdmin && currentOrg && (
            <div className="px-3 py-2 mb-1 border-t border-slate-100">
              <p className="text-[10px] font-bold text-slate-500 truncate">{currentOrg.name}</p>
              <p className="text-[9px] text-slate-400 uppercase tracking-wide">{currentOrg.plan ?? 'Free'} Plan</p>
            </div>
          )}
          <button
            onClick={async () => { if (getActiveTasks().some(t => t.status === 'pending') && !window.confirm('Background tasks are still running. Sign out anyway?')) return; await signOut(); }}
            className={cn("text-[10px] hover:text-red-400 transition-colors text-center w-full py-1 flex items-center justify-center gap-1", isInternalAdmin && !isImpersonating ? "text-slate-500" : "text-slate-400")}
          >
            <LogOut size={10} /> Sign Out
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <main className="ml-56 flex-1 bg-slate-50 min-h-screen">
        <div className="p-2">
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <div className="px-6 py-3 border-b border-slate-100 bg-slate-50/60">
              <Breadcrumbs />
            </div>
            <div className="p-6">
              <Suspense fallback={<PlatformLoadingScreen />}>
              <Routes>
                {/* Universal Launchpad — accessible by all roles */}
                <Route path="/dashboard" element={<Dashboard user={user} currentOrg={currentOrg} isImpersonating={isImpersonating} />} />

                {/* Default route → dashboard */}
                <Route path="/" element={<Navigate to="/dashboard" replace />} />

                {/* 403 Permission Denied */}
                <Route path="/403" element={<PermissionDeniedPage user={user} />} />

                {/* Shared Notifications */}
                <Route path="/notifications" element={
                  <ProtectedRoute allowedRoles={[...PLATFORM_ROLES, UserRole.ADMIN, UserRole.DEPARTMENT_HEAD, UserRole.MANAGER, UserRole.EMPLOYEE]} user={user}>
                    <div><h2 className="text-2xl font-bold mb-6">Notifications</h2><NotificationList notifications={notifications} markRead={markRead} riders={allRiders} setSelectedRider={setShowDetailPopup} /></div>
                  </ProtectedRoute>
                } />

                {/* My Profile — accessible by all authenticated users */}
                <Route path="/profile" element={
                  <ProfilePage
                    user={user}
                    orgName={currentOrg?.name}
                    onChangePassword={async (currentPwd, newPwd) => {
                      if (user.passwordHash !== currentPwd) {
                        return { success: false, error: 'Current password is incorrect.' };
                      }
                      const updated: User = { ...user, passwordHash: newPwd };
                      await repository.saveUser(updated);
                      await repository.setCurrentUser(updated);
                      await refresh();
                      return { success: true };
                    }}
                  />
                } />

                {/* God Mode — Platform Owner/Support read-only org inspector (placed BEFORE /saas-admin/* so it takes priority) */}
                <Route path="/saas-admin/inspect/:orgId/*" element={
                  <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN, UserRole.PLATFORM_OWNER, UserRole.PLATFORM_SUPPORT]} user={user}>
                    {user?.canInspectTenants === false
                      ? <Navigate to="/403" replace />
                      : <GodModeInspectorPage currentUser={user} writeAuditLog={writeAuditLog} />}
                  </ProtectedRoute>
                } />

                {/* Module Catalog — standalone page (placed BEFORE /saas-admin/* so it takes priority) */}
                <Route path="/saas-admin/lab" element={
                  <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN, UserRole.PLATFORM_OWNER, UserRole.PLATFORM_SALES]} user={user}>
                    <LabPage />
                  </ProtectedRoute>
                } />

                {/* Platform-level API Management — cross-org oversight + developer docs */}
                <Route path="/saas-admin/api" element={
                  <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN, UserRole.PLATFORM_OWNER]} user={user}>
                    <PlatformApiDashboard />
                  </ProtectedRoute>
                } />

                {/* Platform Settings — branding, landing CMS, login CMS */}
                <Route path="/saas-admin/settings" element={
                  <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN, UserRole.PLATFORM_OWNER]} user={user}>
                    <PlatformSettingsPage />
                  </ProtectedRoute>
                } />

                {/* Platform Admin Routes — all platform roles can access, sub-tab gating inside SaasAdminPage */}
                <Route path="/saas-admin/*" element={
                  <ProtectedRoute allowedRoles={[...PLATFORM_ROLES]} user={user}>
                    <SaasAdminPage
                      onSwitchOrgContext={switchOrgContext}
                      currentUser={user}
                      onImpersonate={startImpersonation}
                      onInspect={(org) => navigate('/saas-admin/inspect/' + org.id)}
                    />
                  </ProtectedRoute>
                } />

                {/* System Routes — module + tab gated */}
                <Route path="/system/*" element={
                  <ProtectedRoute moduleId={MODULE_IDS.SYSTEM} user={user} currentOrg={currentOrg}>
                    <Routes>
                      <Route path="logs" element={
                        <ProtectedRoute moduleId={MODULE_IDS.SYSTEM} tabId="logs" permission={PERMISSIONS.SYS_VIEW_LOGS} user={user} currentOrg={currentOrg}>
                          <SystemLogsPage />
                        </ProtectedRoute>
                      } />
                      <Route path="users" element={
                        <ProtectedRoute moduleId={MODULE_IDS.SYSTEM} tabId="users" permission={PERMISSIONS.SYS_MANAGE_USERS} user={user} currentOrg={currentOrg}>
                          <UserAccountsPage settings={settings} currentOrg={currentOrg} orgPlan={orgPlan} isSuperAdmin={isSuperAdmin} isInternalAdmin={isInternalAdmin} isPlatformOwner={isPlatformOwner} currentUser={user} />
                        </ProtectedRoute>
                      } />
                      <Route path="api" element={
                        <ProtectedRoute moduleId={MODULE_IDS.SYSTEM} tabId="api" permission={PERMISSIONS.SYS_MANAGE_API} user={user} currentOrg={currentOrg}>
                          <ApiManagementPage allRiders={allRiders} settings={settings} currentOrg={currentOrg} />
                        </ProtectedRoute>
                      } />
                      <Route path="brands" element={
                        <ProtectedRoute moduleId={MODULE_IDS.SYSTEM} tabId="brands" permission={PERMISSIONS.SYS_MANAGE_BRANDS} user={user} currentOrg={currentOrg}>
                          <BrandManagementPage />
                        </ProtectedRoute>
                      } />
                      <Route path="departments" element={
                        <ProtectedRoute moduleId={MODULE_IDS.SYSTEM} tabId="departments" permission={PERMISSIONS.SYS_MANAGE_DEPARTMENTS} user={user} currentOrg={currentOrg}>
                          <DepartmentRegistryPage />
                        </ProtectedRoute>
                      } />
                      <Route path="marketplace" element={
                        <ProtectedRoute permission={PERMISSIONS.SYS_MANAGE_USERS} user={user}>
                          <MarketplacePage currentOrg={currentOrg} user={user} />
                        </ProtectedRoute>
                      } />
                      <Route path="whitelabel" element={
                        <ProtectedRoute permission={PERMISSIONS.SYS_MANAGE_USERS} user={user} currentOrg={currentOrg}>
                          <WhiteLabelSettingsPage currentOrg={currentOrg} canUsePlanFeature={canUsePlanFeature} repository={repository} onBrandingSaved={refresh} />
                        </ProtectedRoute>
                      } />
                      <Route path="maintenance" element={
                        <ProtectedRoute permission={PERMISSIONS.SYS_MANAGE_USERS} user={user} currentOrg={currentOrg}>
                          <MaintenancePage currentOrg={currentOrg} canUsePlanFeature={canUsePlanFeature} isInternalAdmin={isInternalAdmin} />
                        </ProtectedRoute>
                      } />
                    </Routes>
                  </ProtectedRoute>
                } />

                {/* Apps — Rider Manager Module */}
                <Route path="/apps/rider-manager/*" element={
                  <ProtectedRoute moduleId={MODULE_IDS.RIDER_MANAGER} permission={PERMISSIONS.RIDER_VIEW_DB} user={user} currentOrg={currentOrg}>
                    <ModuleGuard appId={APP_IDS.RIDER_MANAGER} currentOrg={currentOrg} isSuperAdmin={isInternalAdmin} isImpersonating={isImpersonating} user={user}>
                      <RiderManagerModule
                      user={user}
                      riders={hasTabVariant(user, 'rider-manager', 'database', 'admin') ? allRiders : riders}
                      allRiders={allRiders}
                      requests={requests}
                      settings={settings}
                      deleteRider={deleteRider}
                      toggleRiderStatus={toggleRiderStatus}
                      submitRequest={submitRequest}
                      updateRequestStatus={updateRequestStatus}
                      addRider={addRider}
                      updateRider={updateRider}
                      checkFileNumberConflict={checkFileNumberConflict}
                      setShowDetailPopup={setShowDetailPopup}
                      nextAutoNumber={nextAutoNumber}
                      updateSettings={updateSettings}
                      currentOrg={currentOrg}
                      orgPlan={orgPlan}
                      subscriptionPlans={subscriptionPlans}
                      maskData={maskData}
                      onDataExport={handleDataExport}
                    />
                    </ModuleGuard>
                  </ProtectedRoute>
                } />

                {/* Apps — Aggregator Operations Module */}
                <Route path="/apps/aggregator/*" element={
                  <ProtectedRoute moduleId={MODULE_IDS.CAMPAIGN_MANAGER} permission={PERMISSIONS.CAMPAIGN_VIEW} user={user} currentOrg={currentOrg}>
                    <ModuleGuard appId={APP_IDS.CAMPAIGN_MANAGER} currentOrg={currentOrg} isSuperAdmin={isInternalAdmin} isImpersonating={isImpersonating} user={user}>
                      <AggregatorModule
                        user={user}
                      />
                    </ModuleGuard>
                  </ProtectedRoute>
                } />

                {/* Apps — Menu Management Module */}
                <Route path="/apps/menu-manage/*" element={
                  <ProtectedRoute moduleId={MODULE_IDS.MENU_MANAGE} permission={PERMISSIONS.MENU_VIEW} user={user} currentOrg={currentOrg}>
                    <ModuleGuard appId={APP_IDS.MENU_MANAGE} currentOrg={currentOrg} isSuperAdmin={isInternalAdmin} isImpersonating={isImpersonating} user={user}>
                      <MenuManageModule
                        user={user}
                      />
                    </ModuleGuard>
                  </ProtectedRoute>
                } />

                {/* Apps — Support & Ticketing Module */}
                <Route path="/apps/support-desk/*" element={
                  <ProtectedRoute moduleId={MODULE_IDS.SUPPORT_DESK} permission={PERMISSIONS.SUPPORT_VIEW} user={user} currentOrg={currentOrg}>
                    <ModuleGuard appId={APP_IDS.SUPPORT_DESK} currentOrg={currentOrg} isSuperAdmin={isInternalAdmin} isImpersonating={isImpersonating} user={user}>
                      <SupportDeskModule
                        user={user}
                        orgEnabledApps={currentOrg?.enabledApps}
                      />
                    </ModuleGuard>
                  </ProtectedRoute>
                } />

                {/* Apps — Market Intelligence & Map Module */}
                <Route path="/apps/market-map/*" element={
                  <ProtectedRoute moduleId={MODULE_IDS.MARKET_MAP} permission={PERMISSIONS.MAP_VIEW} user={user} currentOrg={currentOrg}>
                    <ModuleGuard appId={APP_IDS.MARKET_MAP} currentOrg={currentOrg} isSuperAdmin={isInternalAdmin} isImpersonating={isImpersonating} user={user}>
                      <MarketMapModule user={user} />
                    </ModuleGuard>
                  </ProtectedRoute>
                } />
              </Routes>
              </Suspense>
            </div>
          </div>
        </div>
      </main>

      {/* Detail Popup */}
      {showDetailPopup && (
        <Suspense fallback={<PlatformLoadingScreen />}>
          <RiderDetailPopup
            rider={showDetailPopup}
            settings={settings}
            user={user}
            submitRequest={submitRequest}
            onClose={() => {
              setShowDetailPopup(null);
              navigate('/apps/rider-manager/database');
            }}
          />
        </Suspense>
      )}

      {/* ═══ Floating Support Ticket Side-Car ═══ */}
      <SupportSideCar />

    </div>
  );
}

function SupportSideCar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidecar, setSidecar] = useState<{ ticketId: string; ticketRef: string; issueCategory: string; module: string } | null>(null);

  // Read sidecar state from sessionStorage on route change
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('support_sidecar');
      if (raw) {
        const parsed = JSON.parse(raw);
        // Only show when NOT on the support-desk route
        if (!location.pathname.startsWith('/apps/support-desk')) {
          setSidecar(parsed);
        } else {
          setSidecar(null);
        }
      } else {
        setSidecar(null);
      }
    } catch { setSidecar(null); }
  }, [location.pathname]);

  if (!sidecar) return null;

  return (
    <div className="fixed bottom-6 right-6 z-[9999] group">
      <div className="bg-white border border-indigo-200 rounded-2xl shadow-2xl shadow-indigo-100/50 px-4 py-3 min-w-[220px] flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center text-white shrink-0 shadow-sm">
          <LifeBuoy size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold text-indigo-600 uppercase tracking-widest">Active Ticket</p>
          <p className="text-xs font-bold text-zinc-900 truncate">{sidecar.ticketRef}</p>
          <p className="text-[10px] text-zinc-500 truncate">{sidecar.issueCategory}</p>
        </div>
        <div className="flex flex-col gap-1 shrink-0">
          <button
            onClick={() => {
              navigate(`/apps/support-desk/ticket-center?ticketId=${sidecar.ticketId}`);
              try { sessionStorage.removeItem('support_sidecar'); } catch {}
              setSidecar(null);
            }}
            className="px-2.5 py-1 bg-indigo-600 text-white rounded-lg text-[10px] font-bold hover:bg-indigo-700 transition-all"
          >
            Return
          </button>
          <button
            onClick={() => {
              try { sessionStorage.removeItem('support_sidecar'); } catch {}
              setSidecar(null);
            }}
            className="px-2.5 py-1 bg-zinc-100 text-zinc-500 rounded-lg text-[10px] font-bold hover:bg-zinc-200 transition-all"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}

function Breadcrumbs() {
  const location = useLocation();
  const { user } = useAuth();
  const isSA = isPlatformRole(user?.role);
  const allPathnames = location.pathname.split('/').filter((x) => x);
  // skip 'apps' and 'saas-admin' from display — they're routing namespaces, not useful crumbs
  const pathnames = allPathnames.filter((x) => x !== 'apps' && x !== 'saas-admin');

  const labelMap: Record<string, string> = {
    'apps': 'Applications',
    'rider-manager': 'Rider Manager',
    'campaign-manager': 'Aggregator Operations',
    'aggregator': 'Aggregator Operations',
    'database': 'Rider Database',
    'inbox': 'Request Inbox',
    'submit': 'New Request',
    'transfers': 'Transfer Requests',
    'analytics': 'Analytics',
    'settings': 'App Settings',
    'management': 'Management',
    'system': 'System',
    'locations': 'Branch Registry',
    'logs': 'System Logs',
    'notifications': 'Notifications',
    'new': 'Register Rider',
    'users': isSA ? 'Internal Accounts' : 'User Accounts',
    'api': isSA ? 'API Management' : 'API Settings',
    'brands': 'Brand Registry',
    'departments': 'Department Registry',
    'marketplace': 'Explore Apps',
    'organizations': 'Organizations',
    'health': 'Platform Health',
    'subscriptions': 'Subscriptions',
    'requests': 'Module Requests',
    'lab': 'Module Catalog',
    'inspect': 'Inspect Data',
    'menu-manage': 'Menu Management',
    'support-desk': 'Support & Ticketing',
    'profile': 'My Profile',
  };

  // Root label: SA sees "Platform Admin", tenants see "Business Hub"
  const rootLabel = isSA ? 'Platform Admin' : 'Business Hub';

  const getLabel = (segment: string) => {
    return labelMap[segment.toLowerCase()] ?? (segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' '));
  };

  return (
    <div className="flex items-center gap-1.5 text-xs font-medium text-slate-400">
      <Link to="/" className="hover:text-slate-700 transition-colors font-semibold">{rootLabel}</Link>
      {pathnames.map((value, index) => {
        const last = index === pathnames.length - 1;
        // build the full URL using allPathnames so 'apps' is included in the href
        const posInAll = allPathnames.indexOf(value);
        const to = '/' + allPathnames.slice(0, posInAll + 1).join('/');
        const label = getLabel(value);

        return (
          <React.Fragment key={to}>
            <ChevronRight size={13} className="text-slate-300 flex-shrink-0" />
            {last ? (
              <span className="text-slate-900 font-bold">{label}</span>
            ) : (
              <Link to={to} className="hover:text-slate-700 transition-colors">
                {label}
              </Link>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function Launchpad({ user, currentOrg, isImpersonating }: { user: User | null, currentOrg?: Organization | null, isImpersonating?: boolean }) {
  const navigate = useNavigate();
  const isSuperAdmin = isPlatformRole(user?.role);
  const isAdmin = user?.role === UserRole.ADMIN;

  const [launchpadBrands, setLaunchpadBrands] = useState<Brand[]>([]);
  const [launchpadUsers, setLaunchpadUsers] = useState<User[]>([]);
  // SA platform-level stats
  const [saStats, setSaStats] = useState<{ totalOrgs: number; totalUsers: number; totalApps: number; pendingModuleReqs: number } | null>(null);

  useEffect(() => {
    const repo = getRepository();
    if (isSuperAdmin) {
      Promise.all([
        repo.getOrganizations(),
        repo.getUsers(),
        repo.getModuleRequests(),
      ]).then(([orgs, users, modReqs]) => {
        const totalApps = orgs.reduce((sum: number, o: Organization) => sum + (o.enabledApps?.length ?? 0), 0);
        const pendingModuleReqs = modReqs.filter((r: any) => r.status === 'pending').length;
        setSaStats({
          totalOrgs: orgs.length,
          totalUsers: users.filter((u: User) => u.orgId && u.orgId !== '__platform__').length,
          totalApps,
          pendingModuleReqs,
        });
      });
    } else {
      Promise.all([
        repo.getBrands(),
        repo.getUsers(),
      ]).then(([brands, users]) => {
        setLaunchpadBrands(brands);
        setLaunchpadUsers(users);
      });
    }
  }, [isSuperAdmin, currentOrg?.id]);
  const installedApps = currentOrg?.enabledApps?.length ?? 0;
  const activeStaff = launchpadUsers.filter(u => (u.status ?? UserStatus.ACTIVE) === UserStatus.ACTIVE).length;
  const registeredBrands = launchpadBrands.length;
  const activeBranches = flattenBranches(launchpadBrands).length;

  // Dual-Key: canAccessModule now enforces both org license AND user permission.
  // Treat undefined enabledApps as [] → strict zero-state lockdown.
  const orgApps = currentOrg?.enabledApps ?? [];
  const showCampaignCard = !isSuperAdmin &&
    canAccessModule(user, MODULE_IDS.CAMPAIGN_MANAGER, undefined, orgApps);

  const showMenuCard = !isSuperAdmin &&
    canAccessModule(user, MODULE_IDS.MENU_MANAGE, undefined, orgApps);

  const showSupportCard = !isSuperAdmin &&
    canAccessModule(user, MODULE_IDS.SUPPORT_DESK, undefined, orgApps);

  const showMarketMapCard = !isSuperAdmin &&
    canAccessModule(user, MODULE_IDS.MARKET_MAP, undefined, orgApps);

  return (
    <div className="space-y-8">
      {isImpersonating && currentOrg && (
        <div className="flex items-center gap-3 px-5 py-3.5 bg-amber-50 border border-amber-200 rounded-2xl text-sm">
          <Eye size={16} className="text-amber-600 shrink-0" />
          <span className="text-amber-800 font-medium">
            You are viewing the Launchpad <strong>exactly as a user from <span className="underline">{currentOrg.name}</span> sees it</strong>.
            Apps and modules shown reflect that org&apos;s current configuration.
          </span>
        </div>
      )}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h2 className="text-3xl font-bold tracking-tight text-zinc-900">{isSuperAdmin ? 'Platform Dashboard' : isAdmin ? 'Admin Dashboard' : 'Dashboard'}</h2>
          <p className="text-sm text-slate-500">
            Welcome back, {user?.name}. Here's a high-level overview of {isSuperAdmin ? 'the entire platform' : isAdmin ? 'the platform' : 'your workspace'}.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="px-4 py-2 bg-emerald-50 text-emerald-700 rounded-2xl border border-emerald-100 flex items-center gap-2 text-sm font-bold">
            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            System Online
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      {isSuperAdmin ? (
        <div className="grid gap-4 grid-cols-4">
          <div className="bg-white border border-zinc-200 p-5 rounded-2xl shadow-sm flex items-center gap-4">
            <div className="w-11 h-11 bg-indigo-500 rounded-xl flex items-center justify-center text-white">
              <Building2 size={20} />
            </div>
            <div>
              <p className="text-sm font-bold text-zinc-400 uppercase tracking-widest mb-1">Organisations</p>
              <p className="text-2xl font-bold tracking-tight">{saStats?.totalOrgs ?? '—'}</p>
            </div>
          </div>
          <div className="bg-white border border-zinc-200 p-5 rounded-2xl shadow-sm flex items-center gap-4">
            <div className="w-11 h-11 bg-blue-500 rounded-xl flex items-center justify-center text-white">
              <Users size={20} />
            </div>
            <div>
              <p className="text-sm font-bold text-zinc-400 uppercase tracking-widest mb-1">Tenant Users</p>
              <p className="text-2xl font-bold tracking-tight">{saStats?.totalUsers ?? '—'}</p>
            </div>
          </div>
          <div className="bg-white border border-zinc-200 p-5 rounded-2xl shadow-sm flex items-center gap-4">
            <div className="w-11 h-11 bg-emerald-500 rounded-xl flex items-center justify-center text-white">
              <LayoutGrid size={20} />
            </div>
            <div>
              <p className="text-sm font-bold text-zinc-400 uppercase tracking-widest mb-1">Apps Installed</p>
              <p className="text-2xl font-bold tracking-tight">{saStats?.totalApps ?? '—'}</p>
            </div>
          </div>
          <div className="bg-white border border-zinc-200 p-5 rounded-2xl shadow-sm flex items-center gap-4">
            <div className="w-11 h-11 bg-amber-500 rounded-xl flex items-center justify-center text-white">
              <Inbox size={20} />
            </div>
            <div>
              <p className="text-sm font-bold text-zinc-400 uppercase tracking-widest mb-1">Module Requests</p>
              <p className="text-2xl font-bold tracking-tight">{saStats?.pendingModuleReqs ?? '—'}</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-4">
          <div className="bg-white border border-zinc-200 p-5 rounded-2xl shadow-sm flex items-center gap-4">
            <div className="w-11 h-11 bg-zinc-900 rounded-xl flex items-center justify-center text-white">
              <LayoutGrid size={20} />
            </div>
            <div>
              <p className="text-sm font-bold text-zinc-400 uppercase tracking-widest mb-1">Installed Apps</p>
              <p className="text-2xl font-bold tracking-tight">{installedApps}</p>
            </div>
          </div>
          <div className="bg-white border border-zinc-200 p-5 rounded-2xl shadow-sm flex items-center gap-4">
            <div className="w-11 h-11 bg-indigo-500 rounded-xl flex items-center justify-center text-white">
              <Users size={20} />
            </div>
            <div>
              <p className="text-sm font-bold text-zinc-400 uppercase tracking-widest mb-1">Active Staff</p>
              <p className="text-2xl font-bold tracking-tight">{activeStaff}</p>
            </div>
          </div>
          <div className="bg-white border border-zinc-200 p-5 rounded-2xl shadow-sm flex items-center gap-4">
            <div className="w-11 h-11 bg-emerald-500 rounded-xl flex items-center justify-center text-white">
              <Tag size={20} />
            </div>
            <div>
              <p className="text-sm font-bold text-zinc-400 uppercase tracking-widest mb-1">Registered Brands</p>
              <p className="text-2xl font-bold tracking-tight">{registeredBrands}</p>
            </div>
          </div>
          <div className="bg-white border border-zinc-200 p-5 rounded-2xl shadow-sm flex items-center gap-4">
            <div className="w-11 h-11 bg-amber-500 rounded-xl flex items-center justify-center text-white">
              <MapPin size={20} />
            </div>
            <div>
              <p className="text-sm font-bold text-zinc-400 uppercase tracking-widest mb-1">Active Branches</p>
              <p className="text-2xl font-bold tracking-tight">{activeBranches}</p>
            </div>
          </div>
        </div>
      )}

      <div className={cn("grid gap-5", (() => {
        // Super admin always has 2 cards (Organizations + Platform Health).
        // Tenant shell cards are system and licensed non-rider modules only.
        const nonSACards = 1 + [showCampaignCard, showMenuCard, showSupportCard, showMarketMapCard].filter(Boolean).length;
        const count = isSuperAdmin ? 2 : nonSACards;
        if (count >= 5) return "grid-cols-2 md:grid-cols-5";
        if (count === 4) return "grid-cols-2 md:grid-cols-4";
        if (count === 3) return "grid-cols-3";
        if (count === 2) return "grid-cols-2";
        return "grid-cols-1";
      })())}>
        {isSuperAdmin && (
        <button
          onClick={() => navigate('/saas-admin/organizations')}
          className="bg-white border border-zinc-200 p-8 rounded-2xl shadow-sm hover:border-zinc-900 hover:shadow-2xl transition-all text-left group flex flex-col h-full relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-zinc-900/5 rounded-full -mr-32 -mt-32 group-hover:scale-110 transition-transform duration-500" />
          <div className="w-14 h-14 bg-zinc-900 rounded-2xl flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform relative z-10">
            <Building2 size={28} />
          </div>
          <h3 className="text-xl font-bold text-zinc-900 mb-2 relative z-10">Organizations</h3>
          <p className="text-zinc-500 text-sm mb-6 flex-1 relative z-10">Manage tenant organizations, plans, and billing.</p>
          <div className="flex items-center gap-3 text-zinc-900 font-bold text-sm uppercase tracking-widest relative z-10">
            Manage Orgs <ChevronRight size={20} />
          </div>
        </button>
        )}

        {isSuperAdmin ? (
          <button
            onClick={() => navigate('/saas-admin/health')}
            className="bg-white border border-zinc-200 p-8 rounded-2xl shadow-sm hover:border-zinc-900 hover:shadow-2xl transition-all text-left group flex flex-col h-full relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-violet-500/5 rounded-full -mr-32 -mt-32 group-hover:scale-110 transition-transform duration-500" />
            <div className="w-14 h-14 bg-violet-500 rounded-2xl flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform relative z-10">
              <BarChart3 size={28} />
            </div>
            <h3 className="text-xl font-bold text-zinc-900 mb-2 relative z-10">Platform Health</h3>
            <p className="text-zinc-500 text-sm mb-6 flex-1 relative z-10">Monitor platform-wide statistics, usage metrics, and system activity.</p>
            <div className="flex items-center gap-3 text-zinc-900 font-bold text-sm uppercase tracking-widest relative z-10">
              View Health <ChevronRight size={20} />
            </div>
          </button>
        ) : isAdmin ? (
          <button
            onClick={() => navigate('/system/brands')}
            className="bg-white border border-zinc-200 p-8 rounded-2xl shadow-sm hover:border-zinc-900 hover:shadow-2xl transition-all text-left group flex flex-col h-full relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/5 rounded-full -mr-32 -mt-32 group-hover:scale-110 transition-transform duration-500" />
            <div className="w-14 h-14 bg-indigo-500 rounded-2xl flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform relative z-10">
              <SettingsIcon size={28} />
            </div>
            <h3 className="text-xl font-bold text-zinc-900 mb-2 relative z-10">System Settings</h3>
            <p className="text-zinc-500 text-sm mb-6 flex-1 relative z-10">Manage brands, user accounts, and system-wide preferences.</p>
            <div className="flex items-center gap-3 text-zinc-900 font-bold text-sm uppercase tracking-widest relative z-10">
              Configure System <ChevronRight size={20} />
            </div>
          </button>
        ) : null}

        {showCampaignCard && (
          <button
            onClick={() => navigate('/apps/aggregator')}
            className="bg-white border border-zinc-200 p-8 rounded-2xl shadow-sm hover:border-teal-400 hover:shadow-2xl transition-all text-left group flex flex-col h-full relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-teal-500/5 rounded-full -mr-32 -mt-32 group-hover:scale-110 transition-transform duration-500" />
            <div className="w-14 h-14 bg-teal-500 rounded-2xl flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform relative z-10">
              <Globe size={28} />
            </div>
            <h3 className="text-xl font-bold text-zinc-900 mb-2 relative z-10">Aggregator Operations</h3>
            <p className="text-zinc-500 text-sm mb-6 flex-1 relative z-10">Orchestrate brand promotions, discounts, and aggregator campaigns.</p>
            <div className="flex items-center gap-3 text-teal-700 font-bold text-sm uppercase tracking-widest relative z-10">
              Open Module <ChevronRight size={20} />
            </div>
          </button>
        )}

        {showMenuCard && (
          <button
            onClick={() => navigate('/apps/menu-manage')}
            className="bg-white border border-zinc-200 p-8 rounded-2xl shadow-sm hover:border-indigo-400 hover:shadow-2xl transition-all text-left group flex flex-col h-full relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/5 rounded-full -mr-32 -mt-32 group-hover:scale-110 transition-transform duration-500" />
            <div className="w-14 h-14 bg-indigo-600 rounded-2xl flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform relative z-10">
              <BookOpen size={28} />
            </div>
            <h3 className="text-xl font-bold text-zinc-900 mb-2 relative z-10">Menu Management</h3>
            <p className="text-zinc-500 text-sm mb-6 flex-1 relative z-10">Build dual-language menus, manage ministry permits, and preview digital menus.</p>
            <div className="flex items-center gap-3 text-indigo-700 font-bold text-sm uppercase tracking-widest relative z-10">
              Open Module <ChevronRight size={20} />
            </div>
          </button>
        )}

        {showSupportCard && (
          <button
            onClick={() => navigate('/apps/support-desk')}
            className="bg-white border border-zinc-200 p-8 rounded-2xl shadow-sm hover:border-violet-400 hover:shadow-2xl transition-all text-left group flex flex-col h-full relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-violet-500/5 rounded-full -mr-32 -mt-32 group-hover:scale-110 transition-transform duration-500" />
            <div className="w-14 h-14 bg-violet-600 rounded-2xl flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform relative z-10">
              <LifeBuoy size={28} />
            </div>
            <h3 className="text-xl font-bold text-zinc-900 mb-2 relative z-10">Support & Ticketing</h3>
            <p className="text-zinc-500 text-sm mb-6 flex-1 relative z-10">Report issues, track SLAs, and manage centralised helpdesk operations.</p>
            <div className="flex items-center gap-3 text-violet-700 font-bold text-sm uppercase tracking-widest relative z-10">
              Open Module <ChevronRight size={20} />
            </div>
          </button>
        )}

        {showMarketMapCard && (
          <button
            onClick={() => navigate('/apps/market-map')}
            className="bg-white border border-zinc-200 p-8 rounded-2xl shadow-sm hover:border-cyan-400 hover:shadow-2xl transition-all text-left group flex flex-col h-full relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-500/5 rounded-full -mr-32 -mt-32 group-hover:scale-110 transition-transform duration-500" />
            <div className="w-14 h-14 bg-cyan-600 rounded-2xl flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform relative z-10">
              <MapPin size={28} />
            </div>
            <h3 className="text-xl font-bold text-zinc-900 mb-2 relative z-10">Market Intelligence</h3>
            <p className="text-zinc-500 text-sm mb-6 flex-1 relative z-10">GIS command center for branch performance, competitor tracking, and live sales mapping.</p>
            <div className="flex items-center gap-3 text-cyan-700 font-bold text-sm uppercase tracking-widest relative z-10">
              Open Module <ChevronRight size={20} />
            </div>
          </button>
        )}
      </div>
    </div>
  );
}

function NotificationList({ notifications, markRead, riders, setSelectedRider }: any) {
  const navigate = useNavigate();

  const handleCardClick = (n: any) => {
    if (!n.isRead) markRead(n.id);
    navigate(n.targetPath);
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden shadow-sm">
      <div className="divide-y divide-zinc-100">
        {notifications.length === 0 ? (
          <div className="p-20 text-center text-slate-400 italic">No notifications yet.</div>
        ) : (
          notifications.sort((a: any, b: any) => b.createdAt - a.createdAt).map((n: any) => (
            <div
              key={n.id}
              onClick={() => handleCardClick(n)}
              className={cn(
                "p-6 flex items-start justify-between group hover:bg-zinc-50 transition-colors cursor-pointer",
                !n.isRead && "bg-zinc-50/50"
              )}
            >
              <div className="flex gap-4">
                <div className={cn(
                  "w-12 h-12 rounded-2xl flex items-center justify-center shrink-0",
                  n.isRead ? "bg-zinc-100 text-zinc-400" : "bg-emerald-100 text-emerald-600"
                )}>
                  <Bell size={20} />
                </div>
                <div>
                  <h4 className="font-bold text-zinc-900">{n.title}</h4>
                  <p className="text-zinc-500 text-sm mt-1">{n.message}</p>
                  <div className="flex items-center gap-4 mt-3">
                    <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">
                      {new Date(n.createdAt).toLocaleString()}
                    </span>
                    {n.riderId && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const rider = riders.find((r: any) => r.id === n.riderId);
                          if (rider) setSelectedRider(rider);
                        }}
                        className="text-[10px] font-bold text-indigo-600 uppercase tracking-widest hover:underline"
                      >
                        View Rider
                      </button>
                    )}
                  </div>
                </div>
              </div>
              {!n.isRead && (
                <button
                  onClick={(e) => { e.stopPropagation(); markRead(n.id); }}
                  className="px-4 py-2 bg-zinc-900 text-white rounded-xl text-[10px] font-bold uppercase tracking-widest hover:bg-zinc-800 transition-all opacity-0 group-hover:opacity-100"
                >
                  Mark Read
                </button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function SystemLogsPage() {
  const [logs, setLogs] = useState<SystemLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [moduleFilter, setModuleFilter] = useState<string>('all');
  const repository = getRepository();

  useEffect(() => {
    repository.getSystemLogs().then(data => {
      setLogs(data);
      setLoading(false);
    });
  }, []);

  const filtered = moduleFilter === 'all' ? logs : logs.filter(l => (l.moduleId || MODULE_IDS.SYSTEM) === moduleFilter);

  const moduleBadge = (moduleId?: string) => {
    switch (moduleId) {
      case 'aggregator':
        return <span className="px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest bg-purple-100 text-purple-700 border border-purple-200">Aggregator</span>;
      case 'rider-ops':
        return <span className="px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest bg-blue-100 text-blue-700 border border-blue-200">Rider Ops</span>;
      default:
        return <span className="px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest bg-zinc-100 text-zinc-500 border border-zinc-200">System</span>;
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">System Logs</h2>
          <p className="text-sm text-slate-500 mt-1">Cross-module audit trail for all critical actions.</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Module:</span>
          <CustomSelect value={moduleFilter} onChange={v => setModuleFilter(v)} placeholder="All Modules"
            options={[
              { value: 'all', label: 'All Modules' },
              { value: 'aggregator', label: 'Aggregator Ops' },
              { value: 'rider-ops', label: 'Rider Ops' },
              { value: MODULE_IDS.SYSTEM, label: 'System' },
            ]} />
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden shadow-sm">
        {loading ? (
          <LoadingSpinner message="Loading logs..." />
        ) : filtered.length === 0 ? (
          <div className="p-20 text-center text-slate-400 italic">
            {moduleFilter === 'all' ? 'No system logs yet.' : `No logs for ${moduleFilter === 'aggregator' ? 'Aggregator Ops' : moduleFilter === 'rider-ops' ? 'Rider Ops' : 'System'}.`}
          </div>
        ) : (
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-zinc-50 border-b border-zinc-200">
                <th className="px-6 py-4 text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Timestamp</th>
                <th className="px-6 py-4 text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Module</th>
                <th className="px-6 py-4 text-[10px] font-bold text-zinc-400 uppercase tracking-widest">User</th>
                <th className="px-6 py-4 text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Action</th>
                <th className="px-6 py-4 text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Subject</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {filtered.map(log => (
                <tr key={log.id} className="hover:bg-zinc-50/50 transition-colors">
                  <td className="px-6 py-4 text-xs text-zinc-500 tabular-nums whitespace-nowrap">
                    {new Date(log.timestamp).toLocaleString()}
                  </td>
                  <td className="px-6 py-4">{moduleBadge(log.moduleId)}</td>
                  <td className="px-6 py-4 text-sm font-semibold text-zinc-900">{log.userName}</td>
                  <td className="px-6 py-4 text-sm text-zinc-700 max-w-xs truncate">{log.action}</td>
                  <td className="px-6 py-4 text-xs text-zinc-500">{log.riderName || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─── Brand Management Page (Unified Brand + Branch Registry) ─────────────────

const INSTALLABLE_MODULES: { id: string; label: string }[] = [
  { id: 'rider-manager', label: 'Rider Operations' },
  { id: 'aggregator',    label: 'Aggregator' },
  { id: 'menu-manage',   label: 'Menu Management' },
  { id: 'support-desk',  label: 'Support & Ticketing' },
];

function BrandManagementPage() {
  const repo = getRepository();
  const toast = useToast();
  const [brands, setBrands] = useState<Brand[]>([]);
  const [deleteConfirm, setDeleteConfirm] = useState<DeleteConfirmState | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingBrand, setEditingBrand] = useState<Brand | null>(null);
  const [managingBrandId, setManagingBrandId] = useState<string | null>(null);
  const [branchDrafts, setBranchDrafts] = useState<Record<string, Branch[]>>({});

  // Form state
  const emptyForm = { name: '', logoUrl: '', enabledModules: [] as string[] };
  const [form, setForm] = useState(emptyForm);
  const brandFormSnap = React.useRef<typeof emptyForm | null>(null);
  const brandDirty = React.useMemo(() => editingBrand ? hasDataChanged(brandFormSnap.current, form) : !!form.name.trim(), [form, editingBrand]);

  const [uploadingBrandLogo, setUploadingBrandLogo] = useState(false);
  const brandLogoRef = React.useRef<HTMLInputElement>(null);

  const resolveLogoUrl = (url?: string) => {
    if (!url) return undefined;
    if (url.startsWith('http') || url.startsWith('data:')) return url;
    const apiBase = (import.meta as any).env?.VITE_API_URL ?? 'http://localhost:8787';
    return `${apiBase}${url}`;
  };

  const uploadBrandLogo = async (file: File) => {
    if (!isRemoteMode()) return;
    setUploadingBrandLogo(true);
    try {
      const apiBase = (import.meta as any).env?.VITE_API_URL ?? 'http://localhost:8787';
      const fd = new FormData();
      fd.append('file', file);
      fd.append('purpose', 'brand-logo');
      const token = getAuthToken();
      const res = await fetch(`${apiBase}/api/media/org-branding-upload`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json() as { key: string; url: string };
      setForm(p => ({ ...p, logoUrl: data.url }));
      toast.success('Logo Uploaded', 'Brand logo uploaded successfully.');
    } catch {
      toast.error('Upload Failed', 'Could not upload logo. Please try again.');
    } finally {
      setUploadingBrandLogo(false);
    }
  };

  const load = async () => setBrands(await repo.getBrands());
  useEffect(() => { load(); }, []);

  const slugify = (n: string) =>
    'brand-' + n.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const slugFragment = (value: string) => value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const buildBranchId = (brand: Brand, branch: Pick<Branch, 'name' | 'code'>) =>
    `branch-${slugFragment(brand.id.replace(/^brand-/, ''))}-${slugFragment(branch.code || branch.name)}`;

  const openAdd = () => { setEditingBrand(null); setForm(emptyForm); brandFormSnap.current = null; setShowForm(true); };
  const openEdit = (brand: Brand) => {
    setEditingBrand(brand);
    const snap = {
      name: brand.name,
      logoUrl: brand.logoUrl ?? '',
      enabledModules: brand.enabledModules ?? [],
    };
    setForm(snap);
    brandFormSnap.current = JSON.parse(JSON.stringify(snap));
    setShowForm(true);
  };
  const toggleBranchManager = (brand: Brand) => {
    setManagingBrandId(prev => prev === brand.id ? null : brand.id);
    setBranchDrafts(prev => ({
      ...prev,
      [brand.id]: (prev[brand.id] ?? (brand.branches ?? []).map(branch => ({ ...branch }))),
    }));
  };

  const updateBranchDraft = (brandId: string, branchId: string, field: 'name' | 'code', value: string) => {
    setBranchDrafts(prev => ({
      ...prev,
      [brandId]: (prev[brandId] ?? []).map(branch => (
        branch.id === branchId ? { ...branch, [field]: value } : branch
      )),
    }));
  };

  const addBranchDraft = (brand: Brand) => {
    setBranchDrafts(prev => ({
      ...prev,
      [brand.id]: [
        ...(prev[brand.id] ?? []),
        {
          id: `draft-${crypto.randomUUID()}`,
          name: '',
          code: '',
          brandId: brand.id,
          orgId: brand.orgId,
        },
      ],
    }));
  };

  const removeBranchDraft = (brandId: string, branchId: string) => {
    setBranchDrafts(prev => ({
      ...prev,
      [brandId]: (prev[brandId] ?? []).filter(branch => branch.id !== branchId),
    }));
  };

  const generateBranchCode = (branchName: string, existingCodes: Set<string>, seq: number): string => {
    const initials = branchName
      .replace(/[,./\\|·–—-]+/g, ' ')
      .split(/\s+/)
      .filter(w => w.length > 0)
      .map(w => w[0].toUpperCase())
      .join('');
    const prefix = initials || 'BR';
    for (let i = seq; i < seq + 100; i++) {
      const candidate = `${prefix}-${String(i).padStart(2, '0')}`;
      if (!existingCodes.has(candidate)) return candidate;
    }
    return `${prefix}-${Date.now().toString(36).slice(-4).toUpperCase()}`;
  };

  const handleSaveBranches = async (brand: Brand) => {
    const draftBranches = (branchDrafts[brand.id] ?? []).map(branch => ({
      ...branch,
      name: branch.name.trim(),
      code: branch.code.trim().toUpperCase(),
    }));

    // Only reject if name is also missing — empty code will be auto-generated
    if (draftBranches.some(b => !b.name && b.code)) {
      toast.warning('Branch name required', 'Every branch needs at least a name. Code will be auto-generated if left empty.');
      return;
    }

    // Auto-generate codes for branches that have a name but no code
    const usedCodes = new Set(draftBranches.filter(b => b.code).map(b => b.code));
    let seqCounter = 1;
    const withCodes = draftBranches.map(branch => {
      if (branch.name && !branch.code) {
        const code = generateBranchCode(branch.name, usedCodes, seqCounter++);
        usedCodes.add(code);
        return { ...branch, code };
      }
      return branch;
    });

    const sanitizedBranches = withCodes
      .filter(branch => branch.name && branch.code)
      .map(branch => ({
        ...branch,
        id: branch.id.startsWith('draft-') ? buildBranchId(brand, branch) : branch.id,
        brandId: brand.id,
        orgId: brand.orgId,
      }));

    const seenCodes = new Set<string>();
    for (const branch of sanitizedBranches) {
      if (seenCodes.has(branch.code)) {
        toast.error('Duplicate branch codes', 'Each branch under a brand must have a unique branch code.');
        return;
      }
      seenCodes.add(branch.code);
    }

    await repo.saveBrand({
      ...brand,
      branches: sanitizedBranches,
    });
    await load();
    setBranchDrafts(prev => ({ ...prev, [brand.id]: sanitizedBranches }));
    setManagingBrandId(null);
    toast.success('Branches saved', `${brand.name} now has ${sanitizedBranches.length} branch${sanitizedBranches.length === 1 ? '' : 'es'}.`);
  };

  const toggleModule = (modId: string) => {
    setForm(prev => ({
      ...prev,
      enabledModules: prev.enabledModules.includes(modId)
        ? prev.enabledModules.filter(m => m !== modId)
        : [...prev.enabledModules, modId],
    }));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    const brand: Brand = {
      id: editingBrand?.id ?? slugify(form.name),
      name: form.name.trim(),
      logoUrl: form.logoUrl.trim() || undefined,
      orgId: editingBrand?.orgId,
      branches: editingBrand?.branches ?? [],
      enabledModules: form.enabledModules.length > 0 ? form.enabledModules : undefined,
    };
    await repo.saveBrand(brand);
    setShowForm(false);
    setEditingBrand(null);
    setForm(emptyForm);
    await load();
  };

  const handleDelete = async (id: string) => {
    setDeleteConfirm({
      message: 'Remove this brand? This cannot be undone.',
      onConfirm: async () => { await repo.deleteBrand(id); await load(); },
    });
  };

  const exportBrands = () => {
    const ws = XLSX.utils.json_to_sheet(brands.map(b => ({
      ID: b.id,
      Name: b.name,
      LogoURL: b.logoUrl ?? '',
      BranchCount: b.branches?.length ?? 0,
      BranchCodes: (b.branches ?? []).map(branch => branch.code).join(', '),
      EnabledModules: (b.enabledModules ?? []).join(', '),
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Brands');
    XLSX.writeFile(wb, 'Brand_Registry.xlsx');
  };

  const importBrands = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (evt) => {
      const wb = XLSX.read(evt.target?.result, { type: 'binary' });
      const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]]) as any[];
      for (const row of rows) {
        if (row.Name) {
          const mods = row.EnabledModules ? String(row.EnabledModules).split(',').map((s: string) => s.trim()).filter(Boolean) : undefined;
          const existingBrand = brands.find(brand => brand.id === row.ID || brand.name === row.Name);
          await repo.saveBrand({
            id: row.ID || existingBrand?.id || slugify(row.Name),
            name: row.Name,
            logoUrl: row.LogoURL || undefined,
            orgId: existingBrand?.orgId,
            branches: existingBrand?.branches ?? [],
            enabledModules: mods,
          });
        }
      }
      await load();
    };
    reader.readAsBinaryString(file);
    e.target.value = '';
  };

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Brand Registry</h2>
          <p className="text-sm text-slate-500 mt-1">Manage your brand portfolio, branch hierarchy, and module visibility from one place.</p>
        </div>
        <div className="flex gap-3">
          <label className="flex items-center gap-2 px-5 py-2.5 bg-white border border-zinc-200 rounded-xl text-sm font-medium hover:bg-zinc-50 transition-colors shadow-sm cursor-pointer">
            <Upload size={18} /> Import Excel
            <input type="file" className="hidden" accept=".xlsx,.xls" onChange={importBrands} />
          </label>
          <button onClick={exportBrands} className="flex items-center gap-2 px-5 py-2.5 bg-zinc-900 text-white rounded-xl text-sm font-medium hover:bg-zinc-800 transition-colors shadow-sm">
            <Download size={18} /> Export Excel
          </button>
        </div>
      </div>

      {/* Add / Edit Form */}
      {showForm && (
        <div className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm space-y-5">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-zinc-900">{editingBrand ? 'Edit Brand' : 'Add New Brand'}</h3>
            <button onClick={() => { setShowForm(false); setEditingBrand(null); }} className="p-1.5 text-zinc-400 hover:text-zinc-900 rounded-lg"><X size={16} /></button>
          </div>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Brand Name *</label>
                <input
                  value={form.name}
                  onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. McDonald's - Dubai Mall"
                  className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:border-zinc-900"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Brand Logo</label>
                <div className="flex items-center gap-3">
                  <div className={cn(
                    "w-[42px] h-[42px] rounded-xl border-2 border-dashed flex items-center justify-center overflow-hidden shrink-0 transition-colors",
                    form.logoUrl ? "border-zinc-300 bg-white" : "border-zinc-200 bg-zinc-50"
                  )}>
                    {form.logoUrl ? (
                      <img src={resolveLogoUrl(form.logoUrl)} alt="Logo" className="w-full h-full object-contain" onError={e => (e.currentTarget.style.display = 'none')} />
                    ) : (
                      <ImageIcon size={16} className="text-zinc-300" />
                    )}
                  </div>
                  <input ref={brandLogoRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml" className="hidden"
                    onChange={e => { const f = e.target.files?.[0]; if (f) uploadBrandLogo(f); e.target.value = ''; }} />
                  <button type="button" onClick={() => brandLogoRef.current?.click()} disabled={uploadingBrandLogo}
                    className="flex items-center gap-1.5 px-3 py-2 bg-zinc-900 text-white rounded-lg text-xs font-bold hover:bg-zinc-800 transition-all disabled:opacity-50">
                    <Upload size={12} /> {uploadingBrandLogo ? 'Uploading…' : 'Upload'}
                  </button>
                  {form.logoUrl && (
                    <button type="button" onClick={() => setForm(p => ({ ...p, logoUrl: '' }))}
                      className="flex items-center gap-1 px-2 py-1.5 text-red-500 text-xs font-bold hover:text-red-700 transition-colors">
                      <Trash2 size={11} /> Remove
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Module Access Checkboxes */}
            <div className="space-y-2">
              <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Module Access</label>
              <p className="text-xs text-zinc-500">Select which modules this brand is allowed to appear in.</p>
              <div className="flex flex-wrap gap-2 mt-1">
                {INSTALLABLE_MODULES.map(mod => (
                  <button
                    type="button"
                    key={mod.id}
                    onClick={() => toggleModule(mod.id)}
                    className={cn(
                      "px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all",
                      form.enabledModules.includes(mod.id)
                        ? "bg-indigo-600 text-white border-indigo-600"
                        : "bg-zinc-50 text-zinc-500 border-zinc-200 hover:border-zinc-400"
                    )}
                  >
                    {mod.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              {!brandDirty && editingBrand && <span className="text-[10px] text-zinc-400 self-center mr-1">No changes detected</span>}
              <button
                type="submit"
                disabled={!brandDirty}
                className="flex items-center gap-2 px-5 py-2.5 bg-zinc-900 text-white rounded-xl text-sm font-semibold hover:bg-zinc-800 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                <Check size={16} /> {editingBrand ? 'Save Changes' : 'Add Brand'}
              </button>
              <button type="button" onClick={() => { setShowForm(false); setEditingBrand(null); }} className="px-5 py-2.5 bg-zinc-100 text-zinc-600 rounded-xl text-sm font-medium hover:bg-zinc-200 transition-all">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Brand List */}
      <div className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm space-y-5">
        <div className="flex items-center gap-2">
          <Tag size={18} className="text-zinc-400" />
          <h3 className="font-bold text-zinc-900">Brand Portfolio</h3>
          <span className="ml-auto text-xs text-zinc-400">{brands.length} brand{brands.length !== 1 ? 's' : ''}</span>
          {!showForm && (
            <button onClick={openAdd} className="flex items-center gap-1 px-3 py-1.5 bg-zinc-900 text-white rounded-lg text-xs font-semibold hover:bg-zinc-800 transition-all">
              <Plus size={14} /> Add Brand
            </button>
          )}
        </div>

        <div className="space-y-2">
          {brands.length === 0 && (
            <div className="py-8 text-center text-zinc-400 italic text-sm">No brands defined yet.</div>
          )}
          {brands.map(brand => {
            const initials = brand.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
            const branchPreview = brand.branches ?? [];
            const hiddenBranchCount = Math.max(branchPreview.length - 3, 0);
            const draftBranches = branchDrafts[brand.id] ?? branchPreview;
            return (
              <div key={brand.id} className="rounded-xl border border-zinc-100 bg-zinc-50 overflow-hidden">
                <div className="flex items-center gap-3 p-3 group">
                  {brand.logoUrl ? (
                    <img src={resolveLogoUrl(brand.logoUrl)} alt={brand.name} className="w-9 h-9 rounded-xl object-contain bg-white border border-zinc-100" />
                  ) : (
                    <div className="w-9 h-9 rounded-xl bg-zinc-200 flex items-center justify-center text-xs font-bold text-zinc-600 shrink-0">
                      {initials}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-zinc-900 truncate">{brand.name}</p>
                      <span className="px-2 py-0.5 bg-white border border-zinc-200 rounded-lg text-[10px] font-bold text-zinc-600">
                        {branchPreview.length} branch{branchPreview.length === 1 ? '' : 'es'}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                      <span className="text-[10px] font-mono text-zinc-400">{brand.id}</span>
                      {brand.enabledModules && brand.enabledModules.length > 0 && (
                        <span className="text-[9px] text-zinc-400">
                          · {brand.enabledModules.map(m => INSTALLABLE_MODULES.find(im => im.id === m)?.label ?? m).join(', ')}
                        </span>
                      )}
                    </div>
                    {branchPreview.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {branchPreview.slice(0, 3).map(branch => (
                          <span key={branch.id} className="px-2 py-0.5 bg-white border border-zinc-200 rounded-lg text-[10px] font-semibold text-zinc-600">
                            {branch.name} · {branch.code}
                          </span>
                        ))}
                        {hiddenBranchCount > 0 && (
                          <span className="px-2 py-0.5 bg-zinc-100 border border-zinc-200 rounded-lg text-[10px] font-semibold text-zinc-500">
                            +{hiddenBranchCount} more
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => toggleBranchManager(brand)}
                      className="px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-widest bg-white border border-zinc-200 text-zinc-600 hover:border-zinc-400 transition-all"
                    >
                      {managingBrandId === brand.id ? 'Close Branches' : 'Manage Branches'}
                    </button>
                    <button onClick={() => openEdit(brand)} className="p-1.5 text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 rounded-lg transition-all"><Edit2 size={14} /></button>
                    <button onClick={() => handleDelete(brand.id)} className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"><Trash2 size={14} /></button>
                  </div>
                </div>
                {managingBrandId === brand.id && (
                  <div className="border-t border-zinc-200 bg-white px-4 py-4 space-y-3">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-xs font-bold text-zinc-900">Branch Manager</p>
                        <p className="text-xs text-zinc-500 mt-1">Branch codes feed assignment scope and Rider Operations filters. Each code must stay unique inside this brand.</p>
                      </div>
                      <span className="px-2 py-1 rounded-lg bg-indigo-50 text-indigo-700 text-[10px] font-bold uppercase tracking-widest">
                        {draftBranches.length} draft branch{draftBranches.length === 1 ? '' : 'es'}
                      </span>
                    </div>
                    <div className="space-y-2">
                      {draftBranches.length === 0 && (
                        <div className="px-3 py-3 rounded-xl border border-dashed border-zinc-200 text-sm text-zinc-400 text-center">
                          No branches defined yet.
                        </div>
                      )}
                      {draftBranches.map(branch => (
                        <div key={branch.id} className="grid grid-cols-[1fr_160px_auto] gap-2 items-center">
                          <input
                            value={branch.name}
                            onChange={e => updateBranchDraft(brand.id, branch.id, 'name', e.target.value)}
                            placeholder="Branch name"
                            className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:border-zinc-900"
                          />
                          <input
                            value={branch.code}
                            onChange={e => updateBranchDraft(brand.id, branch.id, 'code', e.target.value.toUpperCase())}
                            placeholder={branch.name ? generateBranchCode(branch.name, new Set(draftBranches.filter(b => b.id !== branch.id && b.code).map(b => b.code.toUpperCase())), draftBranches.indexOf(branch) + 1) : 'Auto'}
                            className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:border-zinc-900 uppercase placeholder:text-zinc-300 placeholder:italic"
                          />
                          <button
                            onClick={() => removeBranchDraft(brand.id, branch.id)}
                            className="p-2 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <button
                        onClick={() => addBranchDraft(brand)}
                        className="flex items-center gap-2 px-4 py-2 bg-zinc-900 text-white rounded-xl text-sm font-semibold hover:bg-zinc-800 transition-all"
                      >
                        <Plus size={14} /> Add Branch
                      </button>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setManagingBrandId(null)}
                          className="px-4 py-2 border border-zinc-200 rounded-xl text-sm font-semibold hover:bg-zinc-50"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => handleSaveBranches(brand)}
                          className="px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-semibold hover:bg-indigo-500"
                        >
                          Save Branches
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <ConfirmDeleteDialog state={deleteConfirm} onClose={() => setDeleteConfirm(null)} />
    </div>
  );
}

// ─── Maintenance Center Page ─────────────────────────────────────────────────
function MaintenancePage({ currentOrg, canUsePlanFeature, isInternalAdmin }: {
  currentOrg: Organization | null;
  canUsePlanFeature: (f: PlanFeature) => boolean;
  isInternalAdmin?: boolean;
}) {
  const toast = useToast();
  const navigate = useNavigate();
  const planAllowed = canUsePlanFeature('maintenanceAccess');
  const allowed = planAllowed || !!isInternalAdmin;
  const API_BASE = (import.meta as any).env?.VITE_API_URL ?? 'http://localhost:8787';

  // ── State ──────────────────────────────────────────────────────────────
  const [footprint, setFootprint] = useState<{ moduleId: string; moduleName: string; tables: string[]; assetFolders: string[] }[]>([]);
  const [health, setHealth] = useState<{ lastBackup: number | null; lastFullBackup: number | null; moduleCounts: { moduleId: string; moduleName: string; rowCount: number }[]; brandCount: number; departmentCount: number; userCount: number; totalItems: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [archiving, setArchiving] = useState<string | null>(null);
  const [restoring, setRestoring] = useState<string | null>(null);
  const [wiping, setWiping] = useState<string | null>(null);
  const [fullArchiving, setFullArchiving] = useState(false);
  const [fullRestoring, setFullRestoring] = useState(false);
  const [wipeConfirm, setWipeConfirm] = useState<{ moduleId: string; moduleName: string } | null>(null);
  const [confirmPhrase, setConfirmPhrase] = useState('');
  const [operationLog, setOperationLog] = useState<string[]>([]);
  const fileRef = React.useRef<HTMLInputElement>(null);
  const fullRestoreRef = React.useRef<HTMLInputElement>(null);
  const moduleRestoreRef = React.useRef<{ ref: HTMLInputElement | null; moduleId: string }>({ ref: null, moduleId: '' });

  const isAnyOpRunning = !!archiving || !!restoring || !!wiping || fullArchiving || fullRestoring;

  const authHeaders = () => {
    const token = getAuthToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const addLog = (msg: string) => setOperationLog(prev => [...prev, `[${new Date().toLocaleTimeString()}] ${msg}`]);

  // ── Navigation Guard ───────────────────────────────────────────────────
  useEffect(() => {
    if (!isAnyOpRunning) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isAnyOpRunning]);

  // ── Load data ──────────────────────────────────────────────────────────
  const loadFootprint = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/maintenance/footprint`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Failed');
      const json = await res.json() as { data: typeof footprint };
      setFootprint(json.data);
    } catch {
      toast.error('Load Failed', 'Could not fetch module data footprint.');
    }
  };

  const loadHealth = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/maintenance/health`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Failed');
      const json = await res.json() as { data: typeof health };
      setHealth(json.data);
    } catch { /* health is optional — fail silently */ }
  };

  useEffect(() => {
    if (allowed) {
      Promise.all([loadFootprint(), loadHealth()]).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [allowed]);

  // ── Module Archive ─────────────────────────────────────────────────────
  const handleArchive = async (moduleId: string) => {
    setArchiving(moduleId);
    setOperationLog([]);
    addLog(`Starting backup for ${moduleId}…`);
    try {
      const res = await fetch(`${API_BASE}/api/maintenance/archive`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ moduleId }),
      });
      if (!res.ok) throw new Error('Archive failed');
      addLog('Archive built — downloading…');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const disposition = res.headers.get('Content-Disposition');
      a.download = disposition?.match(/filename="(.+)"/)?.[1] ?? `${moduleId}-archive.dm-archive`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      addLog('Download complete.');
      toast.success('Archive Created', `Downloaded backup for ${moduleId}.`);
    } catch {
      addLog('Archive FAILED.');
      toast.error('Archive Failed', 'Could not create the archive. Try again.');
    } finally {
      setArchiving(null);
    }
  };

  // ── Module Restore ─────────────────────────────────────────────────────
  const handleModuleRestore = async (file: File, moduleId: string) => {
    setRestoring(moduleId);
    setOperationLog([]);
    addLog(`Restoring ${moduleId} from ${file.name}…`);
    try {
      const formData = new FormData();
      formData.append('archive', file);
      const res = await fetch(`${API_BASE}/api/maintenance/restore`, {
        method: 'POST',
        headers: authHeaders(),
        body: formData,
      });
      if (!res.ok) { const err = await res.json().catch(() => null); throw new Error((err as any)?.message ?? 'Restore failed'); }
      const json = await res.json() as { data: { rowsInjected: Record<string, number>; assetsUploaded: number } };
      const totalRows = typeof json.data.rowsInjected === 'number' ? json.data.rowsInjected : Object.values(json.data.rowsInjected).reduce((s, n) => s + n, 0);
      addLog(`Restored ${totalRows} rows, ${json.data.assetsUploaded} assets.`);
      toast.success('Restore Complete', `Injected ${totalRows} rows, uploaded ${json.data.assetsUploaded} assets.`);
      await Promise.all([loadFootprint(), loadHealth()]);
    } catch (err: any) {
      addLog(`Restore FAILED: ${err.message}`);
      toast.error('Restore Failed', err.message ?? 'Could not restore from archive.');
    } finally {
      setRestoring(null);
    }
  };

  // ── Module Wipe ────────────────────────────────────────────────────────
  const handleWipe = async () => {
    if (!wipeConfirm) return;
    const expected = currentOrg?.slug ? currentOrg.slug.toUpperCase() : `WIPE ${wipeConfirm.moduleId.toUpperCase()}`;
    if (confirmPhrase !== expected) {
      toast.error('Safety Check', `Type "${expected}" to confirm.`);
      return;
    }
    setWiping(wipeConfirm.moduleId);
    setOperationLog([]);
    addLog(`Wiping ${wipeConfirm.moduleName}…`);
    try {
      const res = await fetch(`${API_BASE}/api/maintenance/wipe`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ moduleId: wipeConfirm.moduleId, confirmPhrase: `WIPE ${wipeConfirm.moduleId.toUpperCase()}` }),
      });
      if (!res.ok) throw new Error('Wipe failed');
      const json = await res.json() as { data: { rowsDeleted: number; assetsDeleted: number } };
      addLog(`Deleted ${json.data.rowsDeleted} rows, ${json.data.assetsDeleted} assets.`);
      toast.success('Wipe Complete', `Deleted ${json.data.rowsDeleted} rows, ${json.data.assetsDeleted} assets from ${wipeConfirm.moduleId}.`);
      setWipeConfirm(null);
      setConfirmPhrase('');
      await Promise.all([loadFootprint(), loadHealth()]);
    } catch {
      addLog('Wipe FAILED.');
      toast.error('Wipe Failed', 'Could not wipe module data.');
    } finally {
      setWiping(null);
    }
  };

  // ── Download Backup then Wipe ──────────────────────────────────────────
  const handleBackupThenWipe = async () => {
    if (!wipeConfirm) return;
    const expected = currentOrg?.slug ? currentOrg.slug.toUpperCase() : `WIPE ${wipeConfirm.moduleId.toUpperCase()}`;
    if (confirmPhrase !== expected) {
      toast.error('Safety Check', `Type "${expected}" to confirm.`);
      return;
    }
    setOperationLog([]);
    addLog(`Step 1: Creating backup for ${wipeConfirm.moduleId}…`);
    // First archive
    setArchiving(wipeConfirm.moduleId);
    try {
      const res = await fetch(`${API_BASE}/api/maintenance/archive`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ moduleId: wipeConfirm.moduleId }),
      });
      if (!res.ok) throw new Error('Backup failed — wipe aborted');
      addLog('Backup created — downloading…');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const disposition = res.headers.get('Content-Disposition');
      a.download = disposition?.match(/filename="(.+)"/)?.[1] ?? `${wipeConfirm.moduleId}-pre-wipe.dm-archive`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      addLog('Backup downloaded. Step 2: Wiping data…');
    } catch (err: any) {
      setArchiving(null);
      addLog('Backup FAILED — wipe aborted.');
      toast.error('Backup Failed', 'Safety backup could not be created. Wipe was NOT performed.');
      return;
    }
    setArchiving(null);

    // Then wipe
    setWiping(wipeConfirm.moduleId);
    try {
      const res = await fetch(`${API_BASE}/api/maintenance/wipe`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ moduleId: wipeConfirm.moduleId, confirmPhrase: `WIPE ${wipeConfirm.moduleId.toUpperCase()}` }),
      });
      if (!res.ok) throw new Error('Wipe failed');
      const json = await res.json() as { data: { rowsDeleted: number; assetsDeleted: number } };
      addLog(`Wipe complete: ${json.data.rowsDeleted} rows, ${json.data.assetsDeleted} assets deleted.`);
      toast.success('Backup & Wipe Complete', `Backup downloaded, then wiped ${json.data.rowsDeleted} rows.`);
      setWipeConfirm(null);
      setConfirmPhrase('');
      await Promise.all([loadFootprint(), loadHealth()]);
    } catch {
      addLog('Wipe FAILED after backup.');
      toast.error('Wipe Failed', 'Backup was downloaded, but wipe failed.');
    } finally {
      setWiping(null);
    }
  };

  // ── Full Business Archive ──────────────────────────────────────────────
  const handleFullArchive = async () => {
    setFullArchiving(true);
    setOperationLog([]);
    addLog('Starting Full Business Archive (all modules + org structure)…');
    try {
      const res = await fetch(`${API_BASE}/api/maintenance/full-archive`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error('Full archive failed');
      addLog('Archive built — downloading…');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const disposition = res.headers.get('Content-Disposition');
      a.download = disposition?.match(/filename="(.+)"/)?.[1] ?? `full-archive-${currentOrg?.slug ?? 'org'}.clickit-full`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      addLog('Full archive downloaded successfully.');
      toast.success('Full Archive Complete', 'Your complete business backup has been downloaded.');
      await loadHealth();
    } catch {
      addLog('Full archive FAILED.');
      toast.error('Full Archive Failed', 'Could not create the full business archive.');
    } finally {
      setFullArchiving(false);
    }
  };

  // ── Full Platform Restore ──────────────────────────────────────────────
  const handleFullRestore = async (file: File) => {
    setFullRestoring(true);
    setOperationLog([]);
    addLog(`Restoring full platform from ${file.name}…`);
    try {
      const formData = new FormData();
      formData.append('archive', file);
      const res = await fetch(`${API_BASE}/api/maintenance/full-restore`, {
        method: 'POST',
        headers: authHeaders(),
        body: formData,
      });
      if (!res.ok) { const err = await res.json().catch(() => null); throw new Error((err as any)?.message ?? 'Full restore failed'); }
      const json = await res.json() as { data: { modulesRestored: string[]; totalRows: number; totalAssets: number; structureRestored: string[] } };
      addLog(`Restored ${json.data.modulesRestored.length} modules, ${json.data.totalRows} rows, ${json.data.totalAssets} assets, ${json.data.structureRestored.length} structure tables.`);
      toast.success('Full Restore Complete', `Restored ${json.data.modulesRestored.length} modules and ${json.data.totalRows} data rows.`);
      await Promise.all([loadFootprint(), loadHealth()]);
    } catch (err: any) {
      addLog(`Full restore FAILED: ${err.message}`);
      toast.error('Full Restore Failed', err.message ?? 'Could not restore from full archive.');
    } finally {
      setFullRestoring(false);
    }
  };

  // ── Helpers ────────────────────────────────────────────────────────────
  const fmtDate = (ts: number | null) => ts ? new Date(ts).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Never';
  const getModCount = (moduleId: string) => health?.moduleCounts.find(m => m.moduleId === moduleId)?.rowCount ?? 0;

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div className="max-w-5xl mx-auto py-10 px-4">
      {/* ── Upgrade Teaser (Blurred Overlay) ── */}
      {!planAllowed && !isInternalAdmin ? (
        <div className="relative">
          {/* Blurred preview behind */}
          <div className="pointer-events-none select-none filter blur-[6px] opacity-40">
            <div className="flex items-center gap-3 mb-6">
              <HardDrive size={24} className="text-emerald-600" />
              <h1 className="text-2xl font-bold text-zinc-900">Data Maintenance & Recovery</h1>
            </div>
            <div className="grid grid-cols-4 gap-4 mb-6">
              {[1, 2, 3, 4].map(i => <div key={i} className="bg-white border border-zinc-200 rounded-2xl p-5 h-24" />)}
            </div>
            <div className="space-y-4">
              {[1, 2, 3].map(i => <div key={i} className="bg-white border border-zinc-200 rounded-2xl p-6 h-32" />)}
            </div>
          </div>
          {/* Overlay card */}
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <div className="bg-white/95 backdrop-blur-md border border-amber-200 rounded-2xl shadow-2xl p-8 max-w-md text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Lock size={28} className="text-amber-600" />
              </div>
              <h2 className="text-xl font-bold text-zinc-900 mb-2">Backups are a Pro Feature</h2>
              <p className="text-sm text-zinc-600 leading-relaxed mb-4">
                Secure your business data with automated backups, per-module archives, and full platform recovery. Available on <strong>Pro</strong> plan or higher.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4 mb-6">
                <div className="flex items-center gap-1.5 text-xs text-zinc-500"><Download size={12} /> Module Backups</div>
                <div className="flex items-center gap-1.5 text-xs text-zinc-500"><Layers size={12} /> Full Business Archive</div>
                <div className="flex items-center gap-1.5 text-xs text-zinc-500"><Shield size={12} /> Audit-Logged Ops</div>
                <div className="flex items-center gap-1.5 text-xs text-zinc-500"><RotateCcw size={12} /> One-Click Restore</div>
              </div>
              <button onClick={() => navigate('/system/marketplace')}
                className="inline-flex items-center gap-2 px-6 py-3 bg-amber-600 text-white rounded-xl text-sm font-bold hover:bg-amber-700 transition-colors shadow-lg shadow-amber-200">
                <Zap size={14} /> View Pro Plans
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* ── God Mode Banner ── */}
          {!planAllowed && isInternalAdmin && (
            <div className="flex items-center gap-2 px-4 py-2 bg-indigo-50 border border-indigo-200 rounded-xl text-xs text-indigo-700 font-semibold mb-4">
              <ShieldCheck size={14} /> God Mode — maintenance access bypassed for platform admin
            </div>
          )}

          {/* ── Header ── */}
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                <HardDrive size={20} className="text-emerald-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-zinc-900">Data Maintenance & Recovery</h1>
                <p className="text-xs text-zinc-400 mt-0.5">
                  {currentOrg?.name ?? 'Organization'} &middot; {currentOrg?.slug ?? '—'}
                </p>
              </div>
            </div>
            <button onClick={() => { setLoading(true); Promise.all([loadFootprint(), loadHealth()]).finally(() => setLoading(false)); }}
              disabled={isAnyOpRunning}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-zinc-600 bg-zinc-100 hover:bg-zinc-200 rounded-lg transition-colors disabled:opacity-50">
              <RotateCcw size={12} /> Refresh
            </button>
          </div>

          {/* ── Health KPI Strip ── */}
          {health && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <div className="bg-white border border-zinc-200 rounded-xl p-4">
                <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest mb-1">Last Module Backup</p>
                <p className="text-sm font-bold text-zinc-900">{fmtDate(health.lastBackup)}</p>
              </div>
              <div className="bg-white border border-zinc-200 rounded-xl p-4">
                <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest mb-1">Last Full Backup</p>
                <p className="text-sm font-bold text-zinc-900">{fmtDate(health.lastFullBackup)}</p>
              </div>
              <div className="bg-white border border-zinc-200 rounded-xl p-4">
                <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest mb-1">Total Data Rows</p>
                <p className="text-sm font-bold text-zinc-900">{health.totalItems.toLocaleString()}</p>
              </div>
              <div className="bg-white border border-zinc-200 rounded-xl p-4">
                <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest mb-1">Org Structure</p>
                <p className="text-sm font-bold text-zinc-900">{health.brandCount}B &middot; {health.departmentCount}D &middot; {health.userCount}U</p>
              </div>
            </div>
          )}

          {/* ── Global Actions ── */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl p-5">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-sm font-bold text-emerald-900 flex items-center gap-2"><Layers size={14} /> Full Business Archive</h3>
                  <p className="text-xs text-emerald-700 mt-1">Download a complete backup of ALL modules, brands, departments, and users in a single file.</p>
                </div>
              </div>
              <button onClick={handleFullArchive} disabled={isAnyOpRunning}
                className="mt-3 flex items-center gap-2 px-4 py-2 bg-emerald-700 text-white text-xs font-bold rounded-lg hover:bg-emerald-800 disabled:opacity-50 transition-colors">
                {fullArchiving ? (
                  <><span className="inline-block w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Archiving…</>
                ) : (
                  <><Download size={12} /> Download Full Archive</>
                )}
              </button>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-5">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-sm font-bold text-blue-900 flex items-center gap-2"><Upload size={14} /> Full Platform Restore</h3>
                  <p className="text-xs text-blue-700 mt-1">Upload a <code className="bg-blue-100 px-1 rounded">.clickit-full</code> archive to restore all data.</p>
                </div>
              </div>
              <input ref={fullRestoreRef} type="file" accept=".clickit-full,.zip" className="hidden"
                onChange={e => { const f = e.target.files?.[0]; if (f) handleFullRestore(f); e.target.value = ''; }} />
              <button onClick={() => fullRestoreRef.current?.click()} disabled={isAnyOpRunning}
                className="mt-3 flex items-center gap-2 px-4 py-2 bg-blue-700 text-white text-xs font-bold rounded-lg hover:bg-blue-800 disabled:opacity-50 transition-colors">
                {fullRestoring ? (
                  <><span className="inline-block w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Restoring…</>
                ) : (
                  <><Upload size={12} /> Upload Full Archive</>
                )}
              </button>
            </div>
          </div>

          {/* ── Operation Progress / Log ── */}
          {isAnyOpRunning && (
            <div className="mb-6 bg-zinc-900 rounded-xl p-4 border border-zinc-700">
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-block w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                <p className="text-xs font-bold text-emerald-400 uppercase tracking-widest">Operation In Progress</p>
              </div>
              {/* Pulsing progress bar */}
              <div className="w-full h-1 bg-zinc-700 rounded-full overflow-hidden mb-3">
                <div className="h-full bg-gradient-to-r from-emerald-500 via-teal-400 to-emerald-500 rounded-full animate-pulse" style={{ width: '100%' }} />
              </div>
              {/* Live log feed */}
              <div className="bg-zinc-800 rounded-lg p-3 max-h-32 overflow-y-auto font-mono text-[11px] text-zinc-300 space-y-0.5">
                {operationLog.length === 0 ? (
                  <p className="text-zinc-500 italic">Waiting for output…</p>
                ) : (
                  operationLog.map((line, i) => <p key={i}>{line}</p>)
                )}
              </div>
            </div>
          )}

          {/* ── Module Action Grid ── */}
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-xs font-bold text-zinc-400 uppercase tracking-widest">Module Data</h2>
            <span className="text-[10px] text-zinc-400">{footprint.length} modules with data footprint</span>
          </div>

          {loading ? (
            <div className="p-20 text-center text-zinc-400 italic">Loading module footprint…</div>
          ) : footprint.length === 0 ? (
            <div className="p-12 text-center text-zinc-400">
              <HardDrive size={32} className="mx-auto mb-3 opacity-40" />
              <p className="text-sm">No modules have declared data footprints yet.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {footprint.map(mod => {
                const rowCount = getModCount(mod.moduleId);
                const isModBusy = archiving === mod.moduleId || restoring === mod.moduleId || wiping === mod.moduleId;
                return (
                  <div key={mod.moduleId} className={`bg-white border rounded-2xl p-5 transition-all ${isModBusy ? 'border-emerald-300 ring-1 ring-emerald-200' : 'border-zinc-200 hover:shadow-sm'}`}>
                    {/* Pulsing bar when this module is active */}
                    {isModBusy && (
                      <div className="w-full h-0.5 bg-zinc-100 rounded-full overflow-hidden mb-3">
                        <div className="h-full bg-gradient-to-r from-emerald-500 via-teal-400 to-emerald-500 rounded-full animate-pulse" style={{ width: '100%' }} />
                      </div>
                    )}
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <Package size={14} className="text-zinc-400" />
                          <h3 className="text-sm font-bold text-zinc-900">{mod.moduleName}</h3>
                          <span className="px-1.5 py-0.5 bg-zinc-100 text-zinc-500 text-[10px] font-mono rounded">{mod.moduleId}</span>
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-zinc-400">
                          <span>{rowCount.toLocaleString()} rows</span>
                          <span>{mod.tables.length} tables</span>
                          {mod.assetFolders.length > 0 && <span>{mod.assetFolders.length} asset folders</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Export */}
                        <button onClick={() => handleArchive(mod.moduleId)} disabled={isAnyOpRunning}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-900 text-white text-xs font-bold rounded-lg hover:bg-zinc-800 disabled:opacity-50 transition-colors">
                          <Download size={11} /> {archiving === mod.moduleId ? 'Exporting…' : 'Export'}
                        </button>
                        {/* Restore */}
                        <input type="file" accept=".dm-archive,.zip" className="hidden"
                          ref={el => { if (moduleRestoreRef.current.moduleId === mod.moduleId) moduleRestoreRef.current.ref = el; }}
                          onChange={e => { const f = e.target.files?.[0]; if (f) handleModuleRestore(f, mod.moduleId); e.target.value = ''; }} />
                        <button onClick={() => { moduleRestoreRef.current.moduleId = mod.moduleId; setTimeout(() => moduleRestoreRef.current.ref?.click(), 50); }}
                          disabled={isAnyOpRunning}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 text-xs font-bold rounded-lg border border-blue-200 hover:bg-blue-100 disabled:opacity-50 transition-colors">
                          <Upload size={11} /> {restoring === mod.moduleId ? 'Restoring…' : 'Restore'}
                        </button>
                        {/* Wipe */}
                        <button onClick={() => { setWipeConfirm({ moduleId: mod.moduleId, moduleName: mod.moduleName }); setConfirmPhrase(''); }}
                          disabled={isAnyOpRunning}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-600 text-xs font-bold rounded-lg border border-red-200 hover:bg-red-100 disabled:opacity-50 transition-colors">
                          <Trash2 size={11} /> Wipe
                        </button>
                      </div>
                    </div>
                    {/* Table & Asset Tags */}
                    <div className="flex flex-wrap gap-1 mt-2">
                      {mod.tables.map(t => <span key={t} className="bg-zinc-50 text-zinc-500 px-1.5 py-0.5 rounded text-[10px] font-mono border border-zinc-100">{t}</span>)}
                      {mod.assetFolders.map(f => <span key={f} className="bg-amber-50 text-amber-600 px-1.5 py-0.5 rounded text-[10px] font-mono border border-amber-100">{f}</span>)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ── Enhanced Wipe Confirmation Modal ── */}
      {wipeConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="flex items-center gap-3 p-6 border-b border-zinc-100">
              <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                <AlertTriangle size={20} className="text-red-600" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-bold text-zinc-900">Nuclear Wipe</h2>
                <p className="text-sm text-zinc-500">{wipeConfirm.moduleName} &middot; {wipeConfirm.moduleId}</p>
              </div>
              <button onClick={() => { setWipeConfirm(null); setConfirmPhrase(''); }} className="text-zinc-400 hover:text-zinc-600">
                <X size={18} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="p-3 bg-red-50 border border-red-200 rounded-xl">
                <p className="text-sm text-red-800 leading-relaxed">
                  This will <strong>permanently delete all data</strong> for <strong>{wipeConfirm.moduleName}</strong> including database rows and R2 assets. This action <strong>cannot be undone</strong>.
                </p>
              </div>
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
                <p className="text-xs text-amber-800">
                  <strong>Recommendation:</strong> Use "Download Backup & Wipe" to create a safety backup before deletion.
                </p>
              </div>
              <div>
                <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-2">
                  Type <code className="bg-red-50 text-red-600 px-1.5 py-0.5 rounded">{currentOrg?.slug ? currentOrg.slug.toUpperCase() : `WIPE ${wipeConfirm.moduleId.toUpperCase()}`}</code> to confirm
                </label>
                <input type="text" value={confirmPhrase} onChange={e => setConfirmPhrase(e.target.value)} placeholder="Type confirmation phrase…"
                  className="w-full px-4 py-3 text-sm border border-zinc-200 rounded-xl focus:ring-2 focus:ring-red-500/20 focus:border-red-400 outline-none font-mono" />
              </div>
            </div>
            <div className="flex items-center justify-between gap-3 p-6 border-t border-zinc-100">
              <button onClick={() => { setWipeConfirm(null); setConfirmPhrase(''); }}
                className="px-4 py-2 text-sm font-semibold text-zinc-600 hover:text-zinc-800 transition-colors">
                Cancel
              </button>
              <div className="flex items-center gap-2">
                <button onClick={handleBackupThenWipe}
                  disabled={isAnyOpRunning || confirmPhrase !== (currentOrg?.slug ? currentOrg.slug.toUpperCase() : `WIPE ${wipeConfirm.moduleId.toUpperCase()}`)}
                  className="flex items-center gap-1.5 px-4 py-2.5 bg-amber-600 text-white text-sm font-semibold rounded-xl hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                  <Download size={13} /> Backup & Wipe
                </button>
                <button onClick={handleWipe}
                  disabled={isAnyOpRunning || confirmPhrase !== (currentOrg?.slug ? currentOrg.slug.toUpperCase() : `WIPE ${wipeConfirm.moduleId.toUpperCase()}`)}
                  className="flex items-center gap-1.5 px-4 py-2.5 bg-red-600 text-white text-sm font-semibold rounded-xl hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                  <Trash2 size={13} /> Permanently Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── White-Label Settings Page ───────────────────────────────────────────────
function WhiteLabelSettingsPage({ currentOrg, canUsePlanFeature, repository, onBrandingSaved }: {
  currentOrg: Organization | null;
  canUsePlanFeature: (f: PlanFeature) => boolean;
  repository: IModuleRepository;
  onBrandingSaved?: () => void;
}) {
  const toast = useToast();
  const allowed = canUsePlanFeature('whiteLabelAccess');
  const wl = currentOrg?.whiteLabelSettings ?? {};
  const [logoUrl, setLogoUrl] = useState(wl.logoUrl ?? '');
  const [faviconUrl, setFaviconUrl] = useState(wl.faviconUrl ?? '');
  const [primaryColor, setPrimaryColor] = useState(wl.primaryColor ?? '#10b981');
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const logoRef = React.useRef<HTMLInputElement>(null);
  const faviconRef = React.useRef<HTMLInputElement>(null);
  const wlSnap = React.useRef({ logoUrl: wl.logoUrl ?? '', faviconUrl: wl.faviconUrl ?? '', primaryColor: wl.primaryColor ?? '#10b981' });
  const wlDirty = React.useMemo(() => hasDataChanged(wlSnap.current, { logoUrl, faviconUrl, primaryColor }), [logoUrl, faviconUrl, primaryColor]);

  const API_BASE = (import.meta as any).env?.VITE_API_URL ?? 'http://localhost:8787';
  const resolveUrl = (url?: string) => url ? (url.startsWith('http') ? url : `${API_BASE}${url}`) : undefined;

  useEffect(() => {
    const s = currentOrg?.whiteLabelSettings ?? {};
    setLogoUrl(s.logoUrl ?? '');
    setFaviconUrl(s.faviconUrl ?? '');
    setPrimaryColor(s.primaryColor ?? '#10b981');
    wlSnap.current = { logoUrl: s.logoUrl ?? '', faviconUrl: s.faviconUrl ?? '', primaryColor: s.primaryColor ?? '#10b981' };
  }, [currentOrg?.id]);

  const uploadBranding = async (file: File, purpose: 'logo' | 'favicon', setUrl: (u: string) => void, setUploading: (b: boolean) => void) => {
    if (!isRemoteMode()) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('purpose', purpose);
      const token = getAuthToken();
      const res = await fetch(`${API_BASE}/api/media/org-branding-upload`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json() as { key: string; url: string };
      setUrl(data.url); // /api/public-media/{key}
      toast.success('Image Uploaded', `${purpose === 'logo' ? 'Logo' : 'Favicon'} uploaded successfully.`);
    } catch {
      toast.error('Upload Failed', 'Could not upload the image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!currentOrg) return;
    setSaving(true);
    const settings: WhiteLabelSettings = {
      logoUrl: logoUrl.trim() || undefined,
      faviconUrl: faviconUrl.trim() || undefined,
      primaryColor: primaryColor !== '#10b981' ? primaryColor : undefined,
    };
    await repository.saveOrganization({ ...currentOrg, whiteLabelSettings: settings });
    toast.success('Branding Saved', 'White-label settings have been applied.');
    setSaving(false);
    // Refresh auth context so sidebar/favicon update immediately
    onBrandingSaved?.();
  };

  const handleReset = async () => {
    if (!currentOrg) return;
    setSaving(true);
    await repository.saveOrganization({ ...currentOrg, whiteLabelSettings: undefined });
    setLogoUrl('');
    setFaviconUrl('');
    setPrimaryColor('#10b981');
    toast.info('Branding Reset', 'Restored platform default branding.');
    setSaving(false);
    onBrandingSaved?.();
  };

  const logoPreview = resolveUrl(logoUrl);
  const faviconPreview = resolveUrl(faviconUrl);

  return (
    <div className="max-w-2xl mx-auto py-10 px-4">
      <div className="flex items-center gap-3 mb-6">
        <Palette size={24} className="text-emerald-600" />
        <h1 className="text-2xl font-bold text-zinc-900">White-Label Branding</h1>
      </div>

      {!allowed ? (
        <div className="p-6 bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-2xl">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center shrink-0">
              <Lock size={24} className="text-amber-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-amber-900">Upgrade to Enterprise</h2>
              <p className="text-sm text-amber-700 mt-1 leading-relaxed">
                Custom branding — including logo upload, favicon, and colour overrides — is available on the <strong>Enterprise</strong> plan.
                Contact your platform administrator to upgrade.
              </p>
              <div className="mt-4 flex items-center gap-3">
                <div className="flex items-center gap-2 text-xs text-amber-600">
                  <Upload size={14} /> Custom logo upload
                </div>
                <div className="flex items-center gap-2 text-xs text-amber-600">
                  <ImageIcon size={14} /> Favicon override
                </div>
                <div className="flex items-center gap-2 text-xs text-amber-600">
                  <Palette size={14} /> Colour theming
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Logo Upload */}
          <div>
            <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-1">Organisation Logo</label>
            <p className="text-[11px] text-zinc-400 mb-3">Replaces the sidebar logo. Recommended: 200×200 PNG or SVG.</p>
            <div className="flex items-start gap-4">
              <div className={cn(
                "w-24 h-24 rounded-xl border-2 border-dashed flex items-center justify-center overflow-hidden shrink-0 transition-colors",
                logoUrl ? "border-zinc-300 bg-white" : "border-zinc-200 bg-zinc-50"
              )}>
                {logoPreview ? (
                  <img src={logoPreview} alt="Logo" className="w-full h-full object-contain" onError={e => (e.currentTarget.style.display = 'none')} />
                ) : (
                  <ImageIcon size={24} className="text-zinc-300" />
                )}
              </div>
              <div className="flex flex-col gap-2">
                <input ref={logoRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml" className="hidden"
                  onChange={e => { const f = e.target.files?.[0]; if (f) uploadBranding(f, 'logo', setLogoUrl, setUploadingLogo); e.target.value = ''; }} />
                <button type="button" onClick={() => logoRef.current?.click()} disabled={uploadingLogo}
                  className="flex items-center gap-2 px-3 py-2 bg-zinc-900 text-white rounded-lg text-xs font-bold hover:bg-zinc-800 transition-all disabled:opacity-50">
                  <Upload size={12} /> {uploadingLogo ? 'Uploading…' : 'Upload Logo'}
                </button>
                {logoUrl && (
                  <button type="button" onClick={() => setLogoUrl('')}
                    className="flex items-center gap-1 px-3 py-1.5 text-red-500 text-xs font-bold hover:text-red-700 transition-colors">
                    <Trash2 size={11} /> Remove
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Favicon Upload */}
          <div>
            <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-1">Favicon</label>
            <p className="text-[11px] text-zinc-400 mb-3">Replaces the browser tab icon. Recommended: 32×32 ICO or PNG.</p>
            <div className="flex items-start gap-4">
              <div className={cn(
                "w-24 h-24 rounded-xl border-2 border-dashed flex items-center justify-center overflow-hidden shrink-0 transition-colors",
                faviconUrl ? "border-zinc-300 bg-white" : "border-zinc-200 bg-zinc-50"
              )}>
                {faviconPreview ? (
                  <img src={faviconPreview} alt="Favicon" className="w-full h-full object-contain" onError={e => (e.currentTarget.style.display = 'none')} />
                ) : (
                  <ImageIcon size={24} className="text-zinc-300" />
                )}
              </div>
              <div className="flex flex-col gap-2">
                <input ref={faviconRef} type="file" accept="image/png,image/jpeg,image/x-icon,image/svg+xml" className="hidden"
                  onChange={e => { const f = e.target.files?.[0]; if (f) uploadBranding(f, 'favicon', setFaviconUrl, setUploadingFavicon); e.target.value = ''; }} />
                <button type="button" onClick={() => faviconRef.current?.click()} disabled={uploadingFavicon}
                  className="flex items-center gap-2 px-3 py-2 bg-zinc-900 text-white rounded-lg text-xs font-bold hover:bg-zinc-800 transition-all disabled:opacity-50">
                  <Upload size={12} /> {uploadingFavicon ? 'Uploading…' : 'Upload Favicon'}
                </button>
                {faviconUrl && (
                  <button type="button" onClick={() => setFaviconUrl('')}
                    className="flex items-center gap-1 px-3 py-1.5 text-red-500 text-xs font-bold hover:text-red-700 transition-colors">
                    <Trash2 size={11} /> Remove
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Primary Color */}
          <div>
            <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-1">Primary Colour</label>
            <p className="text-[11px] text-zinc-400 mb-3">Overrides the sidebar accent and key UI elements.</p>
            <div className="flex items-center gap-3">
              <input type="color" value={primaryColor} onChange={e => setPrimaryColor(e.target.value)}
                className="w-10 h-10 rounded-lg border border-zinc-300 cursor-pointer p-0.5" />
              <input type="text" value={primaryColor} onChange={e => setPrimaryColor(e.target.value)}
                className="w-32 px-3 py-2 border border-zinc-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
              <div className="w-24 h-10 rounded-lg border border-zinc-200" style={{ backgroundColor: primaryColor }} />
            </div>
          </div>

          {/* Live Preview */}
          <div className="p-4 bg-zinc-50 rounded-xl border border-zinc-200">
            <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-3">Live Preview</p>
            <div className="flex items-center gap-3 p-3 rounded-lg" style={{ backgroundColor: primaryColor + '18' }}>
              {logoPreview
                ? <img src={logoPreview} alt="Logo" className="w-10 h-10 rounded-lg object-contain bg-white p-1" onError={e => (e.currentTarget.style.display = 'none')} />
                : <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: primaryColor }}><Building2 size={20} className="text-white" /></div>
              }
              <div>
                <p className="text-sm font-bold" style={{ color: primaryColor }}>{currentOrg?.name ?? 'Your Organisation'}</p>
                <p className="text-xs text-zinc-500">Sidebar header preview</p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 pt-2">
            {!wlDirty && !saving && <span className="text-[10px] text-zinc-400 mr-1">No changes detected</span>}
            <button onClick={handleSave} disabled={saving || !wlDirty}
              className="flex items-center gap-2 px-5 py-2.5 bg-zinc-900 text-white rounded-lg text-sm font-semibold hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed">
              <Check size={16} /> {saving ? 'Saving…' : 'Save Branding'}
            </button>
            <button onClick={handleReset} disabled={saving}
              className="flex items-center gap-2 px-4 py-2.5 bg-zinc-100 text-zinc-700 border border-zinc-300 rounded-lg text-sm font-semibold hover:bg-zinc-200 disabled:opacity-50">
              <RotateCcw size={16} /> Restore Defaults
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Department Registry Page (Global Business Hub) ──────────────────────────
function DepartmentRegistryPage() {
  const repo = getRepository();
  const toast = useToast();
  const [departments, setDepartments] = useState<Department[]>([]);
  const [deleteConfirm, setDeleteConfirm] = useState<DeleteConfirmState | null>(null);
  const [newName, setNewName] = useState('');
  const [newLinkedModules, setNewLinkedModules] = useState<string[]>([]);
  const [editDept, setEditDept] = useState<Department | null>(null);
  const [editName, setEditName] = useState('');
  const [editLinkedModules, setEditLinkedModules] = useState<string[]>([]);
  const deptSnap = React.useRef<{ name: string; linkedModules: string[] } | null>(null);
  const deptDirty = React.useMemo(() => editDept ? hasDataChanged(deptSnap.current, { name: editName, linkedModules: editLinkedModules }) : false, [editDept, editName, editLinkedModules]);

  const INSTALLABLE_MODULES = PLATFORM_MODULES.filter(m => m.id !== MODULE_IDS.SYSTEM);

  const load = async () => setDepartments(await repo.getDepartments());
  useEffect(() => { load(); }, []);

  const toggleNewModule = (modId: string) => setNewLinkedModules(prev => prev.includes(modId) ? prev.filter(m => m !== modId) : [...prev, modId]);
  const toggleEditModule = (modId: string) => setEditLinkedModules(prev => prev.includes(modId) ? prev.filter(m => m !== modId) : [...prev, modId]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) return;
    const nextName = newName.trim().toUpperCase();
    if (departments.some(d => d.name.trim().toUpperCase() === nextName)) {
      toast.error('Duplicate Department', 'A department with this name already exists.');
      return;
    }
    await repo.saveDepartment({ id: `dept-${crypto.randomUUID()}`, name: nextName, linkedModules: [...newLinkedModules] });
    setNewName('');
    setNewLinkedModules([]);
    await load();
    toast.success('Department created.');
  };

  const handleEditSave = async () => {
    if (!editDept || !editName.trim()) return;
    const nextName = editName.trim().toUpperCase();
    if (departments.some(d => d.id !== editDept.id && d.name.trim().toUpperCase() === nextName)) {
      toast.error('Duplicate Department', 'A department with this name already exists.');
      return;
    }
    await repo.updateDepartment({ ...editDept, name: nextName, linkedModules: [...editLinkedModules] });
    setEditDept(null);
    setEditName('');
    setEditLinkedModules([]);
    await load();
    toast.success('Department updated.');
  };

  const handleDelete = (id: string) => {
    setDeleteConfirm({
      message: 'Remove this department? This cannot be undone.',
      onConfirm: async () => { await repo.deleteDepartment(id); await load(); toast.success('Department deleted.'); },
    });
  };

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Department Registry</h2>
          <p className="text-sm text-slate-500 mt-1">Manage the master list of company departments and map them to application modules.</p>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm space-y-5">
        <div className="flex items-center gap-2">
          <Layers size={18} className="text-zinc-400" />
          <h3 className="font-bold text-zinc-900">Departments</h3>
          <span className="ml-auto text-xs text-zinc-400">{departments.length} department{departments.length !== 1 ? 's' : ''}</span>
        </div>

        {/* Add new department form */}
        <form onSubmit={handleAdd} className="space-y-3 bg-zinc-50 border border-zinc-200 rounded-xl p-4">
          <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">New Department</p>
          <div className="grid grid-cols-[1fr_auto] gap-2">
            <input
              value={newName}
              onChange={e => setNewName(e.target.value)}
              placeholder="Department name (e.g. F&B, SALES, FINANCE)"
              className="px-3 py-2 bg-white border border-zinc-200 rounded-xl text-sm focus:outline-none focus:border-zinc-900"
            />
            <button
              type="submit"
              disabled={!newName.trim()}
              className="flex items-center gap-2 px-4 py-2 bg-zinc-900 text-white rounded-xl text-sm font-semibold hover:bg-zinc-800 disabled:opacity-40 transition-all"
            >
              <Plus size={16} /> Add
            </button>
          </div>
          <div className="space-y-1.5">
            <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Link to Modules</p>
            <div className="flex flex-wrap gap-2">
              {INSTALLABLE_MODULES.map(mod => {
                const checked = newLinkedModules.includes(mod.id);
                return (
                  <button
                    key={mod.id}
                    type="button"
                    onClick={() => toggleNewModule(mod.id)}
                    className={cn(
                      "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all",
                      checked ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-zinc-500 border-zinc-200 hover:border-zinc-400"
                    )}
                  >
                    {checked && <Check size={12} />}
                    {mod.name}
                  </button>
                );
              })}
            </div>
          </div>
        </form>

        {/* Department list */}
        <div className="space-y-2">
          {departments.length === 0 && (
            <div className="py-8 text-center text-zinc-400 italic text-sm">No departments defined yet.</div>
          )}
          {departments.map(dept => (
            <div key={dept.id} className="flex items-center gap-3 p-3 bg-zinc-50 rounded-xl border border-zinc-100 group">
              {editDept?.id === dept.id ? (
                <div className="flex-1 space-y-2">
                  <input value={editName} onChange={e => setEditName(e.target.value)} className="w-full px-3 py-1.5 bg-white border border-zinc-200 rounded-lg text-sm" />
                  <div className="flex flex-wrap gap-2">
                    {INSTALLABLE_MODULES.map(mod => {
                      const checked = editLinkedModules.includes(mod.id);
                      return (
                        <button key={mod.id} type="button" onClick={() => toggleEditModule(mod.id)}
                          className={cn("flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all",
                            checked ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-zinc-500 border-zinc-200 hover:border-zinc-400"
                          )}>
                          {checked && <Check size={12} />}
                          {mod.name}
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex gap-2">
                    {!deptDirty && <span className="text-[10px] text-zinc-400 self-center mr-1">No changes</span>}
                    <button onClick={handleEditSave} disabled={!deptDirty} className="flex items-center gap-1 px-3 py-1.5 bg-zinc-900 text-white rounded-lg text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed"><Check size={12} /> Save</button>
                    <button onClick={() => setEditDept(null)} className="px-3 py-1.5 bg-zinc-100 text-zinc-500 rounded-lg text-xs font-medium">Cancel</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="w-9 h-9 bg-indigo-100 text-indigo-700 rounded-lg flex items-center justify-center text-xs font-bold">{dept.name.slice(0, 2)}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-zinc-900">{dept.name}</p>
                    {dept.linkedModules && dept.linkedModules.length > 0 && (
                      <div className="flex gap-1 mt-1 flex-wrap">
                        {dept.linkedModules.map(mid => {
                          const modDef = PLATFORM_MODULES.find(m => m.id === mid);
                          return <span key={mid} className="px-2 py-0.5 bg-indigo-50 text-indigo-600 rounded text-[10px] font-medium">{modDef?.name ?? mid}</span>;
                        })}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => { setEditDept(dept); setEditName(dept.name); setEditLinkedModules(dept.linkedModules ?? []); deptSnap.current = { name: dept.name, linkedModules: [...(dept.linkedModules ?? [])] }; }}
                    className="p-1.5 text-zinc-400 hover:text-zinc-900 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg"
                  ><Edit2 size={14} /></button>
                  <button
                    onClick={() => handleDelete(dept.id)}
                    className="p-1.5 text-zinc-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg"
                  ><Trash2 size={14} /></button>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
      <ConfirmDeleteDialog state={deleteConfirm} onClose={() => setDeleteConfirm(null)} />
    </div>
  );
}

function UserStatusBadge({ status }: { status?: UserStatus }) {
  const effectiveStatus = status ?? UserStatus.ACTIVE;
  const map: Record<UserStatus, { label: string; cls: string; Icon: React.FC<{ size?: number }> }> = {
    [UserStatus.ACTIVE]: { label: 'Active', cls: 'bg-emerald-50 text-emerald-700 border border-emerald-100', Icon: CheckCircle2 },
    [UserStatus.PENDING]: { label: 'Pending', cls: 'bg-amber-50 text-amber-700 border border-amber-100', Icon: Clock },
    [UserStatus.REJECTED]: { label: 'Rejected', cls: 'bg-rose-50 text-rose-700 border border-rose-100', Icon: XCircle },
  };
  const { label, cls, Icon } = map[effectiveStatus];
  return (
    <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest', cls)}>
      <Icon size={10} /> {label}
    </span>
  );
}

function UserAccountsPage({ settings, currentOrg, orgPlan, isSuperAdmin, isInternalAdmin, isPlatformOwner, currentUser }: { settings: any, currentOrg: Organization | null, orgPlan: OrgPlan | null, isSuperAdmin: boolean, isInternalAdmin?: boolean, isPlatformOwner?: boolean, currentUser: User | null }) {
  const repo = getRepository();
  const toast = useToast();
  const [users, setUsers] = useState<User[]>([]);
  const [brands, setPageBrands] = useState<Brand[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [showUserLimit, setShowUserLimit] = useState(false);
  const [savingUserId, setSavingUserId] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<DeleteConfirmState | null>(null);
  const [manualApprovalOverride, setManualApprovalOverride] = useState(!!currentOrg?.manualUserApproval);

  const DEMO_IDS = new Set(['sa1', 'sa2', 'sa3', 'u1', 'u2', 'u4']);
  const manualApprovalEnabled = manualApprovalOverride && !isInternalAdmin;
  const pendingUsers = users.filter(u => !isPlatformRole(u.role) && (u.status ?? UserStatus.ACTIVE) === UserStatus.PENDING);
  const assignableModuleIds = PLATFORM_MODULES
    .filter(mod => mod.id === MODULE_IDS.SYSTEM || (currentOrg?.enabledApps ?? []).includes(mod.id))
    .map(mod => mod.id);

  useEffect(() => {
    setManualApprovalOverride(!!currentOrg?.manualUserApproval);
  }, [currentOrg?.manualUserApproval]);

  const load = async () => {
    const [u, b, d] = await Promise.all([repo.getUsers(), repo.getBrands(), repo.getDepartments()]);
    setUsers(u);
    setPageBrands(b);
    setDepartments(d);
  };
  useEffect(() => { load(); }, []);

  const handleToggleManualApproval = async () => {
    if (!currentOrg) return;
    const nextValue = !manualApprovalOverride;
    await repo.saveOrganization({ ...currentOrg, manualUserApproval: nextValue });
    setManualApprovalOverride(nextValue);
    toast.success(
      'Approval Rule Updated',
      nextValue
        ? 'New tenant users will now require admin approval.'
        : 'New tenant users will now be activated immediately.',
    );
  };

  const handleStatusChange = async (account: User, status: UserStatus) => {
    setSavingUserId(account.id);
    try {
      await repo.saveUser({ ...account, status });
      if (currentUser) {
        await repo.saveSystemLog({
          id: crypto.randomUUID(),
          userId: currentUser.id,
          userName: currentUser.name,
          action: `USER_${status}_UPDATED — ${account.name}`,
          orgId: account.orgId,
          timestamp: Date.now(),
        });
      }
      await load();
      toast.success(
        status === UserStatus.ACTIVE ? 'User Approved' : 'User Rejected',
        `${account.name} is now ${status.toLowerCase()}.`,
      );
    } finally {
      setSavingUserId(null);
    }
  };

  const handleSave = async (u: User) => {
    if (!editUser && !isSuperAdmin && isAtPlanLimit(currentOrg, 'max_users', users.length)) {
      setShowUserLimit(true);
      return;
    }
    const normalizeUserArray = (value: unknown): string[] => Array.isArray(value) ? value.map(item => String(item)) : [];
    const rawRole = String(u.role ?? '');
    const safeRole = (Object.values(UserRole) as string[]).includes(rawRole) ? rawRole as UserRole : UserRole.EMPLOYEE;
    const payload: User = {
      ...u,
      role: safeRole,
      assignedBranches: normalizeUserArray(u.assignedBranches),
      assignedModules: normalizeUserArray(u.assignedModules) as ModuleId[],
      assignedBrands: normalizeUserArray(u.assignedBrands),
      tabAccess: normalizeUserArray(u.tabAccess),
      assignedDepartments: normalizeUserArray(u.assignedDepartments),
    };
    const nextUser = !editUser && manualApprovalEnabled && !isPlatformRole(payload.role)
      ? { ...payload, status: UserStatus.PENDING }
      : { ...payload, status: payload.status ?? UserStatus.ACTIVE };
    try {
      // Diff module access for audit log (edits only, not new user creation)
      if (editUser && currentUser) {
        const prevModules = editUser.assignedModules ?? [];
        const nextModules = nextUser.assignedModules ?? [];
        const added   = nextModules.filter(m => !prevModules.includes(m));
        const removed = prevModules.filter(m => !nextModules.includes(m));
        if (added.length > 0 || removed.length > 0) {
          const parts: string[] = [];
          if (added.length)   parts.push(`Added: ${added.join(', ')}`);
          if (removed.length) parts.push(`Removed: ${removed.join(', ')}`);
          await repo.saveSystemLog({
            id: crypto.randomUUID(),
            userId: currentUser.id,
            userName: currentUser.name,
            action: `Admin ${currentUser.name} modified Module Access for User ${u.name}: ${parts.join(' | ')}`,
            timestamp: Date.now(),
          });
        }
      }
      await repo.saveUser(nextUser);
      await load();
      setShowForm(false);
      setEditUser(null);
      toast.success(
        'User Saved',
        (nextUser.status ?? UserStatus.ACTIVE) === UserStatus.PENDING
          ? `${nextUser.name} is pending admin approval.`
          : `${nextUser.name} — ${nextUser.assignedModules?.length ?? 0} modules, ${nextUser.tabAccess?.length ?? 0} tabs saved.`
      );
    } catch (error) {
      console.error('SAVE FAILED:', error);
      toast.error('Save Failed', error instanceof Error ? error.message : 'Failed to save user. Check console for details.');
    }
  };

  const handleDelete = (account: User) => {
    if (DEMO_IDS.has(account.id)) return;
    if (account.id === currentUser?.id) {
      toast.error('Action Blocked', 'You cannot delete your own active session.');
      return;
    }
    setDeleteConfirm({
      message: `Delete ${account.name}? This account will lose access immediately. This cannot be undone.`,
      onConfirm: async () => {
        setSavingUserId(account.id);
        try {
          await repo.deleteUser(account.id);
          await load();
          toast.success('User Deleted', `${account.name} has been removed.`);
        } catch (error) {
          console.error('DELETE USER FAILED:', error);
          toast.error('Delete Failed', error instanceof Error ? error.message : 'Could not delete user.');
        } finally {
          setSavingUserId(null);
        }
      },
    });
  };

  return (
    <div className="space-y-6">
      {showUserLimit && <PlanLimitModal resource="Users" currentPlan={orgPlan} onClose={() => setShowUserLimit(false)} />}
      {!isInternalAdmin && currentOrg && (
        <div className="bg-white border border-zinc-200 rounded-xl p-5 shadow-sm flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-sm font-bold text-zinc-900">Manual approval for new users</p>
            <p className="text-sm text-zinc-500 mt-1">When enabled, newly created tenant accounts stay pending until an org admin activates them.</p>
            {pendingUsers.length > 0 && (
              <p className="text-xs font-bold uppercase tracking-widest text-amber-600 mt-2">{pendingUsers.length} pending user{pendingUsers.length === 1 ? '' : 's'}</p>
            )}
          </div>
          <button onClick={handleToggleManualApproval} className="shrink-0">
            {manualApprovalEnabled
              ? <ToggleRight size={34} className="text-emerald-500" />
              : <ToggleLeft size={34} className="text-zinc-300" />}
          </button>
        </div>
      )}
      <div className="flex items-end justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">User Accounts</h2>
          <p className="text-sm text-slate-500 mt-1">Manage user accounts, module access, and brand-to-branch scope assignments.</p>
        </div>
        <button
          onClick={() => { setEditUser(null); setShowForm(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-zinc-900 text-white rounded-lg text-sm font-semibold hover:bg-zinc-800 transition-all"
        >
          <Plus size={16} /> Add User
        </button>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden shadow-sm">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-zinc-50 border-b border-zinc-200">
              <th className="px-4 py-3 text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Name</th>
              <th className="px-4 py-3 text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Email</th>
              <th className="px-4 py-3 text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Role</th>
              <th className="px-4 py-3 text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Status</th>
              <th className="px-4 py-3 text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Branches</th>
              <th className="px-4 py-3 text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Brands</th>
              <th className="px-4 py-3 text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Modules</th>
              <th className="px-4 py-3 text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Depts</th>
              <th className="px-4 py-3 text-[10px] font-bold text-zinc-400 uppercase tracking-widest text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {users.map(u => {
              const isSelfAccount = currentUser?.id === u.id;
              return (
              <tr key={u.id} className="hover:bg-zinc-50 transition-colors">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-zinc-900 rounded-lg flex items-center justify-center text-white text-xs font-bold">
                      {u.name.charAt(0)}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-zinc-900">{u.name}</p>
                      {DEMO_IDS.has(u.id) && (
                        <span className="text-[9px] font-bold text-indigo-500 uppercase tracking-widest">Demo</span>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-zinc-500">{u.email}</td>
                <td className="px-4 py-3">
                  <span className={cn(
                    "px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest",
                    (u.role === UserRole.PLATFORM_OWNER || u.role === UserRole.SUPER_ADMIN)
                      ? "bg-rose-50 text-rose-600 border border-rose-100"
                      : u.role === UserRole.PLATFORM_SUPPORT
                      ? "bg-sky-50 text-sky-600 border border-sky-100"
                      : u.role === UserRole.PLATFORM_SALES
                      ? "bg-amber-50 text-amber-600 border border-amber-100"
                      : u.role === UserRole.ADMIN
                      ? "bg-indigo-50 text-indigo-600 border border-indigo-100"
                      : u.role === UserRole.DEPARTMENT_HEAD
                      ? "bg-purple-50 text-purple-600 border border-purple-100"
                      : u.role === UserRole.EMPLOYEE
                      ? "bg-slate-50 text-slate-500 border border-slate-200"
                      : "bg-emerald-50 text-emerald-600 border border-emerald-100"
                  )}>
                    {u.role === UserRole.DEPARTMENT_HEAD ? 'DEPT HEAD' : u.role === UserRole.PLATFORM_OWNER || u.role === UserRole.SUPER_ADMIN ? 'OWNER' : u.role === UserRole.PLATFORM_SUPPORT ? 'SUPPORT' : u.role === UserRole.PLATFORM_SALES ? 'SALES' : u.role}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <UserStatusBadge status={u.status} />
                </td>
                <td className="px-4 py-3 text-sm text-zinc-500">
                  {isPlatformRole(u.role) ? (
                    <span className="text-zinc-400 italic">Platform</span>
                  ) : !hasScopedAssignments(u) ? (
                    <span className="text-zinc-400 italic">All</span>
                  ) : getAccessibleBranchCount(brands, u) > 0 ? (
                    <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 text-[10px] font-bold rounded-lg border border-emerald-100">
                      {getAccessibleBranchCount(brands, u)}
                    </span>
                  ) : (
                    <span className="text-zinc-400 italic">None</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {isPlatformRole(u.role) ? (
                    <span className="text-[10px] font-semibold text-zinc-400 italic">Platform</span>
                  ) : !hasScopedAssignments(u) ? (
                    <span className="text-[10px] font-semibold text-zinc-400 italic">All</span>
                  ) : (u.assignedBrands?.length ?? 0) > 0 ? (
                    <span className="px-2 py-0.5 bg-teal-50 text-teal-700 text-[10px] font-bold rounded-lg border border-teal-100">{u.assignedBrands?.length ?? 0}</span>
                  ) : (
                    <span className="text-[10px] font-semibold text-zinc-400 italic">Branch-only</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {isPlatformRole(u.role) ? (
                    <span className="text-[10px] font-semibold text-zinc-400 italic">Platform</span>
                  ) : !u.assignedModules ? (
                    <span className="text-[10px] font-semibold text-zinc-400 italic">All</span>
                  ) : u.assignedModules.length === 0 ? (
                    <span className="text-[10px] font-semibold text-zinc-400 italic">None</span>
                  ) : u.assignedModules.length >= assignableModuleIds.length ? (
                    <span className="text-[10px] font-semibold text-zinc-400 italic">All</span>
                  ) : (
                    <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 text-[10px] font-bold rounded-lg border border-indigo-100">{u.assignedModules?.length ?? 0}</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {(u.assignedDepartments?.length ?? 0) === 0 ? (
                    <span className="text-[10px] font-semibold text-zinc-400 italic">None</span>
                  ) : (
                    <span className="px-2 py-0.5 bg-purple-50 text-purple-700 text-[10px] font-bold rounded-lg border border-purple-100">{u.assignedDepartments?.length ?? 0}</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    {manualApprovalEnabled && !isPlatformRole(u.role) && (u.status ?? UserStatus.ACTIVE) === UserStatus.PENDING && (
                      <>
                        <button
                          onClick={() => handleStatusChange(u, UserStatus.ACTIVE)}
                          disabled={savingUserId === u.id}
                          className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => handleStatusChange(u, UserStatus.REJECTED)}
                          disabled={savingUserId === u.id}
                          className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest rounded-lg bg-rose-50 text-rose-700 hover:bg-rose-100 disabled:opacity-50"
                        >
                          Reject
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => { setEditUser(u); setShowForm(true); }}
                      className="p-1.5 text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 rounded-lg transition-all"
                    >
                      <Edit2 size={14} />
                    </button>
                    {!DEMO_IDS.has(u.id) && !isSelfAccount && (
                      <button
                        onClick={() => handleDelete(u)}
                        disabled={savingUserId === u.id}
                        className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            )})}
          </tbody>
        </table>
      </div>

      {showForm && (
        <UserAccountFormModal
          key={editUser?.id ?? 'new-user'}
          user={editUser}
          settings={settings}
          brands={brands}
          departments={departments}
          currentOrg={currentOrg}
          isPlatformContext={!!isInternalAdmin}
          isPlatformOwner={!!isPlatformOwner}
          currentUserId={currentUser?.id}
          currentUserRole={currentUser?.role}
          onSave={handleSave}
          onClose={() => { setShowForm(false); setEditUser(null); }}
        />
      )}
      <ConfirmDeleteDialog state={deleteConfirm} onClose={() => setDeleteConfirm(null)} />
    </div>
  );
}

function UserAccountFormModal({ user, settings, brands, departments, currentOrg, isPlatformContext, isPlatformOwner, currentUserId, currentUserRole, onSave, onClose }: {
  user: User | null;
  settings: any;
  brands: Brand[];
  departments: Department[];
  currentOrg: Organization | null;
  isPlatformContext?: boolean;
  isPlatformOwner?: boolean;
  currentUserId?: string;
  currentUserRole?: UserRole;
  onSave: (u: User) => void;
  onClose: () => void;
}) {
  // ── Module list derived from PLATFORM_MODULES, filtered by org's enabledApps ──
  const enabledAppsKey = (currentOrg?.enabledApps ?? []).join('|');
  const MODULE_LIST = React.useMemo(() => PLATFORM_MODULES.map(mod => {
    const appDef = APP_REGISTRY.find(a => a.moduleId === mod.id || a.id === mod.id);
    return {
      id: (appDef?.moduleId ?? mod.id) as ModuleId,
      label: mod.name,
      description: appDef?.description ?? '',
      tabs: mod.tabs,
    };
  }).filter(mod => {
    // System module is always visible (core platform, not an installable app)
    if (mod.id === MODULE_IDS.SYSTEM) return true;
    // Dual-Key: only show modules the org has explicitly licensed.
    // Treat undefined enabledApps as [] → strict zero-state lockdown.
    const orgApps = currentOrg?.enabledApps ?? [];
    return orgApps.includes(mod.id);
  }), [enabledAppsKey]);

  const allModuleIds = React.useMemo(() => MODULE_LIST.map(m => m.id), [MODULE_LIST]);

  // Build the initial tabAccess from the user — exact array from DB, no defaults
  const buildInitialTabAccess = React.useCallback((): string[] => {
    if (user?.tabAccess) {
      // Return the exact saved array — respect all manual tab unchecks.
      // No auto-granting: if an admin unchecked tabs, those choices persist.
      return [...user.tabAccess];
    }
    // New user: zero-state — no modules assigned, so no tabs
    if (!user) {
      return [];
    }
    // Existing user with no tabAccess saved yet (legacy) — give all tabs for their modules
    const mods = user.assignedModules ?? allModuleIds;
    return mods.flatMap(mid => allTabKeysForModule(mid));
  }, [allModuleIds, user]);

  const buildUserFormState = React.useCallback(() => ({
    name: user?.name ?? '',
    email: user?.email ?? '',
    role: user?.role ?? (isPlatformContext ? UserRole.PLATFORM_SUPPORT : UserRole.MANAGER),
    assignedBranches: user?.assignedBranches ?? [],
    assignedModules: user?.assignedModules ?? (user ? allModuleIds : []),
    assignedBrands: user?.assignedBrands ?? [],
    tabAccess: buildInitialTabAccess(),
    assignedDepartments: user?.assignedDepartments ?? [],
    tempPassword: '',
    forceChangePassword: user ? (user.mustChangePassword ?? false) : true,
    canExportData: user?.canExportData ?? true,
  }), [allModuleIds, buildInitialTabAccess, isPlatformContext, user]);

  const [form, setForm] = useState<{
    name: string;
    email: string;
    role: UserRole;
    assignedBranches: string[];
    assignedModules: ModuleId[];
    assignedBrands: string[];
    tabAccess: string[];
    assignedDepartments: string[];
    tempPassword: string;
    forceChangePassword: boolean;
    canExportData: boolean;
  }>(buildUserFormState);
  const userFormSnap = React.useRef(user ? JSON.parse(JSON.stringify({
    name: user.name ?? '',
    email: user.email ?? '',
    role: user.role ?? UserRole.MANAGER,
    assignedBranches: user.assignedBranches ?? [],
    assignedModules: user.assignedModules ?? allModuleIds,
    assignedBrands: user.assignedBrands ?? [],
    tabAccess: buildInitialTabAccess(),
    assignedDepartments: user.assignedDepartments ?? [],
    tempPassword: '',
    forceChangePassword: user.mustChangePassword ?? false,
    canExportData: user.canExportData ?? true,
  })) : null);
  const userDirty = React.useMemo(() => user ? hasDataChanged(userFormSnap.current, form) : (!!form.name.trim() && !!form.email.trim()), [form, user]);
  const [showTempPassword, setShowTempPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [expandedBrands, setExpandedBrands] = useState<Record<string, boolean>>({});
  const isEditingSelf = !!user && !!currentUserId && user.id === currentUserId;
  const isEditingOrgOwner = !!user && !!currentOrg?.ownerId && user.id === currentOrg.ownerId;
  const canEditRole = currentUserRole === UserRole.ADMIN || currentUserRole === UserRole.SUPER_ADMIN || currentUserRole === UserRole.PLATFORM_OWNER;
  const roleSelectDisabled = isEditingSelf || isEditingOrgOwner || !canEditRole;
  const shouldShowScopedAssignments = !isPlatformRole(form.role) && form.role !== UserRole.ADMIN;

  useEffect(() => {
    setExpandedBrands(Object.fromEntries(brands.map(brand => [brand.id, false])));
  }, [brands, user?.id]);

  useEffect(() => {
    const nextForm = buildUserFormState();
    setForm(nextForm);
    userFormSnap.current = user ? JSON.parse(JSON.stringify(nextForm)) : null;
    setShowTempPassword(false);
    setPasswordError('');
  }, [buildUserFormState, user?.id]);

  const toggleBrandExpansion = (brandId: string) => {
    setExpandedBrands(prev => ({ ...prev, [brandId]: !prev[brandId] }));
  };

  const toggleBrand = (brand: Brand) => {
    const brandBranchIds = (brand.branches ?? []).map(branch => branch.id);
    setForm(prev => {
      const branchState = getBrandSelectionState(brand, prev.assignedBrands, prev.assignedBranches);
      return {
        ...prev,
        assignedBrands: branchState.checked
          ? prev.assignedBrands.filter(id => id !== brand.id)
          : [...prev.assignedBrands.filter(id => id !== brand.id), brand.id],
        assignedBranches: prev.assignedBranches.filter(branchId => !brandBranchIds.includes(branchId)),
      };
    });
  };

  const toggleBranch = (brand: Brand, branch: Branch) => {
    const brandBranchIds = (brand.branches ?? []).map(item => item.id);
    setForm(prev => {
      const brandSelected = prev.assignedBrands.includes(brand.id);
      let nextAssignedBrands = prev.assignedBrands.filter(id => id !== brand.id);
      let nextAssignedBranches = [...prev.assignedBranches];

      if (brandSelected) {
        nextAssignedBranches = [
          ...prev.assignedBranches.filter(id => !brandBranchIds.includes(id)),
          ...brandBranchIds.filter(id => id !== branch.id),
        ];
      } else if (nextAssignedBranches.includes(branch.id)) {
        nextAssignedBranches = nextAssignedBranches.filter(id => id !== branch.id);
      } else {
        nextAssignedBranches = [...nextAssignedBranches, branch.id];
      }

      nextAssignedBranches = Array.from(new Set(nextAssignedBranches));

      if (brandBranchIds.length > 0 && brandBranchIds.every(id => nextAssignedBranches.includes(id))) {
        nextAssignedBrands = [...nextAssignedBrands, brand.id];
        nextAssignedBranches = nextAssignedBranches.filter(id => !brandBranchIds.includes(id));
      }

      return {
        ...prev,
        assignedBrands: Array.from(new Set(nextAssignedBrands)),
        assignedBranches: nextAssignedBranches,
      };
    });
  };

  const toggleModule = (moduleId: ModuleId) => {
    setForm(prev => {
      const wasActive = prev.assignedModules.includes(moduleId);
      const nextModules = wasActive
        ? prev.assignedModules.filter(m => m !== moduleId)
        : [...prev.assignedModules, moduleId];
      // When enabling a module, grant all its tabs by default
      // When disabling, remove all its tab keys
      const modTabKeys = allTabKeysForModule(moduleId);
      const nextTabAccess = wasActive
        ? prev.tabAccess.filter(k => !modTabKeys.includes(k))
        : [...prev.tabAccess, ...modTabKeys.filter(k => !prev.tabAccess.includes(k))];
      return { ...prev, assignedModules: nextModules, tabAccess: nextTabAccess };
    });
  };

  const toggleTab = (moduleId: string, tabId: string) => {
    // For variant tabs, toggle ALL variant keys at once
    const modDef = PLATFORM_MODULES.find(m => m.id === moduleId);
    const tabDef = modDef?.tabs.find(t => t.id === tabId);
    if (tabDef?.variants && tabDef.variants.length > 0) {
      const variantKeys = tabDef.variants.map(v => tabPermissionKey(moduleId, tabId, v));
      setForm(prev => {
        const hasAny = variantKeys.some(k => prev.tabAccess.includes(k));
        if (hasAny) {
          // Remove all variant keys for this tab
          return { ...prev, tabAccess: prev.tabAccess.filter(k => !variantKeys.includes(k)) };
        } else {
          // Grant all variant keys for this tab
          return { ...prev, tabAccess: [...prev.tabAccess, ...variantKeys.filter(k => !prev.tabAccess.includes(k))] };
        }
      });
    } else {
      const key = tabPermissionKey(moduleId, tabId);
      setForm(prev => ({
        ...prev,
        tabAccess: prev.tabAccess.includes(key)
          ? prev.tabAccess.filter(k => k !== key)
          : [...prev.tabAccess, key],
      }));
    }
  };

  const toggleTabVariant = (moduleId: string, tabId: string, variant: string) => {
    const key = tabPermissionKey(moduleId, tabId, variant);
    setForm(prev => ({
      ...prev,
      tabAccess: prev.tabAccess.includes(key)
        ? prev.tabAccess.filter(k => k !== key)
        : [...prev.tabAccess, key],
    }));
  };

  const toggleDepartment = (deptId: string) => {
    setForm(prev => ({
      ...prev,
      assignedDepartments: prev.assignedDepartments.includes(deptId)
        ? prev.assignedDepartments.filter(d => d !== deptId)
        : [...prev.assignedDepartments, deptId],
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Validate password if provided
    if (form.tempPassword) {
      if (form.tempPassword.length < 8) {
        setPasswordError('Password must be at least 8 characters.');
        return;
      }
      if (!/\d/.test(form.tempPassword)) {
        setPasswordError('Password must contain at least 1 number.');
        return;
      }
    }
    setPasswordError('');
    const normalizeFormArray = (value: unknown): string[] => Array.isArray(value) ? value.map(item => String(item)) : [];
    const roleValue = String(form.role ?? '');
    const safeRole = (Object.values(UserRole) as string[]).includes(roleValue)
      ? roleValue as UserRole
      : (user?.role ?? (isPlatformContext ? UserRole.PLATFORM_SUPPORT : UserRole.EMPLOYEE));
    const isPlatformRoleSelected = isPlatformRole(safeRole);
    // Save exact arrays from form — no role-based overrides
    const formData: User = {
      id: user?.id ?? '',
      name: form.name,
      email: form.email,
      role: safeRole,
      status: user?.status ?? (isPlatformRoleSelected ? UserStatus.ACTIVE : (currentOrg?.manualUserApproval ? UserStatus.PENDING : UserStatus.ACTIVE)),
      accountType: isPlatformRoleSelected ? 'platform_staff' : 'tenant_user',
      assignedBranches: isPlatformRoleSelected ? [] : normalizeFormArray(form.assignedBranches),
      assignedModules: isPlatformRoleSelected ? [] : normalizeFormArray(form.assignedModules) as ModuleId[],
      assignedBrands: isPlatformRoleSelected ? [] : normalizeFormArray(form.assignedBrands),
      tabAccess: isPlatformRoleSelected ? [] : normalizeFormArray(form.tabAccess),
      assignedDepartments: isPlatformRoleSelected ? [] : normalizeFormArray(form.assignedDepartments),
      ...(isPlatformRoleSelected ? { orgId: '__platform__', canInspectTenants: safeRole !== UserRole.PLATFORM_SALES } : {}),
      canExportData: form.canExportData,
      // Security fields
      ...(form.tempPassword ? { passwordHash: form.tempPassword } : {}),
      mustChangePassword: form.forceChangePassword,
    };
    console.log('SUBMITTING PAYLOAD:', formData);
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-zinc-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-4xl shadow-2xl overflow-hidden">
        <div className="bg-slate-900 px-5 py-4 flex items-center justify-between">
          <div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
              {user ? 'Edit Account' : 'New Account'}
            </p>
            <p className="text-white font-bold text-sm mt-0.5">
              {user ? user.name : 'Create User'}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-800 rounded-lg transition-colors text-slate-400 hover:text-white">
            <X size={16} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4 max-h-[80vh] overflow-y-auto">
          {/* ── Section 1: Identity ── */}
          <p className="text-[9px] font-bold text-zinc-300 uppercase tracking-[0.15em]">1 — Identity</p>
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Full Name</label>
            <input
              required
              value={form.name}
              onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
              className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-sm focus:outline-none focus:border-zinc-900"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Email Address</label>
            <input
              type="email"
              required
              value={form.email}
              onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
              className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-sm focus:outline-none focus:border-zinc-900"
            />
          </div>

          {/* ── Section: Security ── */}
          <div className="border-t border-zinc-100 pt-3">
            <p className="text-[9px] font-bold text-zinc-300 uppercase tracking-[0.15em] mb-3">Security</p>
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">
              {user ? 'Reset Password' : 'Temporary Password'}
            </label>
            <div className="relative">
              <input
                type={showTempPassword ? 'text' : 'password'}
                value={form.tempPassword}
                onChange={e => { setForm(p => ({ ...p, tempPassword: e.target.value })); setPasswordError(''); }}
                placeholder={user ? 'Leave blank to keep current' : 'Min 8 chars, 1 number'}
                className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-sm focus:outline-none focus:border-zinc-900 pr-10"
              />
              <button
                type="button"
                onClick={() => setShowTempPassword(p => !p)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
              >
                {showTempPassword ? <Eye size={14} /> : <Lock size={14} />}
              </button>
            </div>
            {passwordError && (
              <p className="text-[10px] text-red-500 font-semibold">{passwordError}</p>
            )}
            {!user && !form.tempPassword && (
              <p className="text-[10px] text-amber-500 font-semibold">A password is recommended for new accounts.</p>
            )}
          </div>
          <div className="flex items-center justify-between bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2.5">
            <div>
              <p className="text-xs font-semibold text-zinc-700">Force password change on next login</p>
              <p className="text-[10px] text-zinc-400">User must set a new password before accessing the portal.</p>
            </div>
            <button
              type="button"
              onClick={() => setForm(p => ({ ...p, forceChangePassword: !p.forceChangePassword }))}
              className="shrink-0"
            >
              {form.forceChangePassword
                ? <ToggleRight size={28} className="text-indigo-600" />
                : <ToggleLeft size={28} className="text-zinc-300" />
              }
            </button>
          </div>

          {/* ── Section 2: Role ── */}
          <div className="border-t border-zinc-100 pt-3">
            <p className="text-[9px] font-bold text-zinc-300 uppercase tracking-[0.15em] mb-3">2 — Role</p>
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Role</label>
            <CustomSelect
              value={form.role}
              disabled={roleSelectDisabled}
              onChange={v => {
                const newRole = v as UserRole;
                setForm(p => {
                  if (newRole === UserRole.ADMIN) {
                    const allMods = allModuleIds;
                    const allTabs = allMods.flatMap(mid => allTabKeysForModule(mid));
                    return {
                      ...p,
                      role: newRole,
                      assignedModules: allMods,
                      tabAccess: allTabs,
                      assignedBranches: [],
                      assignedBrands: [],
                      assignedDepartments: [],
                    };
                  }
                  if (newRole === UserRole.DEPARTMENT_HEAD) {
                    return {
                      ...p,
                      role: newRole,
                      assignedBranches: [],
                      assignedBrands: [],
                    };
                  }
                  return { ...p, role: newRole };
                });
              }}
              options={(() => {
                // ── Platform Context: Internal Accounts — show platform roles ──
                if (isPlatformContext) {
                  const PLATFORM_ROLE_OPTIONS = [
                    { value: UserRole.PLATFORM_OWNER, label: 'Platform Owner' },
                    { value: UserRole.PLATFORM_SUPPORT, label: 'Platform Support' },
                    { value: UserRole.PLATFORM_SALES, label: 'Platform Sales' },
                  ];
                  // Only PLATFORM_OWNER can assign the PLATFORM_OWNER role
                  if (!isPlatformOwner) {
                    return PLATFORM_ROLE_OPTIONS.filter(o => o.value !== UserRole.PLATFORM_OWNER);
                  }
                  // Prevent Support/Sales from self-promoting: if editing themselves, exclude higher roles
                  if (user && user.id === '__self__') {
                    // This is handled above; Owner can assign anything
                  }
                  return PLATFORM_ROLE_OPTIONS;
                }

                // ── Tenant Context: show only tenant roles ──
                const ALL_ROLE_OPTIONS = [
                  { value: UserRole.ADMIN, label: 'Admin' },
                  { value: UserRole.DEPARTMENT_HEAD, label: 'Department Head' },
                  { value: UserRole.MANAGER, label: 'Manager' },
                  { value: UserRole.EMPLOYEE, label: 'Employee' },
                ];
                const provisioned = currentOrg?.provisionedRoles;
                if (!provisioned || provisioned.length === 0) return ALL_ROLE_OPTIONS;
                return ALL_ROLE_OPTIONS.filter(o => provisioned.includes(o.value));
              })()}
              placeholder="Select role…"
            />
            {isEditingSelf && (
              <p className="text-[10px] text-amber-600 font-semibold">
                Self-preservation is active. Your role cannot be changed while you are signed in.
              </p>
            )}
            {isEditingOrgOwner && (
              <p className="text-[10px] text-amber-600 font-semibold">
                Owner lock is active. The primary owner must remain an Admin.
              </p>
            )}
          </div>
          {form.role === UserRole.ADMIN && (
            <div className="bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2">
              <p className="text-[10px] text-indigo-600 font-semibold">
                Admins start with <strong>full module and tab access</strong>, but you can now restrict that access below when you need a tighter operating scope.
              </p>
            </div>
          )}
          {form.role === UserRole.EMPLOYEE && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
              <p className="text-[10px] text-slate-600 font-semibold">
                Employees have <strong>read-only</strong> access. They can view data in assigned modules but cannot create, edit, or delete records.
              </p>
            </div>
          )}

          {/* ── Section 3: Permissions ── */}
          <div className="border-t border-zinc-100 pt-3">
            <p className="text-[9px] font-bold text-zinc-300 uppercase tracking-[0.15em] mb-3">3 — Permissions</p>
          </div>
          <div className="flex items-center justify-between bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2.5">
            <div>
              <p className="text-xs font-semibold text-zinc-700">Allow Data Export (Excel/ZIP)</p>
              <p className="text-[10px] text-zinc-400">Controls export and download actions across modules. SUPER_ADMIN bypass remains enforced separately.</p>
            </div>
            <button
              type="button"
              onClick={() => setForm(p => ({ ...p, canExportData: !p.canExportData }))}
              className="shrink-0"
            >
              {form.canExportData
                ? <ToggleRight size={28} className="text-indigo-600" />
                : <ToggleLeft size={28} className="text-zinc-300" />
              }
            </button>
          </div>

          {/* ── Section 4: Module & Tab Access ── */}
          {shouldShowScopedAssignments && (
            <>
            <div className="border-t border-zinc-100 pt-3">
              <p className="text-[9px] font-bold text-zinc-300 uppercase tracking-[0.15em]">4 — Module &amp; Tab Access</p>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Module & Tab Access</label>
                <span className="text-[9px] text-zinc-400 italic">{form.assignedModules.length}/{MODULE_LIST.length} modules</span>
              </div>
              <div className="flex flex-col gap-3">
                {MODULE_LIST.map(mod => {
                  const moduleActive = form.assignedModules.includes(mod.id);
                  const modTabKeys = allTabKeysForModule(mod.id);
                  const enabledTabCount = form.tabAccess.filter(k => modTabKeys.includes(k)).length;
                  return (
                    <div key={mod.id} className="border border-zinc-200 rounded-lg overflow-hidden">
                      {/* Module toggle header */}
                      <button
                        type="button"
                        onClick={() => toggleModule(mod.id)}
                        className={cn(
                          "flex items-center gap-3 px-3 py-2.5 w-full text-left transition-all",
                          moduleActive
                            ? "bg-zinc-900 text-white"
                            : "bg-zinc-50 text-zinc-500 hover:bg-zinc-100"
                        )}
                      >
                        <div className="flex-1">
                          <p className="text-xs font-semibold">{mod.label}</p>
                          {mod.description && (
                            <p className={cn("text-[10px]", moduleActive ? "text-zinc-400" : "text-zinc-400")}>{mod.description}</p>
                          )}
                        </div>
                        <span className={cn("text-[9px] font-mono", moduleActive ? "text-zinc-400" : "text-zinc-400")}>
                          {enabledTabCount}/{mod.tabs.length}
                        </span>
                        {moduleActive
                          ? <Check size={14} />
                          : <div className="w-3.5 h-3.5 rounded border border-zinc-300" />}
                      </button>
                      {/* Nested tab checkboxes — only visible when module is enabled */}
                      {moduleActive && (
                        <div className="px-3 py-2 bg-zinc-50 border-t border-zinc-200 space-y-1">
                          {mod.tabs.map(tab => {
                            const tabDef = PLATFORM_MODULES.find(m => m.id === mod.id)?.tabs.find(t => t.id === tab.id);
                            const hasVariants = tabDef?.variants && tabDef.variants.length > 0;

                            if (hasVariants) {
                              // ── Variant tab: show label + variant pills ──
                              const variantKeys = tabDef!.variants!.map(v => tabPermissionKey(mod.id, tab.id, v));
                              const activeVariants = tabDef!.variants!.filter(v =>
                                form.tabAccess.includes(tabPermissionKey(mod.id, tab.id, v))
                              );
                              const anyActive = activeVariants.length > 0;
                              return (
                                <div key={tab.id} className={cn(
                                  "flex items-center gap-2 w-full px-2.5 py-1.5 rounded text-xs transition-all",
                                  anyActive ? "bg-indigo-50" : ""
                                )}>
                                  <button
                                    type="button"
                                    onClick={() => toggleTab(mod.id, tab.id)}
                                    className="flex items-center gap-2 shrink-0"
                                  >
                                    <div className={cn(
                                      "w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 transition-all",
                                      anyActive ? "bg-indigo-600 border-indigo-600" : "border-zinc-300"
                                    )}>
                                      {anyActive && <Check size={10} className="text-white" />}
                                    </div>
                                    <span className={cn(anyActive ? "text-indigo-700 font-semibold" : "text-zinc-400")}>{tab.name}</span>
                                  </button>
                                  <div className="flex items-center gap-1 ml-auto">
                                    {tabDef!.variants!.map(v => {
                                      const vKey = tabPermissionKey(mod.id, tab.id, v);
                                      const vActive = form.tabAccess.includes(vKey);
                                      return (
                                        <button
                                          type="button"
                                          key={v}
                                          onClick={() => toggleTabVariant(mod.id, tab.id, v)}
                                          className={cn(
                                            "px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider border transition-all",
                                            vActive
                                              ? v === 'admin'
                                                ? "bg-amber-100 border-amber-300 text-amber-700"
                                                : "bg-sky-100 border-sky-300 text-sky-700"
                                              : "bg-zinc-100 border-zinc-200 text-zinc-400 hover:border-zinc-300"
                                          )}
                                        >
                                          {v}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            }

                            // ── Non-variant tab: simple checkbox ──
                            const key = tabPermissionKey(mod.id, tab.id);
                            const tabActive = form.tabAccess.includes(key);
                            return (
                              <button
                                type="button"
                                key={tab.id}
                                onClick={() => toggleTab(mod.id, tab.id)}
                                className={cn(
                                  "flex items-center gap-2 w-full px-2.5 py-1.5 rounded text-left text-xs transition-all",
                                  tabActive
                                    ? "bg-indigo-50 text-indigo-700 font-semibold"
                                    : "text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600"
                                )}
                              >
                                <div className={cn(
                                  "w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 transition-all",
                                  tabActive
                                    ? "bg-indigo-600 border-indigo-600"
                                    : "border-zinc-300"
                                )}>
                                  {tabActive && <Check size={10} className="text-white" />}
                                </div>
                                <span>{tab.name}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            </>
          )}

          {/* ── Section 5: Scope (Brands, Branches, Departments) ── */}
          {!isPlatformRole(form.role) && (
            <>
            <div className="border-t border-zinc-100 pt-3">
              <p className="text-[9px] font-bold text-zinc-300 uppercase tracking-[0.15em]">5 — Scope</p>
            </div>
          {brands.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Assigned Brands &amp; Branches</label>
                  <p className="text-[10px] text-zinc-400 mt-1">Leave everything unselected for unrestricted scope. Selecting a brand includes every current and future branch under it.</p>
                </div>
                <span className="px-2.5 py-1 bg-indigo-50 text-indigo-700 text-[10px] font-bold rounded-lg border border-indigo-100">
                  Selected: {form.assignedBrands.length} Brands, {form.assignedBranches.length} Branches
                </span>
              </div>
              <div className="border border-zinc-200 rounded-xl overflow-hidden bg-white">
                {brands.map((brand, index) => {
                  const branchState = getBrandSelectionState(brand, form.assignedBrands, form.assignedBranches);
                  const branchCount = brand.branches?.length ?? 0;
                  return (
                    <div key={brand.id} className={cn(index > 0 && 'border-t border-zinc-100')}>
                      <div className="flex items-center gap-2 px-3 py-2.5 bg-zinc-50">
                        {branchCount > 0 ? (
                          <button
                            type="button"
                            onClick={() => toggleBrandExpansion(brand.id)}
                            className="p-1 text-zinc-400 hover:text-zinc-700 rounded"
                          >
                            {expandedBrands[brand.id] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          </button>
                        ) : (
                          <div className="w-6" />
                        )}
                        <button
                          type="button"
                          onClick={() => toggleBrand(brand)}
                          className="flex items-center gap-3 flex-1 text-left"
                        >
                          <ScopeSelectionIndicator checked={branchState.checked} indeterminate={branchState.indeterminate} />
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-zinc-900">{brand.name}</p>
                            <p className="text-[10px] text-zinc-400">{branchCount} branch{branchCount === 1 ? '' : 'es'}</p>
                          </div>
                        </button>
                        <span className="px-2 py-0.5 bg-white border border-zinc-200 rounded-lg text-[10px] font-semibold text-zinc-500">
                          {branchState.checked && form.assignedBrands.includes(brand.id)
                            ? 'Full brand'
                            : branchState.selectedBranches > 0
                            ? `${branchState.selectedBranches}/${branchCount || branchState.selectedBranches} branches`
                            : 'Unselected'}
                        </span>
                      </div>
                      {expandedBrands[brand.id] && branchCount > 0 && (
                        <div className="px-4 py-3 space-y-1 bg-white">
                          {(brand.branches ?? []).map(branch => {
                            const branchSelected = form.assignedBrands.includes(brand.id) || form.assignedBranches.includes(branch.id);
                            return (
                              <button
                                type="button"
                                key={branch.id}
                                onClick={() => toggleBranch(brand, branch)}
                                className={cn(
                                  'w-full flex items-center gap-3 px-3 py-2 rounded-xl border text-left transition-all',
                                  branchSelected
                                    ? 'border-indigo-200 bg-indigo-50'
                                    : 'border-zinc-200 bg-zinc-50 hover:border-zinc-300'
                                )}
                              >
                                <ScopeSelectionIndicator checked={branchSelected} />
                                <div className="min-w-0 flex-1">
                                  <p className={cn('text-sm font-medium', branchSelected ? 'text-indigo-700' : 'text-zinc-700')}>{branch.name}</p>
                                  <p className="text-[10px] text-zinc-400">{branch.code}</p>
                                </div>
                                {form.assignedBrands.includes(brand.id) && (
                                  <span className="px-2 py-0.5 rounded-lg bg-white border border-indigo-200 text-[10px] font-semibold text-indigo-600">
                                    via brand
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {departments.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Departments</label>
                <span className="text-[9px] text-zinc-400 italic">
                  {form.assignedDepartments.length === 0 ? 'None' : `${form.assignedDepartments.length} selected`}
                </span>
              </div>
              {/* Group departments by linked module */}
              {(() => {
                // Build groups: each module → its linked departments
                const moduleGroups: { moduleId: string; moduleName: string; depts: Department[] }[] = [];
                const ungrouped: Department[] = [];
                const INST_MODS = PLATFORM_MODULES.filter(m => m.id !== MODULE_IDS.SYSTEM);
                INST_MODS.forEach(mod => {
                  const linked = departments.filter(d => (d.linkedModules ?? []).includes(mod.id));
                  if (linked.length > 0) moduleGroups.push({ moduleId: mod.id, moduleName: mod.name, depts: linked });
                });
                departments.forEach(d => {
                  if (!(d.linkedModules ?? []).length || !INST_MODS.some(m => (d.linkedModules ?? []).includes(m.id))) {
                    ungrouped.push(d);
                  }
                });
                return (
                  <div className="space-y-3">
                    {moduleGroups.map(group => (
                      <div key={group.moduleId} className="space-y-1.5">
                        <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 inline-block" />
                          {group.moduleName} Depts
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {group.depts.map(dept => (
                            <button
                              type="button"
                              key={dept.id}
                              onClick={() => toggleDepartment(dept.id)}
                              className={cn(
                                "px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all",
                                form.assignedDepartments.includes(dept.id)
                                  ? "bg-amber-600 text-white border-amber-600"
                                  : "bg-zinc-50 text-zinc-500 border-zinc-200 hover:border-zinc-400"
                              )}
                            >
                              {dept.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                    {ungrouped.length > 0 && (
                      <div className="space-y-1.5">
                        <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 inline-block" />
                          Unlinked Departments
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {ungrouped.map(dept => (
                            <button
                              type="button"
                              key={dept.id}
                              onClick={() => toggleDepartment(dept.id)}
                              className={cn(
                                "px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all",
                                form.assignedDepartments.includes(dept.id)
                                  ? "bg-amber-600 text-white border-amber-600"
                                  : "bg-zinc-50 text-zinc-500 border-zinc-200 hover:border-zinc-400"
                              )}
                            >
                              {dept.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
              <p className="text-[10px] text-zinc-400">{form.role === UserRole.ADMIN ? 'Departments this admin can review and approve.' : 'Departments this user belongs to for review workflow.'}</p>
            </div>
          )}
            </>
          )}
          <div className="flex gap-3 pt-1">
            {!userDirty && user && <span className="text-[10px] text-zinc-400 self-center">No changes detected</span>}
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-zinc-200 rounded-lg text-sm font-semibold hover:bg-zinc-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!userDirty}
              className="flex-1 px-4 py-2 bg-zinc-900 text-white rounded-lg text-sm font-semibold hover:bg-zinc-800 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {user ? 'Save Changes' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Scope meta (shared across API components) ────────────────────────────────

/** Build human-friendly label for a scope like 'rider-manager:read' → 'Rider Operations: Read'. */
function scopeLabel(scope: string): string {
  const [modId, type] = scope.split(':');
  const mod = getApiModules().find(m => m.id === modId);
  const modName = mod?.name ?? modId;
  return `${modName}: ${type === 'read' ? 'Read' : 'Write'}`;
}

// ─── ApiKeyFormModal ──────────────────────────────────────────────────────────

function ApiKeyFormModal({ onSave, onClose }: { onSave: (key: ApiKey) => void; onClose: () => void }) {
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<ApiScope[]>([]);
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const apiModules = getApiModules();

  const toggleScope = (scope: ApiScope) => {
    setScopes(prev => prev.includes(scope) ? prev.filter(s => s !== scope) : [...prev, scope]);
  };

  const toggleModuleAll = (moduleId: string) => {
    const modScopes = allApiScopes().filter(s => s.startsWith(moduleId + ':'));
    const allSelected = modScopes.every(s => scopes.includes(s));
    if (allSelected) {
      setScopes(prev => prev.filter(s => !modScopes.includes(s)));
    } else {
      setScopes(prev => [...new Set([...prev, ...modScopes])]);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || scopes.length === 0) return;
    const newKey: ApiKey = {
      id: crypto.randomUUID(),
      name: name.trim(),
      key: crypto.randomUUID(),
      status: 'active',
      scopes,
      createdAt: Date.now(),
    };
    onSave(newKey);
    setCreatedKey(newKey.key);
  };

  const copyKey = () => {
    if (!createdKey) return;
    navigator.clipboard.writeText(createdKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 bg-zinc-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-md shadow-2xl overflow-hidden">
        <div className="bg-slate-900 px-5 py-4 flex items-center justify-between">
          <div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">API Key</p>
            <p className="text-white font-bold text-sm mt-0.5">{createdKey ? 'Key Generated' : 'New API Key'}</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-800 rounded-lg transition-colors text-slate-400 hover:text-white">
            <X size={16} />
          </button>
        </div>

        {createdKey ? (
          <div className="p-6 space-y-5">
            <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <AlertCircle size={18} className="text-amber-600 mt-0.5 shrink-0" />
              <p className="text-sm text-amber-700">
                <span className="font-bold">Copy this key now.</span> For security, it will be partially masked in the key list.
              </p>
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Secret Key</label>
              <div className="flex gap-2">
                <code className="flex-1 px-3 py-2.5 bg-zinc-950 text-emerald-400 text-xs font-mono rounded-lg break-all leading-relaxed border border-zinc-800">
                  {createdKey}
                </code>
                <button onClick={copyKey} className="px-3 py-2 bg-zinc-900 text-white rounded-lg hover:bg-zinc-700 transition-all shrink-0">
                  {copied ? <ClipboardCheck size={16} className="text-emerald-400" /> : <Copy size={16} />}
                </button>
              </div>
            </div>
            <button onClick={onClose} className="w-full py-3 bg-zinc-900 text-white rounded-xl font-bold hover:bg-zinc-800 transition-all">
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            <div className="space-y-1.5">
              <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Key Name</label>
              <input
                required
                value={name}
                onChange={e => setName(e.target.value)}
                className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-sm focus:outline-none focus:border-zinc-900"
                placeholder="e.g. Internal ERP System"
              />
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Module Permissions</label>
                <span className="text-[9px] text-zinc-400 italic">{scopes.length} selected</span>
              </div>
              <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
                {apiModules.map(mod => {
                  const modScopes = allApiScopes().filter(s => s.startsWith(mod.id + ':'));
                  const allSelected = modScopes.every(s => scopes.includes(s));
                  const someSelected = modScopes.some(s => scopes.includes(s));
                  return (
                    <div key={mod.id} className="border border-zinc-200 rounded-xl overflow-hidden">
                      <button
                        type="button"
                        onClick={() => toggleModuleAll(mod.id)}
                        className={cn(
                          "w-full flex items-center justify-between px-3 py-2.5 transition-all",
                          allSelected ? "bg-zinc-900 text-white" : someSelected ? "bg-zinc-100 text-zinc-900" : "bg-zinc-50 text-zinc-700 hover:bg-zinc-100"
                        )}
                      >
                        <span className="text-xs font-bold">{mod.name}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-[9px] text-zinc-400">{allSelected ? 'All' : someSelected ? 'Partial' : 'None'}</span>
                          {allSelected ? <Check size={13} /> : <div className={cn("w-3 h-3 rounded border", someSelected ? "bg-zinc-400 border-zinc-400" : "border-zinc-300")} />}
                        </div>
                      </button>
                      <div className="divide-y divide-zinc-100">
                        {modScopes.map(scope => {
                          const type = scope.split(':')[1];
                          const active = scopes.includes(scope);
                          return (
                            <button
                              key={scope}
                              type="button"
                              onClick={() => toggleScope(scope)}
                              className={cn(
                                "w-full flex items-center gap-3 px-4 py-2 text-left transition-all",
                                active ? "bg-zinc-800 text-white" : "hover:bg-zinc-50 text-zinc-600"
                              )}
                            >
                              <div className="flex-1 flex items-center gap-2">
                                <span className={cn("px-1.5 py-0.5 text-[9px] font-bold uppercase rounded tracking-wider", type === 'read' ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700", active && (type === 'read' ? "bg-emerald-800 text-emerald-300" : "bg-amber-800 text-amber-300"))}>{type}</span>
                                <span className="text-[11px]">{type === 'read' ? 'Read access' : 'Write access'}</span>
                              </div>
                              {active ? <Check size={12} /> : <div className="w-3 h-3 rounded border border-zinc-300" />}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-zinc-200 rounded-lg text-sm font-semibold hover:bg-zinc-50">Cancel</button>
              <button
                type="submit"
                disabled={!name.trim() || scopes.length === 0}
                className="flex-1 px-4 py-2 bg-zinc-900 text-white rounded-lg text-sm font-semibold hover:bg-zinc-800 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Generate Key
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ─── PlatformApiDashboard ─────────────────────────────────────────────────────

function PlatformApiDashboard() {
  const repo = getRepository();
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [allKeys, setAllKeys] = useState<ApiKey[]>([]);
  const [deleteConfirm, setDeleteConfirm] = useState<DeleteConfirmState | null>(null);

  // Explorer state — platform sees ALL modules
  const apiModules = getApiModules();
  const [explorerModuleId, setExplorerModuleId] = useState(apiModules[0]?.id ?? '');
  const [explorerEndpointIdx, setExplorerEndpointIdx] = useState(0);
  const [explorerCodeTab, setExplorerCodeTab] = useState<'curl' | 'fetch'>('curl');
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);

  const activeModule = apiModules.find(m => m.id === explorerModuleId);
  const endpoints = activeModule?.apiEndpoints ?? [];
  const currentEp: ApiEndpointDefinition | undefined = endpoints[explorerEndpointIdx];

  const baseUrl = 'https://your-api.datamanage.app/v1';
  const fullUrl = currentEp ? `${baseUrl}${currentEp.path}` : '';
  const sampleBodyStr = currentEp?.sampleBody ? JSON.stringify(currentEp.sampleBody, null, 2) : '';
  const curlCode = currentEp
    ? `curl -X ${currentEp.method} \\\n  ${fullUrl} \\\n  -H "X-API-KEY: <YOUR_API_KEY>" \\\n  -H "Content-Type: application/json"${currentEp.method === 'POST' && sampleBodyStr ? ` \\\n  -d '${sampleBodyStr}'` : ''}`
    : '';
  const fetchCode = currentEp
    ? `const res = await fetch(\n  '${fullUrl}',\n  {\n    method: '${currentEp.method}',\n    headers: {\n      'X-API-KEY': '<YOUR_API_KEY>',\n      'Content-Type': 'application/json',\n    },${currentEp.method === 'POST' && sampleBodyStr ? `\n    body: JSON.stringify(${sampleBodyStr}),` : ''}\n  }\n);\nconst data = await res.json();`
    : '';

  const handleCopyUrl = (url: string) => {
    navigator.clipboard.writeText(url);
    setCopiedUrl(url);
    setTimeout(() => setCopiedUrl(null), 2000);
  };

  const load = async () => {
    const fetchedOrgs = await repo.getOrganizations();
    const fetchedKeys = await repo.getApiKeys().catch(() => [] as ApiKey[]);
    setOrgs(fetchedOrgs);
    setAllKeys(fetchedKeys);
  };
  useEffect(() => { load(); }, []);

  const handleRevokeOrgKeys = async (orgId: string) => {
    // Revoke via repository — keys are stored in D1
    for (const k of allKeys.filter(k => k.orgId === orgId && k.status === 'active')) {
      await repo.revokeApiKey(k.id).catch(() => {});
    }
    await load();
  };

  // Org-level stats
  const orgStats = orgs.map(org => {
    const orgKeys = allKeys.filter(k => k.orgId === org.id);
    const activeKeys = orgKeys.filter(k => k.status === 'active').length;
    const revokedKeys = orgKeys.filter(k => k.status === 'revoked').length;
    const limits = PLAN_LIMITS[org.plan];
    return { org, activeKeys, revokedKeys, totalKeys: orgKeys.length, hasApiAccess: limits.apiAccess };
  });

  const totalActiveKeys = allKeys.filter(k => k.status === 'active').length;
  const orgsWithAccess = orgStats.filter(o => o.hasApiAccess).length;
  const orgsWithKeys = orgStats.filter(o => o.activeKeys > 0).length;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold tracking-tight">API Management</h2>
        <p className="text-sm text-slate-500 mt-1">Platform-wide API key oversight, usage monitoring, and developer documentation.</p>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: 'Total Active Keys', value: totalActiveKeys, icon: Key, color: 'text-emerald-600 bg-emerald-50' },
          { label: 'Orgs with API Access', value: orgsWithAccess, icon: Building2, color: 'text-indigo-600 bg-indigo-50' },
          { label: 'Orgs Using Keys', value: orgsWithKeys, icon: Zap, color: 'text-amber-600 bg-amber-50' },
          { label: 'Total Keys (All Time)', value: allKeys.length, icon: Shield, color: 'text-slate-600 bg-slate-100' },
        ].map(card => (
          <div key={card.label} className="bg-white border border-zinc-200 rounded-xl p-5 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">{card.label}</span>
              <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center', card.color)}>
                <card.icon size={16} />
              </div>
            </div>
            <p className="text-2xl font-bold text-zinc-900 tabular-nums">{card.value}</p>
          </div>
        ))}
      </div>

      {/* Organization API Usage Table */}
      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden shadow-sm">
        <div className="px-5 py-4 border-b border-zinc-100 flex items-center justify-between">
          <h3 className="font-bold text-zinc-900">Organization API Usage</h3>
          <span className="text-xs text-zinc-400">{orgs.length} organizations</span>
        </div>
        {orgs.length === 0 ? (
          <div className="p-12 text-center text-slate-400 italic text-sm">No organizations found.</div>
        ) : (
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-zinc-50 border-b border-zinc-200">
                {['Organization', 'Plan', 'API Access', 'Active Keys', 'Revoked', 'Total', ''].map(h => (
                  <th key={h} className="px-4 py-3 text-[10px] font-bold text-zinc-400 uppercase tracking-widest">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {orgStats.map(({ org, activeKeys, revokedKeys, totalKeys, hasApiAccess }) => (
                <tr key={org.id} className="hover:bg-zinc-50 transition-colors">
                  <td className="px-4 py-3">
                    <div>
                      <p className="text-sm font-semibold text-zinc-900">{org.name}</p>
                      <p className="text-[10px] text-zinc-400 font-mono">{org.id.slice(0, 12)}…</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest',
                      PLAN_BADGE_COLORS[org.plan]
                    )}>{PLAN_LABELS[org.plan]}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest',
                      hasApiAccess ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-100 text-zinc-400'
                    )}>{hasApiAccess ? 'Enabled' : 'Disabled'}</span>
                  </td>
                  <td className="px-4 py-3 text-sm font-bold text-zinc-900 tabular-nums">{activeKeys}</td>
                  <td className="px-4 py-3 text-sm text-zinc-400 tabular-nums">{revokedKeys}</td>
                  <td className="px-4 py-3 text-sm text-zinc-500 tabular-nums">{totalKeys}</td>
                  <td className="px-4 py-3 text-right">
                    {activeKeys > 0 && (
                      <button
                        onClick={() => setDeleteConfirm({
                          message: `Revoke all ${activeKeys} active API key(s) for "${org.name}"? This will immediately block their API access.`,
                          onConfirm: () => handleRevokeOrgKeys(org.id),
                        })}
                        className="px-3 py-1.5 text-xs font-semibold text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-all"
                      >
                        Revoke All
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Developer Documentation — API Explorer (Read-Only for Platform) ──── */}
      <div className="bg-zinc-950 rounded-2xl overflow-hidden border border-zinc-800">
        <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Code2 size={18} className="text-emerald-400" />
            <h3 className="font-bold text-white text-sm">Developer Documentation — API Reference</h3>
          </div>
          <div className="flex items-center gap-3">
            <CustomSelect
              value={explorerModuleId}
              onChange={v => { setExplorerModuleId(v); setExplorerEndpointIdx(0); }}
              placeholder="Select module"
              options={apiModules.map(m => ({ value: m.id, label: m.name }))}
              className="bg-zinc-800 text-zinc-300 border-zinc-700 min-w-[180px]"
            />
          </div>
        </div>

        {/* Endpoint selector */}
        <div className="px-4 pt-4 space-y-2">
          <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest px-2">Endpoints</p>
          <div className="flex flex-wrap items-center gap-1">
            {endpoints.map((ep, idx) => (
              <button
                key={ep.path + ep.method}
                onClick={() => setExplorerEndpointIdx(idx)}
                className={cn(
                  "flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-mono transition-all",
                  explorerEndpointIdx === idx ? "bg-zinc-700 text-white" : "text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800"
                )}
              >
                <span className={cn("font-bold text-[10px]", ep.method === 'GET' ? "text-emerald-400" : ep.method === 'POST' ? "text-amber-400" : ep.method === 'PUT' ? "text-blue-400" : "text-red-400")}>{ep.method}</span>
                <span>{ep.path}</span>
                <span
                  role="button"
                  tabIndex={0}
                  onClick={e => { e.stopPropagation(); handleCopyUrl(`${baseUrl}${ep.path}`); }}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); handleCopyUrl(`${baseUrl}${ep.path}`); } }}
                  className="p-0.5 text-zinc-600 hover:text-zinc-300 transition-colors cursor-pointer"
                  title="Copy endpoint URL"
                >
                  {copiedUrl === `${baseUrl}${ep.path}` ? <ClipboardCheck size={11} className="text-emerald-400" /> : <Copy size={11} />}
                </span>
              </button>
            ))}
          </div>
          {currentEp && <p className="text-[11px] text-zinc-500 italic px-2">{currentEp.desc}</p>}
        </div>

        {/* Code tab switcher */}
        <div className="flex gap-2 px-6 pt-3">
          {(['curl', 'fetch'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setExplorerCodeTab(tab)}
              className={cn("px-3 py-1 text-[11px] font-bold rounded-md uppercase tracking-widest transition-all", explorerCodeTab === tab ? "bg-zinc-700 text-white" : "text-zinc-500 hover:text-zinc-300")}
            >{tab}</button>
          ))}
        </div>

        {/* Code block */}
        <div className="mx-6 mt-3 mb-6 bg-zinc-900 rounded-xl border border-zinc-700 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2 border-b border-zinc-700">
            <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">{explorerCodeTab === 'curl' ? 'cURL' : 'JavaScript (fetch)'}</span>
            <button onClick={() => navigator.clipboard.writeText(explorerCodeTab === 'curl' ? curlCode : fetchCode)} className="p-1 text-zinc-500 hover:text-zinc-300 transition-colors" title="Copy code">
              <Copy size={13} />
            </button>
          </div>
          <pre className="p-4 text-xs font-mono overflow-x-auto leading-relaxed">
            {explorerCodeTab === 'curl' ? (
              curlCode.split('\n').map((line, i) => (
                <div key={i}>
                  {line.split(/(".*?")/).map((part, j) =>
                    part.startsWith('"') ? <span key={j} className="text-amber-300">{part}</span>
                    : part.startsWith('curl') ? <span key={j} className="text-violet-400">{part}</span>
                    : part.includes('-X') || part.includes('-H') || part.includes('-d') ? <span key={j} className="text-sky-400">{part}</span>
                    : <span key={j} className="text-zinc-300">{part}</span>
                  )}
                </div>
              ))
            ) : (
              fetchCode.split('\n').map((line, i) => (
                <div key={i}>
                  {line.includes('const ') || line.includes('await ') ? <span className="text-violet-400">{line.replace(/('.*?')/g, '§$1§').split('§').map((p2, j2) => p2.startsWith("'") ? <span key={j2} className="text-emerald-400">{p2}</span> : <span key={j2}>{p2}</span>)}</span>
                  : line.includes("'") ? <>{line.split(/('.*?')/).map((p3, j3) => p3.startsWith("'") ? <span key={j3} className="text-emerald-400">{p3}</span> : <span key={j3} className="text-zinc-300">{p3}</span>)}</>
                  : <span className="text-zinc-300">{line}</span>
                  }
                </div>
              ))
            )}
          </pre>
        </div>
      </div>

      <ConfirmDeleteDialog state={deleteConfirm} onClose={() => setDeleteConfirm(null)} />
    </div>
  );
}

// ─── ApiManagementPage ────────────────────────────────────────────────────────

function ApiManagementPage({ allRiders, settings, currentOrg }: { allRiders: any[]; settings: any; currentOrg?: Organization | null }) {
  const repo = getRepository();
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [apiSettings, setApiSettings] = useState<ApiSettings>({ webhookUrl: '', webhookEnabled: false, webhooks: [] });
  const [showForm, setShowForm] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<DeleteConfirmState | null>(null);

  // Explorer state — filter modules by org's installed apps
  const allApiModules = getApiModules();
  const apiModules = currentOrg?.enabledApps
    ? allApiModules.filter(m => currentOrg.enabledApps!.includes(m.id as any) || m.id === MODULE_IDS.SYSTEM)
    : allApiModules;
  const [explorerModuleId, setExplorerModuleId] = useState(apiModules[0]?.id ?? '');
  const [explorerKeyId, setExplorerKeyId] = useState('');
  const [explorerEndpointIdx, setExplorerEndpointIdx] = useState(0);
  const [explorerResult, setExplorerResult] = useState<SimulatedResponse | null>(null);
  const [explorerLoading, setExplorerLoading] = useState(false);
  const [explorerCodeTab, setExplorerCodeTab] = useState<'curl' | 'fetch'>('curl');

  // Webhook state
  const [webhookSaved, setWebhookSaved] = useState(false);
  const [newWebhookModuleId, setNewWebhookModuleId] = useState(apiModules[0]?.id ?? '');
  const [newWebhookEvent, setNewWebhookEvent] = useState('');
  const [newWebhookUrl, setNewWebhookUrl] = useState('');

  const activeModule = apiModules.find(m => m.id === explorerModuleId);
  const endpoints = activeModule?.apiEndpoints ?? [];
  const currentEp: ApiEndpointDefinition | undefined = endpoints[explorerEndpointIdx];
  const activeKey = apiKeys.find(k => k.id === explorerKeyId && k.status === 'active');
  const keyDisplay = activeKey?.key ?? '<YOUR_API_KEY>';

  const load = async () => {
    const [keys, cfg] = await Promise.all([repo.getApiKeys(), repo.getApiSettings()]);
    setApiKeys(keys);
    setApiSettings({ ...cfg, webhooks: cfg.webhooks ?? [] });
    return keys;
  };
  useEffect(() => {
    load().then(keys => {
      const firstActive = keys.find(k => k.status === 'active');
      if (firstActive) setExplorerKeyId(firstActive.id);
    });
  }, []);

  const handleSaveKey = async (key: ApiKey) => {
    await repo.saveApiKey(key);
    await load();
    setExplorerKeyId(key.id);
  };
  const handleRevoke = async (id: string) => {
    const k = apiKeys.find(k => k.id === id);
    if (k) { await repo.saveApiKey({ ...k, status: 'revoked' }); await load(); }
  };
  const handleDelete = async (id: string) => {
    await repo.deleteApiKey(id);
    await load();
  };
  const handleCopyKey = (key: ApiKey) => {
    navigator.clipboard.writeText(key.key);
    setCopiedId(key.id);
    setTimeout(() => setCopiedId(null), 2000);
  };
  const handleCopyUrl = (url: string) => {
    navigator.clipboard.writeText(url);
    setCopiedUrl(url);
    setTimeout(() => setCopiedUrl(null), 2000);
  };

  // Webhook actions
  const handleAddWebhook = async () => {
    if (!newWebhookModuleId || !newWebhookEvent || !newWebhookUrl.trim()) return;
    const wh: WebhookRegistration = {
      id: crypto.randomUUID(),
      moduleId: newWebhookModuleId,
      event: newWebhookEvent,
      url: newWebhookUrl.trim(),
      enabled: true,
      createdAt: Date.now(),
    };
    const updated = { ...apiSettings, webhooks: [...(apiSettings.webhooks ?? []), wh] };
    await repo.saveApiSettings(updated);
    setApiSettings(updated);
    setNewWebhookUrl('');
    setNewWebhookEvent('');
  };
  const handleToggleWebhook = async (whId: string) => {
    const webhooks = (apiSettings.webhooks ?? []).map(w => w.id === whId ? { ...w, enabled: !w.enabled } : w);
    const updated = { ...apiSettings, webhooks };
    await repo.saveApiSettings(updated);
    setApiSettings(updated);
  };
  const handleDeleteWebhook = async (whId: string) => {
    const webhooks = (apiSettings.webhooks ?? []).filter(w => w.id !== whId);
    const updated = { ...apiSettings, webhooks };
    await repo.saveApiSettings(updated);
    setApiSettings(updated);
  };
  const handleTestWebhook = (wh: WebhookRegistration) => {
    if (!wh.url) return;
    const mod = apiModules.find(m => m.id === wh.moduleId);
    const evtDef = mod?.webhookEvents?.find(e => e.event === wh.event);
    fetch(wh.url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event: wh.event, timestamp: Date.now(), test: true, data: evtDef?.samplePayload ?? null }),
    }).catch(() => {});
  };

  // Explorer code generation
  const baseUrl = 'https://your-api.datamanage.app/v1';
  const fullUrl = currentEp ? `${baseUrl}${currentEp.path}` : '';
  const sampleBodyStr = currentEp?.sampleBody ? JSON.stringify(currentEp.sampleBody, null, 2) : '';
  const curlCode = currentEp
    ? `curl -X ${currentEp.method} \\\n  ${fullUrl} \\\n  -H "X-API-KEY: ${keyDisplay}" \\\n  -H "Content-Type: application/json"${currentEp.method === 'POST' && sampleBodyStr ? ` \\\n  -d '${sampleBodyStr}'` : ''}`
    : '';
  const fetchCode = currentEp
    ? `const res = await fetch(\n  '${fullUrl}',\n  {\n    method: '${currentEp.method}',\n    headers: {\n      'X-API-KEY': '${keyDisplay}',\n      'Content-Type': 'application/json',\n    },${currentEp.method === 'POST' && sampleBodyStr ? `\n    body: JSON.stringify(${sampleBodyStr}),` : ''}\n  }\n);\nconst data = await res.json();`
    : '';

  const handleTryIt = async () => {
    if (!activeKey || !currentEp) return;
    setExplorerLoading(true);
    setExplorerResult(null);
    let ep = currentEp.path;
    if (ep.includes('{RIDER_ID}') && allRiders.length > 0) ep = ep.replace('{RIDER_ID}', allRiders[0].id);
    const result = await dispatchApiRequest({ method: currentEp.method as 'GET' | 'POST', endpoint: ep, apiKey: activeKey.key, body: currentEp.sampleBody }, repo);
    setExplorerResult(result);
    setExplorerLoading(false);
  };

  // Webhook event options for the selected module
  const webhookModuleDef = apiModules.find(m => m.id === newWebhookModuleId);
  const webhookEventOptions = webhookModuleDef?.webhookEvents ?? [];

  // All registered webhooks
  const allWebhooks = allWebhookEvents();

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">API Settings</h2>
          <p className="text-sm text-slate-500 mt-1">Manage API keys and webhooks for your organization's integrations.</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-zinc-900 text-white rounded-lg text-sm font-semibold hover:bg-zinc-800 transition-all"
        >
          <Key size={16} /> Generate Key
        </button>
      </div>

      {/* ── Keys table ─────────────────────────────────────────────────────── */}
      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden shadow-sm">
        <div className="px-5 py-4 border-b border-zinc-100 flex items-center justify-between">
          <h3 className="font-bold text-zinc-900">API Keys</h3>
          <span className="text-xs text-zinc-400">{apiKeys.filter(k => k.status === 'active').length} active</span>
        </div>
        {apiKeys.length === 0 ? (
          <div className="p-16 text-center text-slate-400 italic text-sm">No API keys yet. Click "Generate Key" to create one.</div>
        ) : (
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-zinc-50 border-b border-zinc-200">
                {['Name', 'Scopes', 'Status', 'Created', 'Secret Key', ''].map(h => (
                  <th key={h} className="px-4 py-3 text-[10px] font-bold text-zinc-400 uppercase tracking-widest">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {apiKeys.map(key => (
                <tr key={key.id} className={cn("hover:bg-zinc-50 transition-colors", key.status === 'revoked' && "opacity-50")}>
                  <td className="px-4 py-3 text-sm font-semibold text-zinc-900">{key.name}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {key.scopes.map(s => (
                        <span key={s} className="px-2 py-0.5 bg-zinc-100 text-zinc-600 text-[10px] font-bold rounded uppercase tracking-widest">
                          {scopeLabel(s)}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={cn("px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest", key.status === 'active' ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-500")}>
                      {key.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-500 tabular-nums">{new Date(key.createdAt).toLocaleDateString()}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <code className="text-xs font-mono text-zinc-500 bg-zinc-50 px-2 py-1 rounded border border-zinc-100">
                        sk-{key.key.slice(0, 8)}••••••••
                      </code>
                      <button onClick={() => handleCopyKey(key)} className="p-1 text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 rounded transition-all" title="Copy full key">
                        {copiedId === key.id ? <ClipboardCheck size={13} className="text-emerald-500" /> : <Copy size={13} />}
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {key.status === 'active' && (
                        <button onClick={() => handleRevoke(key.id)} className="px-3 py-1.5 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-all">
                          Revoke
                        </button>
                      )}
                      <button onClick={() => setDeleteConfirm({ message: 'Delete this API key permanently?', onConfirm: () => handleDelete(key.id) })} className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── API Explorer ────────────────────────────────────────────────────── */}
      <div className="bg-zinc-950 rounded-2xl overflow-hidden border border-zinc-800">
        <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Code2 size={18} className="text-emerald-400" />
            <h3 className="font-bold text-white text-sm">API Explorer & Developer Docs</h3>
          </div>
          <div className="flex items-center gap-3">
            <CustomSelect
              value={explorerModuleId}
              onChange={v => { setExplorerModuleId(v); setExplorerEndpointIdx(0); setExplorerResult(null); }}
              placeholder="Select module"
              options={apiModules.map(m => ({ value: m.id, label: m.name }))}
              className="bg-zinc-800 text-zinc-300 border-zinc-700 min-w-[180px]"
            />
            <CustomSelect
              value={explorerKeyId}
              onChange={v => { setExplorerKeyId(v); setExplorerResult(null); }}
              placeholder="— Select API key —"
              options={[{ value: '', label: '— Select API key —' }, ...apiKeys.filter(k => k.status === 'active').map(k => ({ value: k.id, label: k.name }))]}
              className="bg-zinc-800 text-zinc-300 border-zinc-700 min-w-[180px]"
            />
          </div>
        </div>

        {/* Endpoint selector — dynamic from selected module */}
        <div className="px-4 pt-4 space-y-2">
          <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest px-2">Endpoints</p>
          <div className="flex flex-wrap items-center gap-1">
            {endpoints.map((ep, idx) => (
              <button
                key={ep.path + ep.method}
                onClick={() => { setExplorerEndpointIdx(idx); setExplorerResult(null); }}
                className={cn(
                  "flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-mono transition-all",
                  explorerEndpointIdx === idx ? "bg-zinc-700 text-white" : "text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800"
                )}
              >
                <span className={cn("font-bold text-[10px]", ep.method === 'GET' ? "text-emerald-400" : ep.method === 'POST' ? "text-amber-400" : ep.method === 'PUT' ? "text-blue-400" : "text-red-400")}>{ep.method}</span>
                <span>{ep.path}</span>
                <span
                  role="button"
                  tabIndex={0}
                  onClick={e => { e.stopPropagation(); handleCopyUrl(`${baseUrl}${ep.path}`); }}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); handleCopyUrl(`${baseUrl}${ep.path}`); } }}
                  className="p-0.5 text-zinc-600 hover:text-zinc-300 transition-colors cursor-pointer"
                  title="Copy endpoint URL"
                >
                  {copiedUrl === `${baseUrl}${ep.path}` ? <ClipboardCheck size={11} className="text-emerald-400" /> : <Copy size={11} />}
                </span>
              </button>
            ))}
          </div>
          {currentEp && (
            <p className="text-[11px] text-zinc-500 italic px-2">{currentEp.desc}</p>
          )}
        </div>

        {/* Code tab switcher */}
        <div className="flex gap-2 px-6 pt-3">
          {(['curl', 'fetch'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setExplorerCodeTab(tab)}
              className={cn("px-3 py-1 text-[11px] font-bold rounded-md uppercase tracking-widest transition-all", explorerCodeTab === tab ? "bg-zinc-700 text-white" : "text-zinc-500 hover:text-zinc-300")}
            >{tab}</button>
          ))}
        </div>

        {/* Code block with syntax highlighting */}
        <div className="mx-6 mt-3 bg-zinc-900 rounded-xl border border-zinc-700 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2 border-b border-zinc-700">
            <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">{explorerCodeTab === 'curl' ? 'cURL' : 'JavaScript (fetch)'}</span>
            <button onClick={() => navigator.clipboard.writeText(explorerCodeTab === 'curl' ? curlCode : fetchCode)} className="p-1 text-zinc-500 hover:text-zinc-300 transition-colors" title="Copy code">
              <Copy size={13} />
            </button>
          </div>
          <pre className="p-4 text-xs font-mono overflow-x-auto leading-relaxed">
            {explorerCodeTab === 'curl' ? (
              curlCode.split('\n').map((line, i) => (
                <div key={i}>
                  {line.split(/(".*?")/).map((part, j) =>
                    part.startsWith('"') ? <span key={j} className="text-amber-300">{part}</span>
                    : part.startsWith('curl') ? <span key={j} className="text-violet-400">{part}</span>
                    : part.includes('-X') || part.includes('-H') || part.includes('-d') ? <span key={j} className="text-sky-400">{part}</span>
                    : <span key={j} className="text-zinc-300">{part}</span>
                  )}
                </div>
              ))
            ) : (
              fetchCode.split('\n').map((line, i) => (
                <div key={i}>
                  {line.includes('const ') || line.includes('await ') ? <span className="text-violet-400">{line.replace(/('.*?')/g, '§$1§').split('§').map((p2, j2) => p2.startsWith("'") ? <span key={j2} className="text-emerald-400">{p2}</span> : <span key={j2}>{p2}</span>)}</span>
                  : line.includes("'") ? <>{line.split(/('.*?')/).map((p3, j3) => p3.startsWith("'") ? <span key={j3} className="text-emerald-400">{p3}</span> : <span key={j3} className="text-zinc-300">{p3}</span>)}</>
                  : <span className="text-zinc-300">{line}</span>
                  }
                </div>
              ))
            )}
          </pre>
        </div>

        {/* Try it */}
        <div className="mx-6 mt-4 mb-6 space-y-3">
          <button
            onClick={handleTryIt}
            disabled={!activeKey || explorerLoading || !currentEp}
            className="flex items-center gap-2 px-5 py-2.5 bg-emerald-500 text-white text-sm font-bold rounded-xl hover:bg-emerald-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            <Zap size={16} />
            {explorerLoading ? 'Sending…' : 'Try it (Simulated)'}
          </button>
          {!activeKey && (
            <p className="text-[11px] text-zinc-500 italic">Select an active API key above to enable the simulation.</p>
          )}
          {explorerResult && (
            <div className="bg-zinc-900 rounded-xl border border-zinc-700 overflow-hidden">
              <div className="flex items-center gap-3 px-4 py-2.5 border-b border-zinc-700">
                <span className={cn("px-2 py-0.5 rounded text-[11px] font-bold tabular-nums",
                  explorerResult.status < 300 ? "bg-emerald-900/50 text-emerald-400" :
                  explorerResult.status < 500 ? "bg-amber-900/50 text-amber-400" :
                  "bg-red-900/50 text-red-400")}>
                  {explorerResult.status} {explorerResult.statusText}
                </span>
                <span className="text-[10px] text-zinc-500">{explorerResult.responseTime}ms</span>
              </div>
              <pre className="p-4 text-xs font-mono text-zinc-300 overflow-x-auto max-h-64 leading-relaxed">{JSON.stringify(explorerResult.data, null, 2)}</pre>
            </div>
          )}
        </div>
      </div>

      {/* ── Universal Webhook Engine ─────────────────────────────────────────── */}
      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden shadow-sm">
        <div className="px-6 py-4 border-b border-zinc-100 flex items-center gap-3">
          <Zap size={18} className="text-zinc-400" />
          <div>
            <h3 className="font-bold text-zinc-900">Webhook Event Listeners</h3>
            <p className="text-xs text-slate-500 mt-0.5">Register webhooks for any module event. The system fires the configured URL when the event occurs.</p>
          </div>
        </div>

        {/* Registered webhooks list */}
        {(apiSettings.webhooks ?? []).length > 0 && (
          <div className="divide-y divide-zinc-100">
            {(apiSettings.webhooks ?? []).map(wh => {
              const mod = apiModules.find(m => m.id === wh.moduleId);
              const evtDef = mod?.webhookEvents?.find(e => e.event === wh.event);
              return (
                <div key={wh.id} className="px-6 py-3 flex items-center gap-4">
                  <button type="button" onClick={() => handleToggleWebhook(wh.id)} className="shrink-0">
                    {wh.enabled ? <ToggleRight size={28} className="text-emerald-500" /> : <ToggleLeft size={28} className="text-zinc-300" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-bold px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded uppercase tracking-widest">{mod?.name ?? wh.moduleId}</span>
                      <span className="text-[10px] font-bold px-2 py-0.5 bg-zinc-100 text-zinc-600 rounded uppercase tracking-widest">{evtDef?.label ?? wh.event}</span>
                    </div>
                    <p className="text-xs text-zinc-500 mt-1 truncate font-mono">{wh.url}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => handleTestWebhook(wh)}
                      disabled={!wh.enabled}
                      className="px-3 py-1.5 text-xs font-semibold border border-zinc-200 rounded-lg hover:bg-zinc-50 disabled:opacity-40 transition-all flex items-center gap-1.5"
                    >
                      <Send size={12} /> Test
                    </button>
                    <button
                      onClick={() => setDeleteConfirm({ message: 'Remove this webhook?', onConfirm: () => handleDeleteWebhook(wh.id) })}
                      className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Add new webhook */}
        <div className="px-6 py-5 bg-zinc-50 border-t border-zinc-100 space-y-3">
          <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Register New Webhook</p>
          <div className="grid grid-cols-[1fr_1fr_2fr_auto] gap-3 items-end">
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Target Module</label>
              <CustomSelect
                value={newWebhookModuleId}
                onChange={v => { setNewWebhookModuleId(v); setNewWebhookEvent(''); }}
                options={apiModules.filter(m => m.webhookEvents && m.webhookEvents.length > 0).map(m => ({ value: m.id, label: m.name }))}
                className="bg-white"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Event Trigger</label>
              <CustomSelect
                value={newWebhookEvent}
                onChange={v => setNewWebhookEvent(v)}
                placeholder="Select event"
                options={webhookEventOptions.map(e => ({ value: e.event, label: e.label }))}
                className="bg-white"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Webhook URL</label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={14} />
                <input
                  type="url"
                  value={newWebhookUrl}
                  onChange={e => setNewWebhookUrl(e.target.value)}
                  className="w-full pl-8 pr-3 py-2 bg-white border border-zinc-200 rounded-lg text-sm focus:outline-none focus:border-zinc-900 transition-all"
                  placeholder="https://your-system.com/webhook"
                />
              </div>
            </div>
            <button
              onClick={handleAddWebhook}
              disabled={!newWebhookModuleId || !newWebhookEvent || !newWebhookUrl.trim()}
              className="px-4 py-2 bg-zinc-900 text-white text-sm font-semibold rounded-lg hover:bg-zinc-800 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center gap-2"
            >
              <Plus size={15} /> Add
            </button>
          </div>
        </div>
      </div>

      {showForm && (
        <ApiKeyFormModal onSave={handleSaveKey} onClose={() => setShowForm(false)} />
      )}
      <ConfirmDeleteDialog state={deleteConfirm} onClose={() => setDeleteConfirm(null)} />
    </div>
  );
}
